import { HELP_FAZ8_DE } from './entries.faz8.de';
import { AI_HELP_DE } from './entries.ai.de';
import type { HelpContent, HelpKey } from './entries.tr';
import type { MobileHelpKey } from './entries.mobile';

type Loc = Pick<HelpContent, 'title' | 'short'>;

/** Yardım başlıkları ve kısa açıklamalar — Almanca (F7-17). Ayrıntı alanları Türkçe kayda düşer (İngilizcedeki gibi). */
export const HELP_DE: Record<HelpKey, Loc> = {
  ...HELP_FAZ8_DE,
  ...AI_HELP_DE,
  'page.dashboard': { title: 'Dashboard', short: 'Zeigt Ihr Geschäft auf einen Blick.' },
  'page.setup': {
    title: 'Einrichtungsassistent',
    short: 'Die 24 Schritte bis zum Livegang, in der richtigen Reihenfolge.',
  },
  'page.organization': { title: 'Unternehmensdaten', short: 'Firmenname, Steuer- und Kontaktdaten.' },
  'page.brands': {
    title: 'Marken',
    short: 'Die Marken, die Sie verkaufen (z. B. Zootopia Global, VITATOPIA).',
  },
  'page.stores': { title: 'Shops', short: 'Vertriebskanäle: Domains und Märkte nach Land/Sprache/Währung.' },
  'page.users': { title: 'Benutzer', short: 'Mitarbeitende mit Zugang zur Verwaltung und ihre Rollen.' },
  'page.roles': { title: 'Rollen & Berechtigungen', short: 'Was jede Rolle darf; eigene Rollen erstellen.' },
  'page.api_keys': {
    title: 'API-Schlüssel',
    short: 'Schlüssel, mit denen sich externe Programme (Buchhaltung, ERP, n8n) verbinden.',
  },
  'page.webhooks': {
    title: 'Webhooks',
    short: 'Benachrichtigt ein anderes Programm sofort, wenn etwas passiert.',
  },
  'page.integrations': { title: 'Integrationen', short: 'Tatsächlicher Verbindungsstatus externer Dienste.' },
  'page.audit': { title: 'Prüfprotokoll', short: 'Wer was wann geändert hat, mit altem und neuem Wert.' },
  'page.system': { title: 'Systemstatus', short: 'Datenbank, Warteschlangen, E-Mail, SSL und Backups.' },
  'page.migration': {
    title: 'Migrationspaket',
    short:
      'Überträgt einen auf Staging vorbereiteten Shop mit einer einzigen Datei in den Live-Betrieb (oder in einen zweiten Shop).',
  },
  'page.privacy': {
    title: 'Betroffenenanfragen',
    short:
      'Erfasst, verfolgt und erledigt Anfragen zu personenbezogenen Daten innerhalb der gesetzlichen Frist von 30 Tagen.',
  },
  'page.account': { title: 'Mein Konto', short: 'Profil, Passwort, Sprache und offene Sitzungen.' },
  'page.help_editor': {
    title: 'Hilfetexte',
    short: 'Bearbeiten Sie die in der Verwaltung angezeigten Erklärungen.',
  },
  'auth.sign_in': {
    title: 'Anmelden',
    short: 'Anmeldung mit E-Mail und Passwort; anschließend folgt ein Bestätigungscode.',
  },
  'auth.mfa_enroll': {
    title: 'Zwei-Faktor-Authentifizierung einrichten',
    short: 'Verknüpft die Authenticator-App auf Ihrem Smartphone mit Ihrem Konto.',
  },
  'auth.mfa_verify': {
    title: 'Bestätigungscode',
    short: 'Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein.',
  },
  'auth.backup_codes': {
    title: 'Backup-Codes',
    short: '10 Einmalcodes für den Fall, dass Sie keinen Zugriff auf Ihr Smartphone haben.',
  },
  'auth.forgot_password': {
    title: 'Passwort vergessen',
    short: 'Sendet per E-Mail einen Link zum Zurücksetzen, der 1 Stunde gültig ist.',
  },
  'organization.save': { title: 'Unternehmensdaten speichern', short: 'Speichert Ihre Änderungen.' },
  'organization.tax_number': {
    title: 'Steuernummer / nationale ID-Nummer',
    short: '10-stellige Steuernummer oder 11-stellige nationale ID-Nummer.',
  },
  'organization.mersis': {
    title: 'MERSIS-Nummer',
    short: '16-stellige Nummer des zentralen Handelsregisters.',
  },
  'organization.kep': {
    title: 'KEP-Adresse',
    short: 'Adresse für registrierte elektronische Post (falls vorhanden).',
  },
  'organization.etbis': {
    title: 'ETBİS-Verifizierungslink',
    short:
      'Die ETBİS-Adresse, die Ihr Registrierungs-QR-Code öffnet; wird in der Fußzeile der Storefront angezeigt.',
  },
  'brands.create': { title: 'Marke hinzufügen', short: 'Legt eine neue Marke an.' },
  'brands.edit': { title: 'Marke bearbeiten', short: 'Ändert Name, Beschreibung, Farbe oder Logo.' },
  'brands.archive': { title: 'Archivieren', short: 'Blendet die Marke aus, ohne sie zu löschen.' },
  'brands.logo_upload': { title: 'Logo hochladen', short: 'PNG, JPEG, WebP, AVIF oder GIF; max. 5 MB.' },
  'brands.slug': {
    title: 'Kurzname',
    short: 'In URLs verwendeter Name: Kleinbuchstaben, Ziffern und Bindestriche.',
  },
  'brands.primary_color': { title: 'Primärfarbe', short: 'Hauptfarbe der Marke (z. B. #0F766E).' },
  'stores.create': { title: 'Shop hinzufügen', short: 'Legt einen neuen Vertriebskanal an.' },
  'stores.edit': { title: 'Shop bearbeiten', short: 'Ändert Name, Kanaltyp und Marken.' },
  'stores.channel_type': { title: 'Kanaltyp', short: 'B2C, B2B oder hybrid (beides).' },
  'stores.status': {
    title: 'Veröffentlichungsstatus',
    short:
      'Vorschau, Live oder Wartung. Für den Livegang sind die Bereitschaftsliste, eine ausdrückliche Bestätigung und die Zwei-Faktor-Authentifizierung erforderlich.',
  },
  'stores.go_live': { title: 'Shop live schalten', short: 'Öffnet die Storefront für echte Kunden.' },
  'stores.maintenance': {
    title: 'Wartung',
    short: 'Schließt die Storefront vorübergehend mit einer Wartungsseite (503).',
  },
  'stores.to_draft': {
    title: 'Zurück zur Vorschau',
    short: 'Nimmt den Shop offline und versetzt ihn in die Vorschau.',
  },
  'stores.go_live_confirm': {
    title: 'Bestätigung des Livegangs',
    short: 'Sie bestätigen, dass Preise, Zahlungsarten und Rechtstexte geprüft wurden.',
  },
  'stores.status_note': {
    title: 'Notiz',
    short: 'Grund für die Änderung; wird im Prüfprotokoll festgehalten.',
  },
  'stores.add_domain': { title: 'Domain hinzufügen', short: 'Verbindet eine Domain mit dem Shop.' },
  'stores.remove_domain': { title: 'Domain entfernen', short: 'Trennt die Domain vom Shop.' },
  'stores.default_market': {
    title: 'Als Standard festlegen',
    short: 'Die Storefront öffnet sich in der Sprache und Währung dieses Markts.',
  },
  'stores.add_market': {
    title: 'Markt hinzufügen',
    short: 'Fügt eine Kombination aus Land, Sprache und Währung hinzu.',
  },
  'stores.return_policy': {
    title: 'Rückgaberichtlinie (für Suchmaschinen)',
    short:
      'An Google gemeldete Rückgabefrist, Rückgabemethode und Rücksendekosten; müssen mit Ihren Rechtstexten übereinstimmen.',
  },
  'stores.return_days': { title: 'Rückgabefrist (Tage)', short: 'Bei Fernabsatz mindestens 14 Tage.' },
  'stores.return_method': { title: 'Rückgabemethode', short: 'Per Post oder im Geschäft.' },
  'stores.return_fees': {
    title: 'Rücksendekosten',
    short: '„Kostenlos“ nur, wenn Sie die Rücksendekosten übernehmen.',
  },
  'stores.remove_market': {
    title: 'Markt entfernen',
    short: 'Löscht den Markt. Der Standardmarkt kann nicht entfernt werden.',
  },
  'users.copy_invite_link': {
    title: 'Link kopieren',
    short: 'Kopiert den Einladungslink, damit Sie ihn per WhatsApp, SMS oder persönlich weitergeben können.',
  },
  'users.invite': {
    title: 'Benutzer einladen',
    short:
      'Erstellt einen 7 Tage gültigen Einladungslink und versendet ihn per E-Mail, wenn der E-Mail-Dienst eingerichtet ist.',
  },
  'users.change_role': { title: 'Rolle ändern', short: 'Ändert die Rolle, die die Berechtigungen festlegt.' },
  'users.store_scope': {
    title: 'Shop-Beschränkung',
    short: 'Leer = alle Shops; sonst nur die ausgewählten Shops.',
  },
  'users.dealer_scope': {
    title: 'Händlerbereich',
    short:
      '„Nur zugewiesene Händler“: Der Benutzer sieht die ihm zugewiesenen Händler oder die Händler in den von ihm betreuten Regionen samt deren Benutzern, Bestellungen, Angeboten und Kontoauszügen; Endkunden und andere Händler bleiben verborgen.',
  },
  'users.suspend': {
    title: 'Sperren',
    short: 'Beendet den Zugang zur Verwaltung; die Datensätze bleiben erhalten.',
  },
  'users.remove': { title: 'Mitglied entfernen', short: 'Entfernt die Person aus diesem Unternehmen.' },
  'users.revoke_invitation': {
    title: 'Einladung widerrufen',
    short: 'Macht eine ausstehende Einladung ungültig.',
  },
  'roles.create_custom': {
    title: 'Eigene Rolle erstellen',
    short: 'Definiert eine neue Rolle, Berechtigung für Berechtigung.',
  },
  'roles.edit_custom': {
    title: 'Eigene Rolle bearbeiten',
    short: 'Ändert die Berechtigungen der Rolle für alle, die sie verwenden.',
  },
  'roles.delete_custom': { title: 'Eigene Rolle löschen', short: 'Löscht die Rolle.' },
  'api_keys.create': {
    title: 'API-Schlüssel erstellen',
    short: 'Erzeugt einen Schlüssel, mit dem sich ein externes Programm verbinden kann.',
  },
  'api_keys.scopes': { title: 'Bereiche', short: 'Was der Schlüssel darf. Wählen Sie nur das Nötige aus.' },
  'api_keys.revoke': { title: 'Widerrufen', short: 'Macht den Schlüssel sofort ungültig.' },
  'webhooks.create': {
    title: 'Webhook hinzufügen',
    short: 'Registriert eine URL, die bei ausgewählten Ereignissen benachrichtigt wird.',
  },
  'webhooks.events': {
    title: 'Ereignisse',
    short: 'Welche Ereignisse eine Benachrichtigung auslösen. „*“ bedeutet alle.',
  },
  'webhooks.test': {
    title: 'Testereignis senden',
    short: 'Sendet ein „webhook.test“-Ereignis an alle aktiven Webhooks.',
  },
  'webhooks.rotate_secret': {
    title: 'Secret erneuern',
    short: 'Erzeugt ein neues Signatur-Secret; das alte funktioniert nicht mehr.',
  },
  'webhooks.redeliver': {
    title: 'Erneut zustellen',
    short: 'Wiederholt eine fehlgeschlagene Zustellung sofort.',
  },
  'webhooks.delete': { title: 'Webhook löschen', short: 'Beendet die Benachrichtigungen an diese URL.' },
  'integrations.save_credentials': {
    title: 'Zugangsdaten speichern',
    short:
      'Speichert die Angaben verschlüsselt. Der Status bleibt „Einrichtung erforderlich“, bis sie getestet wurden.',
  },
  'integrations.test': {
    title: 'Verbindung testen',
    short: 'Versucht, sich mit den gespeicherten Angaben mit dem echten Dienst zu verbinden.',
  },
  'integrations.disconnect': {
    title: 'Verbindung trennen',
    short: 'Löscht die gespeicherten Angaben und deaktiviert die Integration.',
  },
  'integrations.environment': {
    title: 'Umgebung',
    short: 'Sandbox: kein echtes Geld. Live: echte Transaktionen.',
  },
  'audit.filter': { title: 'Filter', short: 'Filtert Einträge nach Typ (z. B. nur Shop-Änderungen).' },
  'system.refresh': { title: 'Aktualisieren', short: 'Misst den Status erneut.' },
  'setup.skip_step': {
    title: 'Schritt überspringen',
    short: 'Markiert diesen Schritt als vorerst nicht erforderlich.',
  },
  'account.change_password': {
    title: 'Passwort ändern',
    short: 'Mindestens 12 Zeichen. Andere Sitzungen werden abgemeldet.',
  },
  'account.sessions_revoke': {
    title: 'Sitzung abmelden',
    short: 'Beendet die Sitzung auf dem ausgewählten Gerät.',
  },
  'account.ui_mode': {
    title: 'Nutzungsmodus',
    short: 'Der Einsteigermodus zeigt Erklärungen an; der erweiterte Modus hält die Ansicht übersichtlich.',
  },
  'account.language': { title: 'Sprache der Verwaltung', short: 'Türkçe, English, Deutsch oder العربية.' },
  'help.edit': {
    title: 'Erklärung bearbeiten',
    short: 'Ändert diesen Hilfetext; die alte Version bleibt erhalten.',
  },
  'page.products': {
    title: 'Produkte',
    short: 'Alle Produkte: Varianten, Bestand, tierärztliche Prüfung und Veröffentlichungsstatus.',
  },
  'page.product_editor': {
    title: 'Produkt bearbeiten',
    short: 'Details, Inhalte, Varianten, Bilder, Preise und Veröffentlichung.',
  },
  'page.categories': { title: 'Kategorien', short: 'Produktgruppen in der Storefront.' },
  'page.pricing': {
    title: 'Preise',
    short: 'Kundengruppen, Preislisten, Steuerklassen und Massenpreisänderungen.',
  },
  'page.discounts': {
    title: 'Gutscheincodes',
    short: 'Prozentuale oder feste Rabatte, die per Code angewendet werden.',
  },
  'page.stock': {
    title: 'Bestand',
    short: 'Lager, Variantenbestand, Chargen / Mindesthaltbarkeitsdaten und Bewegungen.',
  },
  'page.customers': { title: 'Kunden', short: 'Mitglieder der Storefront und Händlerbenutzer.' },
  'page.customer_detail': {
    title: 'Kunde',
    short: 'Profil, Firmen, Einwilligungen, Tags, Bestellübersicht (RFM), Adressen und Bestellungen.',
  },
  'page.dealers': { title: 'Händler', short: 'Händlerfirmen, Status und Preisgruppen.' },
  'page.dealer_detail': { title: 'Händler', short: 'Firmendaten, Statusaktionen, Benutzer und Anträge.' },
  'page.dealer_applications': { title: 'Händleranträge', short: 'In der Storefront eingereichte Anträge.' },
  'page.imports': {
    title: 'Import / Export',
    short: 'Massen-Upload und -Download von Produkten und Händlern (Excel/CSV).',
  },
  'page.orders': {
    title: 'Bestellungen',
    short: 'Bestellungen aus der Storefront, von Händlern und aus der Verwaltung.',
  },
  'page.order_detail': {
    title: 'Bestellung',
    short: 'Positionen, Summen, Adressen, Verlauf und Statusaktionen.',
  },
  'products.create': { title: 'Neues Produkt', short: 'Legt ein Produkt als Entwurf an.' },
  'products.slug': {
    title: 'Kurzname (URL)',
    short: 'Die Adresse der Produktseite. Wird aus dem Titel erzeugt, wenn das Feld leer ist.',
  },
  'products.species': { title: 'Tierart', short: 'Für welche Tiere das Produkt bestimmt ist.' },
  'products.tax_class': { title: 'Steuerklasse', short: 'Leer: Die Standard-Steuerklasse wird verwendet.' },
  'products.status': { title: 'Status', short: 'Entwurf · Aktiv (verkäuflich) · Archiviert.' },
  'products.dealer_min_quantity': {
    title: 'Mindestmenge für Händler',
    short:
      'Händler bestellen von einer Variante mindestens diese Menge (gilt nicht für Endkunden). Wird auf die Kartongröße aufgerundet. Leer: keine Regel.',
  },
  'products.dealer_pack_size': {
    title: 'Kartongröße für Händler',
    short:
      'Händler bestellen in Vielfachen dieser Menge (z. B. 12, 24). Wird auf der Produktseite und im Warenkorb angezeigt. Leer: keine Regel.',
  },
  'pricing.group_min_order': {
    title: 'Mindestbestellwert',
    short:
      'Händler dieser Gruppe können unterhalb dieses Warenkorbwerts (ohne Versand) nicht bestellen. Leer: kein Mindestwert.',
  },
  'dealers.po_requirement': {
    title: 'Pflicht zum Bestellschein (PO)',
    short:
      'Bestellungen über die Storefront benötigen eine PO-Nummer oder eine Nummer und ein Dokument. Für Bestellungen, die Sie in der Verwaltung anlegen, nicht erforderlich.',
  },
  'dealers.min_order': {
    title: 'Mindestbestellwert des Händlers',
    short: 'Überschreibt den Gruppenbetrag für diesen Händler. Leer: Es gilt der Betrag der Händlergruppe.',
  },
  'products.visibility': { title: 'Sichtbar für', short: 'Endkunden und/oder freigegebene Händler.' },
  'products.health_content': {
    title: 'Gesundheitsbezogene Inhalte',
    short: 'Erfordert vor der Veröffentlichung die Freigabe durch die tierärztliche Fachprüfung.',
  },
  'products.feed_registration_no': {
    title: 'Futtermittel-Registriernummer',
    short: 'Registrierungs- bzw. Zulassungsnummer des Ministeriums.',
  },
  'products.variants': {
    title: 'Optionen und Varianten',
    short: 'Jede Optionskombination ist eine Variante mit eigener SKU und eigenem Bestand.',
  },
  'products.sku': { title: 'SKU', short: 'Eindeutige Artikelnummer.' },
  'products.cost': { title: 'Kosten', short: 'Einkaufskosten; nur mit der Kostenberechtigung sichtbar.' },
  'products.vet_review': {
    title: 'Tierärztliche Prüfung',
    short: 'Der tierärztliche Prüfer gibt den Inhalt frei oder lehnt ihn ab.',
  },
  'products.media_upload': {
    title: 'Bild hinzufügen',
    short: 'PNG/JPEG/WebP bis 5 MB; wird in WebP umgewandelt.',
  },
  'products.prices': {
    title: 'Produktpreise',
    short: 'Preis in jeder Preisliste; wird beim Verlassen des Feldes gespeichert.',
  },
  'products.publish': { title: 'Veröffentlichen', short: 'Zeigt das Produkt im ausgewählten Shop an.' },
  'categories.parent': { title: 'Übergeordnete Kategorie', short: 'Leer bedeutet oberste Ebene.' },
  'pricing.group_kind': {
    title: 'Gruppentyp',
    short: 'Händlergruppen enthalten freigegebene Händlerfirmen.',
  },
  'pricing.list_group': {
    title: 'Listengruppe',
    short: 'Leer: Basisliste für alle; eine Gruppe beschränkt die Liste auf diese Gruppe.',
  },
  'pricing.tax_inclusive': {
    title: 'Preise inkl. Steuer',
    short: 'Händlerlisten können ohne Steuer erfasst werden.',
  },
  'pricing.priority': {
    title: 'Priorität',
    short: 'Gelten mehrere Listen, setzt sich die höhere Priorität durch.',
  },
  'pricing.tax_class': { title: 'Steuerklassen', short: 'Den Produkten zugewiesene Steuersätze.' },
  'pricing.bulk_preview': {
    title: 'Vorschau der Massenpreise',
    short: 'Berechnet neue Preise, ohne etwas zu ändern.',
  },
  'pricing.bulk_apply': {
    title: 'Massenpreise anwenden',
    short:
      'Übernimmt die in der Vorschau gezeigten Preise (erfordert eine kürzlich erfolgte Zwei-Faktor-Authentifizierung).',
  },
  'pricing.bulk_revert': {
    title: 'Massenpreise zurücksetzen',
    short: 'Stellt die alten Preise wieder her; manuelle Änderungen bleiben erhalten.',
  },
  'pricing.price_ending': { title: 'Preisendung', short: 'Rundet auf ganze Beträge, ,90 oder ,99 auf.' },
  'discounts.code': { title: 'Gutscheincode', short: '3–32 Zeichen; Buchstaben, Ziffern, - und _.' },
  'discounts.min_subtotal': {
    title: 'Mindestwarenkorbwert',
    short: 'Unterhalb dieser Zwischensumme gilt der Gutscheincode nicht.',
  },
  'discounts.usage_limit': {
    title: 'Nutzungslimit',
    short: 'Wie viele Bestellungen ihn insgesamt nutzen können.',
  },
  'discounts.companies': {
    title: 'Händlerfirmen',
    short:
      'Gutscheincode nur für Händler: Nur Benutzer der ausgewählten freigegebenen Händlerfirmen können den Code verwenden.',
  },
  'discounts.groups': { title: 'Gültige Gruppen', short: 'Leer bedeutet alle.' },
  'stock.change_type': {
    title: 'Bewegungsart',
    short: 'Wareneingang, Korrektur, Beschädigung oder Inventur.',
  },
  'stock.lot': {
    title: 'Charge und Mindesthaltbarkeit',
    short:
      'Die Charge mit dem frühesten Mindesthaltbarkeitsdatum wird zuerst verkauft; abgelaufene Chargen werden nicht verkauft.',
  },
  'stock.transfer': { title: 'Umlagerung', short: 'Verschiebt Bestand zwischen Lagern.' },
  'stock.expiring': {
    title: 'Bald ablaufend',
    short: 'Chargen mit Bestand, die innerhalb der ausgewählten Anzahl von Tagen ablaufen.',
  },
  'customers.pii_masked': {
    title: 'Maskierte personenbezogene Daten',
    short: 'Ohne PII-Berechtigung werden E-Mail, Telefon und Adresse maskiert.',
  },
  'customers.access_link': {
    title: 'Zugangslink',
    short:
      'Aktivierungs- oder Passwort-Reset-Link; wird auf dem Bildschirm angezeigt, wenn E-Mail nicht eingerichtet ist.',
  },
  'customers.unlock': {
    title: 'Entsperren',
    short: 'Entsperrt ein Konto, das nach fehlgeschlagenen Anmeldeversuchen gesperrt wurde.',
  },
  'customers.consents': {
    title: 'Marketing-Einwilligungen',
    short: 'Verlauf der E-Mail-/SMS-Einwilligungen; wird nie gelöscht.',
  },
  'dealers.tax_number': {
    title: 'Steuernr. / nationale ID-Nr.',
    short: 'Wird verschlüsselt gespeichert; muss eindeutig sein.',
  },
  'dealers.external_code': {
    title: 'Händlercode',
    short: 'Code aus Ihrem bisherigen System; wird beim Import zum Abgleich verwendet.',
  },
  'dealers.group': { title: 'Händlergruppe', short: 'Legt fest, welche Preisliste der Händler sieht.' },
  'dealers.status_action': { title: 'Händlerstatus', short: 'Freigeben, sperren oder ablehnen.' },
  'dealers.add_member': {
    title: 'Benutzer hinzufügen',
    short: 'Verknüpft einen Mitarbeiter des Händlers und erstellt einen Aktivierungslink.',
  },
  'applications.documents': {
    title: 'Dokumente',
    short: 'Private Dateien; nur berechtigte Benutzer der Verwaltung können sie herunterladen.',
  },
  'applications.decision': {
    title: 'Entscheidung',
    short: 'Wählen Sie zur Freigabe eine Händlergruppe; geben Sie für eine Ablehnung einen Grund an.',
  },
  'imports.products_file': {
    title: 'Produktdatei hochladen',
    short: 'Unsere Vorlage, eine Shopify-Produkt-CSV oder ein Produktdokument im JSON- bzw. XML-Format.',
  },
  'imports.dealers_file': {
    title: 'Händlerliste hochladen',
    short: 'Excel/CSV mit Firmenname und Händlercode oder Steuernummer.',
  },
  'imports.price_list': {
    title: 'Zielpreisliste',
    short: 'Die Spalte „Preis“ wird in diese Liste geschrieben.',
  },
  'imports.warehouse': {
    title: 'Ziellager',
    short: 'Die Spalte „Bestand“ wird hier als Inventurzählung geschrieben.',
  },
  'imports.default_brand': {
    title: 'Standardmarke',
    short: 'Neue Produkte in Zeilen mit leerer Spalte „Marke“ erhalten diese Marke.',
  },
  'imports.default_status': {
    title: 'Standardstatus',
    short: 'Wird verwendet, wenn die Statusspalte leer ist.',
  },
  'imports.send_links': {
    title: 'Aktivierungs-E-Mail',
    short: 'Sendet Links zum Festlegen des Passworts, wenn E-Mail eingerichtet ist.',
  },
  'imports.download_issues': {
    title: 'Problemliste herunterladen',
    short: 'Lädt die in der Vorschau gefundenen Probleme als CSV mit Zeilennummern herunter.',
  },
  'imports.export': {
    title: 'Exportieren',
    short: 'Lädt dieselben Spalten als Excel oder als JSON- bzw. XML-Dokument herunter.',
  },
  'orders.manual_create': {
    title: 'Bestellung anlegen',
    short: 'Legt eine Bestellung zum Preis des Kunden an.',
  },
  'orders.ledger_settled': {
    title: 'Über Kundenkonto ausgeglichen',
    short:
      'Die Forderung aus dem offenen Konto wurde vollständig durch Zahlungen beglichen, die auf dem Kundenkonto des Händlers erfasst wurden.',
  },
  'orders.status_change': {
    title: 'Status ändern',
    short: 'Bringt die Bestellung zum nächsten Schritt; eine Stornierung gibt den Bestand frei.',
  },
  'orders.payment_note': {
    title: 'Zahlungsnotiz',
    short: 'Beschreiben Sie, wie die Zahlung eingegangen ist.',
  },
  'orders.notes': { title: 'Interne Notiz', short: 'Nur in der Verwaltung sichtbar.' },
  'orders.po': {
    title: 'Bestellschein und Firmenfreigabe',
    short:
      'Nummer und Dokument des Bestellscheins (PO) des Händlers; wer die Bestellung eines Käufers oberhalb seines Schwellenwerts freigegeben hat.',
  },
  'orders.po_number': {
    title: 'PO-Nummer',
    short:
      'Die eigene Bestellscheinnummer des Händlers (bis zu 50 Zeichen). Bestellungen lassen sich danach durchsuchen.',
  },
  'orders.po_document': {
    title: 'PO-Dokument anhängen oder ersetzen',
    short:
      'PDF, JPEG oder PNG (bis zu 10 MB). Wird privat gespeichert; nur Benutzer der Verwaltung, die Bestellungen sehen dürfen, können es herunterladen.',
  },
  'orders.company_approval': {
    title: 'Firmenfreigabe',
    short:
      'Die verantwortliche Person der Firma hat den Warenkorb des Käufers oberhalb seines Schwellenwerts in der Storefront freigegeben.',
  },
  'page.order_print': {
    title: 'Bestellungen drucken',
    short: 'Bestellübersicht oder Packliste der ausgewählten Bestellungen.',
  },
  'orders.guest': {
    title: 'Kundentyp',
    short: 'Registrierter Kunde (Preis seiner Gruppe) oder Gast (Endkundenpreis; E-Mail erforderlich).',
  },
  'orders.shipping_fee': {
    title: 'Versand',
    short: 'Gebühr aus der Versandart oder eine manuelle Gebühr mit Preisberechtigung.',
  },
  'orders.manual_discount': { title: 'Rabatt', short: 'Gutscheincode oder manueller Betrag; nicht beides.' },
  'orders.edit_draft': {
    title: 'Entwurf bearbeiten',
    short: 'Bei einem Entwurf lässt sich alles ändern; die Preise werden neu berechnet.',
  },
  'orders.edit_lines': {
    title: 'Artikel bearbeiten',
    short: 'Artikel und Mengen können vor der Zahlung geändert werden.',
  },
  'orders.edit_address': {
    title: 'Adresse und Notiz bearbeiten',
    short: 'Möglich, bis eine Sendung erstellt wird.',
  },
  'orders.payment_link': {
    title: 'Zahlungslink',
    short: 'Erstellt einen zeitlich begrenzten Link, mit dem der Kunde die Bestellung per Karte bezahlt.',
  },
  'orders.payment_link_hours': {
    title: 'Gültig für',
    short: 'Danach nimmt der Link keine Zahlungen mehr an.',
  },
  'orders.payment_link_email': {
    title: 'Per E-Mail senden',
    short: 'Sendet den Link an die E-Mail-Adresse der Bestellung.',
  },
  'orders.bulk_status': {
    title: 'Status mehrerer Bestellungen ändern',
    short: 'Jede ausgewählte Bestellung wird nach ihren eigenen Regeln geändert.',
  },
  'orders.bulk_print': {
    title: 'Drucken',
    short: 'Öffnet die Bestellübersicht oder Packliste der ausgewählten Bestellungen.',
  },
  'orders.bulk_export': {
    title: 'Als Excel herunterladen',
    short: 'Exportiert die ausgewählten Bestellungen mit maskierten personenbezogenen Daten.',
  },
  'orders.print': { title: 'Drucken / als PDF speichern', short: 'Öffnet den Druckdialog des Browsers.' },
  'page.payment_settings': {
    title: 'Zahlungseinstellungen (POS-Einrichtung)',
    short:
      'Kartenzahlung (iyzico/PayTR), Banküberweisung, Nachnahme, offenes Konto für Händler; Checkliste für den Livegang.',
  },
  'page.payments': {
    title: 'Zahlungen',
    short: 'Alle Zahlungsversuche, Einzüge, Erstattungen und der Abgleich mit dem Anbieter.',
  },
  'page.invoices': {
    title: 'Rechnungen',
    short: 'Bestellrechnungen, die an BizimHesap gesendet oder manuell erfasst wurden.',
  },
  'page.redirects': {
    title: 'Weiterleitungen',
    short: 'Leitet alte Adressen auf neue um (301 dauerhaft, 302 vorübergehend).',
  },
  'redirects.add': { title: 'Weiterleitung hinzufügen', short: 'Leitet eine alte Adresse auf eine neue um.' },
  'redirects.import': {
    title: 'Aus Datei importieren',
    short:
      'Lädt die Adressliste der alten Website gesammelt hoch (CSV/Excel); zuerst wird eine Vorschau angezeigt.',
  },
  'redirects.search': {
    title: 'Adressen suchen',
    short: 'Geben Sie einen Teil einer alten oder neuen Adresse ein.',
  },
  'redirects.from': { title: 'Alte Adresse', short: 'Weiterzuleitender Pfad, beginnend mit „/“.' },
  'redirects.to': {
    title: 'Neue Adresse',
    short: 'Pfad auf dieser Website oder eine vollständige Adresse, die mit https:// beginnt.',
  },
  'redirects.code': {
    title: 'Weiterleitungstyp',
    short:
      '301 ist dauerhaft und teilt Suchmaschinen mit, dass die Seite umgezogen ist; 302 ist vorübergehend.',
  },
  'redirects.note': { title: 'Notiz', short: 'Eine Notiz für Sie selbst.' },
  'redirects.template': {
    title: 'Vorlage herunterladen',
    short: 'Leere CSV-Vorlage mit den richtigen Spaltenüberschriften.',
  },
  'page.shipping': {
    title: 'Versandarten',
    short: 'Versandoptionen und -preise, die an der Kasse angezeigt werden.',
  },
  'page.legal': {
    title: 'Rechtstexte',
    short:
      'Datenschutzhinweis, Mitgliedschafts- und Fernabsatzvertrag, Formular zur Vorabinformation, Rücksendungen, Datenschutz- und Cookie-Richtlinie.',
  },
  'payments.card_provider': {
    title: 'Kartenanbieter',
    short:
      'iyzico, PayTR oder Sipay; der Verbindungstest muss für die ausgewählte Umgebung erfolgreich sein.',
  },
  'payments.environment': {
    title: 'Umgebung',
    short:
      'Im Test (Sandbox) wird kein echtes Geld belastet. Für den Livegang ist eine gesonderte Bestätigung erforderlich.',
  },
  'payments.max_installments': {
    title: 'Ratenzahlung',
    short: 'Maximale Anzahl an Raten, die Kunden angeboten wird.',
  },
  'payments.card_capture': {
    title: 'Zeitpunkt der Belastung',
    short: 'Vorautorisierung an der Kasse und Belastung beim Versand (nur iyzico).',
  },
  'orders.preauth_capture': {
    title: 'Einziehen',
    short: 'Belastet den aktuellen Bestellbetrag aus der Reservierung auf der Karte.',
  },
  'orders.preauth_release': {
    title: 'Vorautorisierung freigeben',
    short: 'Gibt die Reservierung auf der Karte frei und storniert die Bestellung.',
  },
  'payments.card_timeout': {
    title: 'Zeitlimit für Kartenzahlungen',
    short:
      'Nicht abgeschlossene Kartenzahlungen werden nach dieser Zeit storniert; der Bestand wird freigegeben.',
  },
  'payments.bank_transfer_hold': {
    title: 'Zahlungsfrist für Banküberweisung',
    short:
      'Bestellungen per Banküberweisung, die nicht innerhalb dieser Zeit bezahlt werden, werden automatisch storniert und der Bestand wird freigegeben.',
  },
  'payments.notify_urls': {
    title: 'Benachrichtigungs-URLs',
    short:
      'Adressen, die in den Verwaltungsoberflächen der Anbieter als Callback- bzw. Benachrichtigungs-URL einzutragen sind.',
  },
  'payments.other_methods': {
    title: 'Weitere Zahlungsarten',
    short: 'Banküberweisung, Nachnahme, offenes Konto für Händler und Bestellung als Gast.',
  },
  'payments.cod_fee': {
    title: 'Nachnahmegebühr',
    short: 'Servicegebühr, die der Bestellung hinzugefügt wird (inkl. Steuer).',
  },
  'payments.cod_max': {
    title: 'Nachnahme-Höchstbetrag',
    short: 'Wird oberhalb dieses Bestellwerts nicht angeboten. Leer: keine Grenze.',
  },
  'payments.bank_account': {
    title: 'Bankkonto',
    short: 'Für Banküberweisungen angezeigte IBANs; Änderungen sind sensibel und werden protokolliert.',
  },
  'payments.iban': { title: 'IBAN', short: '26 Zeichen, beginnend mit TR.' },
  'payments.reconcile': {
    title: 'Abgleich',
    short: 'Vergleicht den Tagesbericht des Anbieters mit Ihren Zahlungen.',
  },
  'shipping.method': {
    title: 'Versandart',
    short: 'Fügt eine Versandoption für die Kasse hinzu oder bearbeitet sie.',
  },
  'shipping.handling_days': {
    title: 'Bearbeitungszeit (Werktage)',
    short: 'Von der Bestellung bis zur Übergabe an den Versanddienstleister.',
  },
  'shipping.transit_days': {
    title: 'Laufzeit (Werktage)',
    short: 'Transportzeit beim Versanddienstleister.',
  },
  'shipping.carrier': {
    title: 'Versanddienstleister',
    short: 'Wird in Sendungsverfolgungslinks und E-Mails angezeigt; keine API erforderlich.',
  },
  'shipping.audience': { title: 'Zielgruppe', short: 'Alle, nur Endkunden oder nur freigegebene Händler.' },
  'shipping.rate_type': {
    title: 'Tariftyp',
    short: 'Pauschalpreis oder nach Gewichtsstufen des Warenkorbs.',
  },
  'shipping.free_over': {
    title: 'Kostenloser Versand ab',
    short: 'Der Versand ist kostenlos, sobald der Warenwert diesen Betrag erreicht.',
  },
  'legal.variables': {
    title: 'Variablen',
    short: '{{field}}-Platzhalter werden bei der Anzeige mit echten Daten befüllt.',
  },
  'legal.languages': {
    title: 'Sprachfassungen',
    short:
      'Jeder Text hat je Sprache einen eigenen Entwurf, eine eigene Veröffentlichung und eine eigene Prüfungserklärung. Türkisch ist die Hauptfassung.',
  },
  'legal.language': {
    title: 'Sprache des Textes',
    short: 'Die Sprache, die Sie bearbeiten. Jede Sprache wird getrennt gespeichert und veröffentlicht.',
  },
  'legal.copy_tr': {
    title: 'Mit dem türkischen Text beginnen',
    short:
      'Kopiert den türkischen Text (mit Variablen) in den Entwurf dieser Sprache, damit Sie ihn übersetzen.',
  },
  'legal.reviewed': { title: 'Rechtliche Prüfung', short: 'Erklärt, dass ein Anwalt den Text geprüft hat.' },
  'legal.publish': {
    title: 'Rechtstext veröffentlichen',
    short:
      'Veröffentlicht den gespeicherten Entwurf dieser Sprache als neue Version; die vorherige wird archiviert.',
  },
  'invoices.send': {
    title: 'An BizimHesap senden',
    short: 'Sendet die Rechnung an BizimHesap; bei Zeitüberschreitung wird sie als „unklar“ markiert.',
  },
  'invoices.uncertain': {
    title: 'Unklare Rechnung',
    short: 'Prüfen Sie in BizimHesap und erfassen Sie, ob die Rechnung erstellt wurde.',
  },
  'invoices.create': {
    title: 'Rechnung erstellen',
    short: 'Rechnungsentwurf für BizimHesap oder Erfassung einer anderweitig ausgestellten Rechnung.',
  },
  'orders.capture': {
    title: 'Zahlung erhalten',
    short: 'Erfasst eine Zahlung per Banküberweisung, Nachnahme oder offenem Konto.',
  },
  'orders.refund': {
    title: 'Erstattung',
    short:
      'Kartenerstattungen laufen über den Anbieter; bei anderen Zahlungsarten wird eine manuelle Erstattung erfasst.',
  },
  'orders.resolve_refund': {
    title: 'Ergebnis der Erstattung festlegen',
    short:
      'Schließt eine unterbrochene Erstattung nach Prüfung im Anbieter-Panel als erfolgt oder nicht erfolgt ab.',
  },
  'orders.resolve_refund_outcome': {
    title: 'Ergebnis im Anbieter-Panel',
    short: 'Ob diese Erstattung in der Transaktionsliste von iyzico oder PayTR erscheint.',
  },
  'orders.resolve_refund_note': {
    title: 'Notiz',
    short: 'Was Sie für die Entscheidung geprüft haben; wird im Prüfprotokoll festgehalten.',
  },
  'orders.returns': {
    title: 'Rücksende- / Widerrufsanfragen',
    short:
      'Rücksende-, Widerrufs- und Umtauschanfragen, die Kunden auf ihrer Bestellseite stellen; eine Freigabe löst keine Erstattung aus.',
  },
  'orders.address_place': {
    title: 'Provinz und Bezirk',
    short:
      'Wählen Sie für Adressen in der Türkei Provinz und Bezirk aus der Liste. Die Postleitzahl muss mit dem Provinzcode beginnen.',
  },
  'orders.cancel_items': {
    title: 'Restliche Artikel stornieren',
    short:
      'Storniert Artikel einer teilweise versendeten Bestellung, die nicht versendet werden können; reservierter Bestand wird freigegeben und der Kunde per E-Mail informiert.',
  },
  'orders.exchange_ship': {
    title: 'Umtausch versenden',
    short:
      'Sobald der zurückgesendete Artikel eingetroffen ist, wird der neue Artikel mit Sendungsnummer versendet; der reservierte Artikel wird aus dem Bestand gebucht und die Anfrage abgeschlossen.',
  },
  'orders.return_note': {
    title: 'Notiz / Grund',
    short:
      'Wird dem Kunden per E-Mail gesendet und auf seiner Bestellseite angezeigt. Erforderlich bei Ablehnung und Stornierung.',
  },
  'orders.restock': {
    title: 'Wieder einlagern',
    short: 'Bucht zurückgesendete Artikel wieder in dieselbe Charge ein.',
  },
  'orders.ship': {
    title: 'Versenden',
    short:
      'Erstellt eine Sendung mit Sendungsnummer; die verpackten Mengen werden aus dem Bestand gebucht. Ist nichts mehr zu versenden, gilt die Bestellung als versendet.',
  },
  'orders.ship_lines': {
    title: 'Artikel in diesem Paket',
    short:
      'In diesem Paket versendete Mengen. Standardmäßig alles noch nicht Versendete; setzen Sie einen Artikel auf 0, um ihn für ein späteres Paket zurückzuhalten.',
  },
  'orders.manual_payment_method': {
    title: 'Zahlungsart',
    short:
      'Manuell/Banküberweisung: später als „Zahlung erhalten“ markieren. Offenes Konto/Nachnahme: Die Bestellung wird direkt vorbereitet.',
  },
  'page.page_designs': {
    title: 'Seitendesigns',
    short: 'Wählt eine von drei Designoptionen für das Hauptmenü und die Artikelseite.',
  },
  'page_designs.header': {
    title: 'Design des Hauptmenüs',
    short: 'Anordnung von Logo, Menüpunkten, Suche und Warenkorb im Kopfbereich.',
  },
  'page_designs.articles': {
    title: 'Design der Artikelseite',
    short: 'Aussehen der Blogliste sowie der Kategorie- und Archivseiten.',
  },
  'page_designs.preview': {
    title: 'Vorschau',
    short: 'Öffnet die Option im Shop nur in Ihrem Browser, ohne zu veröffentlichen.',
  },
  'page_designs.publish': {
    title: 'Veröffentlichen',
    short: 'Schaltet die Option sofort für alle Besucher frei.',
  },
  'page_designs.history': {
    title: 'Änderungsverlauf',
    short: 'Die letzten 30 Optionswechsel: Datum, Person, Bereich und Änderung.',
  },
  'page.sales_points': {
    title: 'Verkaufsstellen',
    short: 'Verwaltet die Händler auf der Storefront-Seite „Verkaufsstellen“.',
  },
  'dealers.sales_points': {
    title: 'Verkaufsstelle',
    short: 'Geschäftsangaben dieses Händlers auf der Seite „Verkaufsstellen“.',
  },
  'sales_points.auto_add': {
    title: 'Automatisch aufnehmen',
    short: 'Neu freigegebene Händler werden aus ihren Standorten aufgenommen (standardmäßig an).',
  },
  'sales_points.visible': {
    title: 'In der Storefront anzeigen',
    short: 'Wenn aus, ist die Verkaufsstelle ausgeblendet; der Eintrag bleibt.',
  },
  'sales_points.name': { title: 'Anzeigename', short: 'Laden- oder Geschäftsname in der Storefront.' },
  'sales_points.neighbourhood': {
    title: 'Viertel',
    short: 'Viertel aus der PTT-Liste; Besucher können danach filtern.',
  },
  'sales_points.pin': {
    title: 'Standort (Kartenpin)',
    short: 'Aus Google Maps kopierte Koordinaten oder Link.',
  },
  'sales_points.phone': { title: 'Storefront-Telefon', short: 'Die Geschäftsnummer für Besucher.' },
  'sales_points.hours': {
    title: 'Öffnungszeiten',
    short: 'Geöffnet/geschlossen und Zeiten je Tag; nicht angegebene Tage werden nicht gezeigt.',
  },
  'sales_points.note': {
    title: 'Hinweis',
    short: 'Kurzer Hinweis unter der Karte (z. B. „Parkplätze vorhanden“).',
  },
  'sales_points.bulk': {
    title: 'Gesammelt anzeigen/ausblenden',
    short: 'Zeigt oder blendet die Auswahl aus; wird protokolliert.',
  },
  'sales_points.from_location': {
    title: 'Aus Standort hinzufügen',
    short: 'Erstellt eine Verkaufsstelle aus einem Händlerstandort.',
  },
  'sales_points.geocode': {
    title: 'Standort aus Adresse ermitteln',
    short:
      'Verkaufsstellen ohne manuelle Markierung werden über OpenStreetMap anhand der Adresse verortet (standardmäßig an).',
  },
  'sales_points.backfill': {
    title: 'Aus freigegebenen Händlern erstellen',
    short:
      'Legt für jeden freigegebenen Händler ohne Verkaufsstelle eine sichtbare Verkaufsstelle aus Standort oder Firmenadresse an.',
  },
  'sales_points.geocode_now': {
    title: 'Standorte jetzt ermitteln',
    short: 'Sucht sofort die Adressen von bis zu 10 wartenden Verkaufsstellen (eine Anfrage pro Sekunde).',
  },
  'sales_points.geocode_one': {
    title: 'Standort aus Adresse ermitteln',
    short: 'Sucht diese Verkaufsstelle in OpenStreetMap und speichert das Ergebnis als Markierung.',
  },
  'page_designs.store_locator': {
    title: 'Design der Verkaufsstellen',
    short: 'Layout der Verkaufsstellen-Seite und der Provinz-/Bezirksseiten.',
  },
  'page.design': {
    title: 'Design',
    short: 'Ändern Sie Farben, Schriften und Layout der Storefront ohne Code.',
  },
  'design.preview': {
    title: 'Vorschau in der Storefront',
    short: 'Zeigt den Entwurf in der echten Storefront, nur für Sie (Link 2 Stunden gültig).',
  },
  'design.save': {
    title: 'Entwurf speichern',
    short: 'Speichert die Änderungen als Entwurf; die Storefront ändert sich nicht.',
  },
  'design.publish': {
    title: 'Veröffentlichen',
    short: 'Macht den Entwurf öffentlich und speichert ihn als neue Version.',
  },
  'design.revert_local': {
    title: 'Meine Änderungen verwerfen',
    short: 'Verwirft nicht gespeicherte Änderungen und zeigt den gespeicherten Entwurf.',
  },
  'design.discard': {
    title: 'Auf das Live-Theme zurücksetzen',
    short: 'Löscht nicht veröffentlichte Entwurfsänderungen; der Entwurf entspricht dann der Live-Version.',
  },
  'design.templates': {
    title: 'Vorlagen',
    short: 'Fertige Ausgangspunkte, die Farben, Schriften und Layout gemeinsam ändern.',
  },
  'design.palettes': {
    title: 'Fertige Paletten',
    short: 'Auf Kontrast geprüfte Farbsets; Schriften und Layout bleiben erhalten.',
  },
  'design.color.background': { title: 'Hintergrund', short: 'Haupthintergrund der Seite.' },
  'design.color.surface': {
    title: 'Hintergrund von Karten und Feldern',
    short: 'Hintergrund von Produktkarten, Kopfbereich, Zusammenfassungen und Feldern.',
  },
  'design.color.text': {
    title: 'Text',
    short: 'Farbe für Fließtext und Überschriften; erfordert einen Kontrast von mindestens 4,5:1.',
  },
  'design.color.muted_text': {
    title: 'Sekundärer Text',
    short: 'Beschreibungen, Markennamen und Datumsangaben; erfordert einen Kontrast von mindestens 4,5:1.',
  },
  'design.color.border': { title: 'Linien und Rahmen', short: 'Kartenrahmen und Trennlinien.' },
  'design.color.primary': {
    title: 'Primärfarbe',
    short: 'Links, ausgewählte Tabs und Hervorhebungen; erfordert einen Kontrast von mindestens 4,5:1.',
  },
  'design.color.secondary': {
    title: 'Sekundärfarbe',
    short: 'Sekundäre Labels und Badges; die Textfarbe wird automatisch gewählt.',
  },
  'design.color.accent': {
    title: 'Akzentfarbe',
    short: 'Aktions- und Rabattlabels; die Textfarbe wird automatisch gewählt.',
  },
  'design.color.button': {
    title: 'Schaltfläche',
    short: 'Farbe der Hauptschaltflächen wie „In den Warenkorb“.',
  },
  'design.color.button_text': {
    title: 'Schaltflächentext',
    short: 'Text auf den Hauptschaltflächen; erfordert einen Kontrast von mindestens 4,5:1.',
  },
  'design.color.button_hover': {
    title: 'Schaltfläche (Hover)',
    short: 'Farbe der Schaltfläche, während sich der Mauszeiger darüber befindet.',
  },
  'design.dark_mode': {
    title: 'Dunkle Darstellung',
    short: 'Verwendet eigene dunkle Farben, wenn sich das Gerät des Besuchers im Dunkelmodus befindet.',
  },
  'design.font_body': {
    title: 'Schrift für Fließtext',
    short: 'Schrift für Texte, Preise und Formulare. Wird mit der Storefront ausgeliefert.',
  },
  'design.font_heading': {
    title: 'Schrift für Überschriften',
    short: 'Schrift für Seiten- und Produktüberschriften.',
  },
  'design.base_size': {
    title: 'Textgröße',
    short: 'Basisschriftgröße; Überschriften skalieren davon ausgehend.',
  },
  'design.heading_scale': {
    title: 'Überschriftengrößen',
    short: 'Wie viel größer Überschriften als der Fließtext sind.',
  },
  'design.heading_weight': {
    title: 'Schriftstärke der Überschriften',
    short: 'Strichstärke der Überschriften.',
  },
  'design.radius': { title: 'Eckenradius', short: 'Ecken von Karten, Feldern und Bildern (0 = eckig).' },
  'design.button_shape': {
    title: 'Schaltflächenform',
    short: 'Eckige, abgerundete oder pillenförmige Schaltflächen.',
  },
  'design.shadow': {
    title: 'Schatten',
    short: 'Schatten für hervorgehobene Karten und Zusammenfassungsboxen.',
  },
  'design.card_style': { title: 'Produktkarte', short: 'Produktkarten mit Rahmen, flach oder erhöht.' },
  'design.image_ratio': {
    title: 'Seitenverhältnis der Produktbilder',
    short: 'Form des Bildbereichs in Produktlisten.',
  },
  'design.grid_desktop': {
    title: 'Spalten auf dem Desktop',
    short: 'Produkte pro Zeile auf breiten Bildschirmen; Tablets verwenden höchstens 3.',
  },
  'design.grid_mobile': {
    title: 'Spalten auf Smartphones',
    short: 'Ein oder zwei Produkte pro Zeile auf Smartphones.',
  },
  'design.header_layout': { title: 'Logoposition', short: 'Logo links oder mittig im Kopfbereich.' },
  'design.header_style': {
    title: 'Hintergrund des Kopfbereichs',
    short: 'Heller Kopfbereich oder Kopfbereich in Primärfarbe.',
  },
  'design.header_sticky': {
    title: 'Fixierter Kopfbereich',
    short: 'Hält den Kopfbereich beim Scrollen oben.',
  },
  'design.footer_style': {
    title: 'Hintergrund der Fußzeile',
    short: 'Helle, dunkle oder primärfarbene Fußzeile.',
  },
  'design.gallery': {
    title: 'Bild auf der Produktseite',
    short: 'Produktbild auf breiten Bildschirmen links oder rechts.',
  },
  'design.blog_layout': { title: 'Blogseite', short: 'Blogbeiträge als Raster oder Liste.' },
  'design.input_style': { title: 'Formularfelder', short: 'Umrandete oder gefüllte Formularfelder.' },
  'design.live_preview': {
    title: 'Live-Vorschau',
    short: 'So sehen die Änderungen vor dem Speichern auf einer Beispielseite der Storefront aus.',
  },
  'design.device': {
    title: 'Gerät',
    short: 'Zeigt die Vorschau in Smartphone-, Tablet- oder Desktop-Breite.',
  },
  'design.contrast': {
    title: 'Lesbarkeitsprüfung',
    short: 'Prüft den WCAG-AA-Kontrast (mindestens 4,5:1) zwischen Text und Hintergründen.',
  },
  'design.versions': {
    title: 'Versionsverlauf',
    short: 'Jedes veröffentlichte Theme mit Datum und der Person, die es veröffentlicht hat.',
  },
  'design.rollback': {
    title: 'Zu dieser Version zurückkehren',
    short: 'Veröffentlicht die ausgewählte Version erneut und lädt sie in den Entwurf.',
  },
  'design.note': {
    title: 'Versionsnotiz',
    short: 'Warum diese Version veröffentlicht wurde; wird im Versionsverlauf angezeigt.',
  },
  'page.menus': {
    title: 'Menüs',
    short: 'Bearbeiten Sie das Kopfmenü (Mega-Menü) und das Fußzeilenmenü der Storefront.',
  },
  'menus.location': {
    title: 'Menüposition',
    short: 'Kopf- oder Fußzeilenmenü; beide werden separat gespeichert.',
  },
  'menus.add': {
    title: 'Eintrag hinzufügen',
    short: 'Fügt am Ende einen neuen Eintrag der obersten Ebene hinzu.',
  },
  'menus.add_child': {
    title: 'Untereintrag hinzufügen',
    short: 'Fügt unter diesem Eintrag einen weiteren Eintrag hinzu.',
  },
  'menus.label': {
    title: 'Im Menü angezeigter Name',
    short: 'Der Text, den Besucher sehen (max. 60 Zeichen).',
  },
  'menus.link': {
    title: 'Link',
    short:
      'Ziel des Eintrags: Kategorie, Produkt, alle Produkte, eine Seite, eine Adresse oder eine Überschrift ohne Link.',
  },
  'menus.category': {
    title: 'Kategorie',
    short: 'Die zu verlinkende Kategorie. Ausgeblendete Kategorien werden nicht angezeigt.',
  },
  'menus.product': {
    title: 'Produkt',
    short: 'Suchen Sie nach Name, SKU oder Barcode und wählen Sie ein Produkt aus.',
  },
  'menus.url': {
    title: 'Adresse',
    short: 'Ein Pfad der Website (z. B. /iletisim) oder eine vollständige https://-Adresse.',
  },
  'menus.new_tab': { title: 'In neuem Tab öffnen', short: 'Für Links zu anderen Websites.' },
  'menus.move': {
    title: 'Neu anordnen',
    short: 'Verschiebt den Eintrag innerhalb seiner Ebene nach oben oder unten.',
  },
  'menus.remove': {
    title: 'Entfernen',
    short: 'Entfernt den Eintrag und seine Untereinträge (die Storefront ändert sich beim Speichern).',
  },
  'menus.save': { title: 'Menü speichern', short: 'Speichert das Menü für die ausgewählte Position.' },
  'menus.page': {
    title: 'Seite',
    short: 'Eine Unternehmens- oder Aktionsseite; wird nur im Menü angezeigt, solange sie live ist.',
  },
  'menus.unavailable': {
    title: 'Ziel nicht sichtbar',
    short:
      'Die Kategorie ist ausgeblendet oder das Produkt bzw. die Seite ist nicht live; der Eintrag wird nicht angezeigt.',
  },
  'page.pages': {
    title: 'Seiten',
    short:
      'Gestalten Sie das Layout der Startseite, Unternehmensseiten und Aktionsseiten Abschnitt für Abschnitt.',
  },
  'page.page_editor': {
    title: 'Seiteneditor',
    short: 'Bearbeiten Sie Seitentitel, Adresse, SEO-Angaben und Abschnitte.',
  },
  'pages.create': { title: 'Neue Seite', short: 'Legt eine Seite als Entwurf an und öffnet den Editor.' },
  'pages.kind': {
    title: 'Seitentyp',
    short:
      'Startseiten-Layout (eines pro Shop, unter „/“), Unternehmensseite oder Aktionsseite (unter /sayfa/kurzname).',
  },
  'pages.title': {
    title: 'Seitentitel',
    short: 'Wird in der Verwaltung und in der Storefront angezeigt (max. 120 Zeichen).',
  },
  'pages.slug': {
    title: 'Kurzname (Adresse)',
    short:
      'Kleinbuchstaben, Ziffern und Bindestriche. Bei einer Live-Seite ändert er sich beim Speichern und die alte Adresse wird weitergeleitet (erfordert die Berechtigung zum Veröffentlichen).',
  },
  'pages.seo_title': {
    title: 'SEO-Titel',
    short: 'Wird in den Suchergebnissen und im Browser-Tab angezeigt (max. 70).',
  },
  'pages.seo_description': {
    title: 'SEO-Beschreibung',
    short: 'Kurzer Text unter dem Titel in den Suchergebnissen (max. 170).',
  },
  'pages.health_content': {
    title: 'Gesundheitsbezogene Inhalte',
    short:
      'Seiten zur Tiergesundheit erfordern vor der Veröffentlichung die Freigabe durch die tierärztliche Fachprüfung.',
  },
  'pages.preview': {
    title: 'Vorschau',
    short:
      'Speichert die Änderungen und öffnet den Entwurf in der Storefront in einem neuen Tab (signierter Link, 2 Stunden gültig).',
  },
  'pages.save': {
    title: 'Entwurf speichern',
    short: 'Speichert die Änderungen als Entwurf; die Storefront ändert sich nicht.',
  },
  'pages.publish': {
    title: 'Veröffentlichen',
    short: 'Veröffentlicht den Entwurf als neue Version, optional ab einem Start- bis zu einem Enddatum.',
  },
  'pages.schedule': {
    title: 'Veröffentlichungs- und Enddatum',
    short:
      'Leer lassen, um sofort und ohne Ende zu veröffentlichen. Das Enddatum muss nach dem Startdatum liegen.',
  },
  'pages.unpublish': {
    title: 'Veröffentlichung zurückziehen',
    short: 'Entfernt die Seite aus der Storefront; Entwürfe und Versionen bleiben erhalten.',
  },
  'pages.delete': { title: 'Seite löschen', short: 'Löscht eine nie veröffentlichte Seite dauerhaft.' },
  'pages.review': {
    title: 'Fachprüfung',
    short:
      'Der tierärztliche Prüfer gibt frei oder lehnt mit Begründung ab. Die Freigabe ist an den aktuellen Inhalt gebunden.',
  },
  'pages.review_note': {
    title: 'Prüfnotiz',
    short: 'Bei Ablehnung erforderlich: Teilen Sie dem Bearbeiter mit, was zu korrigieren ist.',
  },
  'pages.section_add': {
    title: 'Abschnitt hinzufügen',
    short: 'Fügt am Ende einen Abschnitt des gewählten Typs hinzu (max. 40).',
  },
  'pages.section_hide': {
    title: 'Abschnitt ausblenden',
    short: 'Behält den Abschnitt im Entwurf, blendet ihn aber in der Storefront aus.',
  },
  'pages.section_move': {
    title: 'Abschnitt verschieben',
    short: 'Verschiebt den Abschnitt nach oben oder unten.',
  },
  'pages.section_remove': { title: 'Abschnitt entfernen', short: 'Entfernt den Abschnitt aus dem Entwurf.' },
  'pages.heading': {
    title: 'Abschnittsüberschrift',
    short: 'Wird über dem Abschnitt angezeigt. Die erste Hero-Überschrift ist die Seitenüberschrift.',
  },
  'pages.cta': {
    title: 'Schaltfläche',
    short: 'Text und Adresse der Hero-Schaltfläche (z. B. /urunler oder eine https://-Adresse).',
  },
  'pages.rich_text': {
    title: 'Texteditor',
    short:
      'Absätze, Überschriften, fett, kursiv, Listen, Zitate, Trennlinien, Links und Bilder (Alt-Text erforderlich). Verschieben Sie einen Block per Drag-and-drop oder mit den Auf-/Ab-Schaltflächen.',
  },
  'pages.image': { title: 'Bild hochladen', short: 'Lädt ein PNG-, JPEG-, WebP-, AVIF- oder GIF-Bild hoch.' },
  'pages.image_alt': { title: 'Alt-Text', short: 'Beschreiben Sie kurz, was das Bild zeigt. Erforderlich.' },
  'pages.image_position': {
    title: 'Bildposition',
    short: 'Bild auf dem Desktop links oder rechts vom Text.',
  },
  'pages.grid_source': {
    title: 'Produktquelle',
    short: 'Neueste Produkte, eine Kategorie oder von Ihnen ausgewählte Produkte.',
  },
  'pages.grid_collection': {
    title: 'Kollektion',
    short: 'Eine der Kollektionen unter Katalog → Kollektionen.',
  },
  'pages.grid_brand': {
    title: 'Marke',
    short: 'Veröffentlichte Produkte der Marke; „Alle ansehen“ öffnet die Markenseite.',
  },
  'pages.grid_limit': { title: 'Anzahl der Produkte', short: 'Wie viele Produkte angezeigt werden (2–24).' },
  'pages.products': {
    title: 'Ausgewählte Produkte',
    short:
      'Suchen Sie Produkte und fügen Sie sie hinzu (max. 24); sie werden in dieser Reihenfolge angezeigt.',
  },
  'pages.tiles': {
    title: 'Kategoriekacheln',
    short: 'Als Kacheln angezeigte Kategorien. Ausgeblendete Kategorien werden nicht angezeigt.',
  },
  'pages.faq': {
    title: 'Frage und Antwort',
    short: 'Jeder FAQ-Eintrag; wird als aufklappbare Liste angezeigt.',
  },
  'page.banners': {
    title: 'Banner',
    short: 'Verwalten Sie Startseiten-Slider, Werbekacheln, Kategoriebanner, Hinweisleiste und Pop-up.',
  },
  'page.banner_editor': {
    title: 'Banner-Editor',
    short: 'Platzierung, Text, Bilder, Zielgruppe und Zeitraum eines Banners.',
  },
  'banners.create': {
    title: 'Neues Banner',
    short: 'Legt ein Banner als Entwurf an; es wird erst nach der Aktivierung angezeigt.',
  },
  'banners.placement': {
    title: 'Platzierung',
    short:
      'Startseiten-Slider, sekundärer Bereich der Startseite, oberer Kategoriebereich, Hinweisleiste oder Pop-up.',
  },
  'banners.name': { title: 'Interner Name', short: 'Wird nur in der Verwaltung angezeigt.' },
  'banners.title': { title: 'Titel', short: 'Großer Text auf dem Banner (max. 120).' },
  'banners.text': { title: 'Text', short: 'Kurzer Text (max. 300). Für die Hinweisleiste erforderlich.' },
  'banners.cta': {
    title: 'Schaltflächentext',
    short: 'Ohne Schaltflächentext ist das gesamte Banner der Link.',
  },
  'banners.href': {
    title: 'Linkadresse',
    short: 'Ein Pfad der Website (z. B. /urunler) oder eine https://-Adresse.',
  },
  'banners.desktop_image': {
    title: 'Desktop-Bild',
    short: 'Erforderlich für Slider, sekundären Bereich und oberen Kategoriebereich.',
  },
  'banners.mobile_image': {
    title: 'Mobiles Bild',
    short: 'Optional; fehlt es, wird das Desktop-Bild verwendet.',
  },
  'banners.image_alt': {
    title: 'Alt-Text',
    short: 'Beschreiben Sie das Bild. Erforderlich, wenn ein Bild vorhanden ist.',
  },
  'banners.audience': { title: 'Zielgruppe', short: 'Alle, Nicht-Händler oder nur freigegebene Händler.' },
  'banners.dates': { title: 'Beginn und Ende', short: 'Leer bedeutet: ab der Aktivierung bis zum Stoppen.' },
  'banners.dismiss_days': {
    title: 'Nach dem Schließen ausblenden',
    short: 'Anzahl der Tage, die das Pop-up ausgeblendet bleibt, nachdem ein Besucher es geschlossen hat.',
  },
  'banners.categories': {
    title: 'Kategorien',
    short: 'Wo das Kategoriebanner erscheint; keine Auswahl bedeutet alle.',
  },
  'banners.save': {
    title: 'Speichern',
    short: 'Speichert das Banner; Änderungen an einem aktiven Banner sind sofort live.',
  },
  'banners.activate': { title: 'Aktivieren', short: 'Zeigt das Banner zwischen Start- und Enddatum an.' },
  'banners.deactivate': { title: 'Stoppen', short: 'Entfernt das Banner sofort aus der Storefront.' },
  'banners.move': {
    title: 'Neu anordnen',
    short: 'Verschiebt das Banner innerhalb seiner Platzierung; der Slider folgt dieser Reihenfolge.',
  },
  'banners.delete': { title: 'Banner löschen', short: 'Löscht ein inaktives Banner dauerhaft.' },
  'pages.post_list': {
    title: 'Blogbeiträge',
    short: 'Zeigt die neuesten veröffentlichten Beiträge der gewählten Kategorie (1–12).',
  },
  'pages.banners': { title: 'Bannerbereich', short: 'Zeigt die aktiven Banner des gewählten Bereichs.' },
  'products.media_alt': {
    title: 'Alternativtext des Bildes',
    short: 'Beschreiben Sie das Bild; bleibt das Feld leer, wird der Produktname verwendet.',
  },
  'products.media_order': {
    title: 'Bildreihenfolge',
    short: 'Das erste Bild ist das Titelbild; die Galerie folgt dieser Reihenfolge.',
  },
  'page.blog': { title: 'Blog', short: 'Verwalten Sie Artikel, Ratgeber und Blog-Kategorien.' },
  'page.post_editor': {
    title: 'Beitragseditor',
    short: 'Titel, Text, Titelbild, Quellen, verwandte Produkte und SEO eines Beitrags.',
  },
  'blog.create': { title: 'Neuer Beitrag', short: 'Erstellt einen Beitragsentwurf und öffnet den Editor.' },
  'blog.filter': { title: 'Filter', short: 'Alle, wartend auf Expertenfreigabe, live oder Entwürfe.' },
  'page.content_calendar': {
    title: 'Inhaltskalender',
    short: 'Geplante und vergangene Veröffentlichungen des Monats: Beiträge, Seiten und Banner, Tag für Tag.',
  },
  'content_calendar.month': {
    title: 'Monatsauswahl',
    short:
      'Wechseln Sie zum vorherigen oder nächsten Monat; „Dieser Monat“ kehrt zu heute zurück. Monatsgrenzen richten sich nach türkischer Zeit.',
  },
  'content_calendar.kind': {
    title: 'Typ',
    short: 'Beschränken Sie den Kalender auf Blogbeiträge, Seiten oder Banner.',
  },
  'page.content_report': {
    title: 'Inhaltsbericht',
    short:
      'Status von Beiträgen, Seiten und Bannern; Veröffentlichungen nach Kategorie und Monat; lange nicht aktualisierte Beiträge; Search-Console-Klicks.',
  },
  'content_report.states': {
    title: 'Statusübersicht',
    short:
      'Live: im Storefront sichtbar. Geplant: terminiert. Entwurf: nie veröffentlicht. Abgelaufen: Enddatum überschritten.',
  },
  'content_report.stale': {
    title: 'Nicht aktualisierte Beiträge',
    short:
      'Live-Beiträge, deren zuletzt veröffentlichte Version älter als 365 Tage ist, die ältesten zuerst. Prüfen und erneut veröffentlichen.',
  },
  'content_report.search_console': {
    title: 'Search-Console-Klicks',
    short:
      'Letzte 28 Tage aus der verknüpften Search-Console-Property: Klicks, Impressionen, CTR und durchschnittliche Position je Beitrag. Nur Storefront-Sprache.',
  },
  'page.forms': {
    title: 'Formulare',
    short: 'Bearbeiten Sie die Felder des Kontaktformulars und die Zusatzfelder des Händlerantrags.',
  },
  'forms.kind': {
    title: 'Formular',
    short:
      'Kontaktformular: Alle Felder sind bearbeitbar. Händlerantrag: Firmen- und Steuerfelder sind fest; nur Zusatzfelder hinzufügen.',
  },
  'forms.enabled': {
    title: 'Formular auf der Kontaktseite anzeigen',
    short:
      'Wenn aktiviert, erscheint das Formular auf der Kontaktseite des Storefronts. Es kann erst aktiviert werden, wenn der Datenschutzhinweis veröffentlicht ist.',
  },
  'forms.notify': {
    title: 'E-Mail-Benachrichtigung bei neuen Nachrichten',
    short:
      'Inhaber und Administratoren erhalten eine E-Mail ohne Nachrichteninhalt, mit einem Link zur Verwaltung; höchstens einmal pro 15 Minuten.',
  },
  'forms.fields': {
    title: 'Formularfelder',
    short:
      'Bezeichnung, Typ, Hilfetext und Pflichtkennzeichen je Feld; Reihenfolge mit den Pfeilen ändern. Das E-Mail-Feld des Kontaktformulars ist Pflicht und kann nicht entfernt werden.',
  },
  'forms.add_field': {
    title: 'Feld hinzufügen',
    short: 'Fügt am Ende ein leeres Kurztextfeld hinzu (max. 20 Felder).',
  },
  'forms.save': { title: 'Formular speichern', short: 'Speichert Felder und Einstellungen; sofort live.' },
  'forms.label': {
    title: 'Bezeichnung',
    short:
      'Feldname, der Besuchern angezeigt wird (max. 80). Bleibt er bei einem vordefinierten Feld leer, wird die Standardbezeichnung verwendet.',
  },
  'forms.type': {
    title: 'Feldtyp',
    short:
      'Eingaben werden nach Typ geprüft: E-Mail, Telefon, Zahl, Datum; eine Auswahlliste akzeptiert nur ihre Optionen.',
  },
  'forms.help_text': { title: 'Hilfetext', short: 'Kurzer Hinweis unter dem Feld (max. 200).' },
  'forms.options': { title: 'Optionen', short: 'Eine Option pro Zeile (2–30, alle unterschiedlich).' },
  'forms.required': {
    title: 'Pflichtfeld',
    short: 'Besucher müssen es ausfüllen; ein Kontrollkästchen muss angehakt werden.',
  },
  'page.contact_messages': {
    title: 'Kontaktnachrichten',
    short: 'Über das Kontaktformular des Storefronts gesendete Nachrichten.',
  },
  'messages.filter': { title: 'Anzeigen', short: 'Neue (unbearbeitete) Nachrichten, bearbeitete oder alle.' },
  'messages.handle': {
    title: 'Als bearbeitet markieren',
    short: 'Hält fest, dass Sie geantwortet haben; rückgängig mit „Als neu markieren“.',
  },
  'messages.delete': {
    title: 'Nachricht löschen',
    short: 'Löscht die Nachricht endgültig (kann nicht rückgängig gemacht werden).',
  },
  'topbar.store': {
    title: 'Aktiver Shop',
    short:
      'Die Verwaltungsseiten wählen standardmäßig diesen Shop; ein Shopwechsel auf einer Seite ändert ihn ebenfalls. Wird in diesem Browser gespeichert.',
  },
  'topbar.jobs': {
    title: 'Hintergrundaufgaben',
    short:
      'Fortschritt langer Aufgaben (Importe, Massenaktionen für Produkte, Website-Crawl, Seitengeschwindigkeit). Laufende und in den letzten 10 Minuten abgeschlossene Aufgaben.',
  },
  'system.observability': {
    title: 'Beobachtbarkeit (OpenTelemetry)',
    short:
      'Anfrage- und Hintergrundauftragsdauern, Fehlerraten und Traces werden an einen Collector gesendet (Grafana Cloud, Honeycomb, New Relic oder eigener OpenTelemetry Collector). Keine personenbezogenen Daten: nur Methode, Routenvorlage, Statuscode und Auftragsname. Aktivieren unter Integrationen → Beobachtbarkeit.',
  },
  'system.csp_reports': {
    title: 'Verstöße gegen die Inhaltssicherheit im Shop',
    short:
      'Blockiert die Sicherheitsrichtlinie des Shops ein nicht erlaubtes Skript, Bild, Frame oder eine Verbindung, meldet der Browser des Besuchers dies. Ein neu hinzugefügtes Tag oder Video, das nicht funktioniert, oder ein Versuch, Code in eine Seite einzuschleusen, erscheint hier. Gespeichert wird nur eine Zusammenfassung (Regel, blockierte Domain, Seitenpfad, Anzahl, Datum); keine personenbezogenen Daten, IP-Adressen oder Meldungen von Browsererweiterungen. Einträge, die 30 Tage nicht gesehen wurden, werden gelöscht.',
  },
  'system.offsite': {
    title: 'Externe Sicherungskopie',
    short:
      'Die nächtliche Sicherung wird verschlüsselt und in einen Objektspeicher (S3 / R2) hochgeladen. Aktivieren unter Integrationen → Objektspeicher.',
  },
  'page.marketing_audience': {
    title: 'Abonnenten und Einwilligungen',
    short: 'Newsletter-Abonnenten, Sperrliste und İYS-Einwilligungsdatensätze.',
  },
  'audience.subscribers': {
    title: 'Newsletter-Abonnenten',
    short: 'Über das Storefront-Formular angemeldet (Double-Opt-in).',
  },
  'audience.status': { title: 'Status', short: 'Angemeldet, Bestätigung ausstehend, abgemeldet oder alle.' },
  'audience.search': {
    title: 'E-Mail suchen',
    short: 'Sucht nach einem Teil der Adresse (erfordert die Berechtigung für personenbezogene Daten).',
  },
  'audience.subscriber_actions': {
    title: 'Abonnement beenden / Löschen',
    short:
      'Beenden setzt die Adresse auf die Sperrliste und meldet einen Widerspruch an İYS. Löschen ist für Datenschutzanfragen gedacht.',
  },
  'audience.suppressions': {
    title: 'Sperrliste',
    short: 'An diese Adressen werden keine Marketingnachrichten gesendet.',
  },
  'audience.channel': { title: 'Kanal', short: 'E-Mail, SMS oder WhatsApp.' },
  'audience.address': { title: 'Adresse', short: 'E-Mail-Adresse oder Telefonnummer.' },
  'audience.note': { title: 'Notiz', short: 'Warum sie gesperrt wurde.' },
  'audience.suppress': {
    title: 'Sperren',
    short: 'Setzt die Adresse auf die Liste und meldet einen Widerspruch an İYS.',
  },
  'audience.channel_filter': { title: 'Kanal', short: 'Filtert die Liste nach Kanal.' },
  'audience.unsuppress': {
    title: 'Entfernen',
    short: 'Hebt die Sperre auf; die Einwilligung muss erneut erteilt werden.',
  },
  'audience.iys': {
    title: 'İYS-Datensätze',
    short: 'Einwilligungen und Widersprüche müssen innerhalb von drei Werktagen an İYS übermittelt werden.',
  },
  'audience.iys_filter': {
    title: 'Anzeigen',
    short: 'Zu meldende, gesendete, manuell gemeldete oder alle Datensätze.',
  },
  'audience.iys_download': {
    title: 'Datei herunterladen (CSV)',
    short: 'Zu meldende Datensätze mit İYS-Feldnamen.',
  },
  'audience.iys_mark': {
    title: 'Als gemeldet markieren',
    short: 'Markiert Datensätze, die Sie im İYS-Panel hochgeladen haben.',
  },
  'page.segments': { title: 'Segmente', short: 'RFM-Gruppen und regelbasierte Kundensegmente.' },
  'page.segment_editor': { title: 'Segment', short: 'Name, Regeln, Vorschau und Mitglieder eines Segments.' },
  'segments.rfm': {
    title: 'RFM-Gruppen',
    short: 'Aktualität, Häufigkeit und Ausgaben werden mit 1–5 bewertet; daraus ergeben sich die Gruppen.',
  },
  'segments.recompute': {
    title: 'Jetzt neu berechnen',
    short: 'Aktualisiert RFM-Werte und Segmentgrößen (läuft jede Nacht).',
  },
  'segments.save_rfm': {
    title: 'Als Segment speichern',
    short: 'Speichert diese RFM-Gruppe als Segment für Kampagnen.',
  },
  'segments.list': {
    title: 'Segmente',
    short: 'Die Zahlen stammen aus der letzten Zählung (beim Speichern, bei Neuberechnung, nächtlich).',
  },
  'segments.name': { title: 'Name', short: 'Wird bei der Zielgruppenauswahl angezeigt; eindeutig.' },
  'segments.description': { title: 'Beschreibung', short: 'Wofür das Segment dient.' },
  'segments.match': { title: 'Übereinstimmung', short: 'Alle Bedingungen oder eine beliebige davon.' },
  'segments.conditions': {
    title: 'Bedingungen',
    short: 'Bis zu 10. Ausgaben verstehen sich abzüglich Erstattungen und Stornierungen.',
  },
  'segments.preview': {
    title: 'Vorschau',
    short: 'Zeigt die Trefferzahl und die ersten 10 Kunden, ohne zu speichern.',
  },
  'segments.save': { title: 'Speichern', short: 'Speichert das Segment und aktualisiert seine Anzahl.' },
  'segments.delete': { title: 'Löschen', short: 'Löscht das Segment; Kunden sind nicht betroffen.' },
  'segments.members': {
    title: 'Kunden im Segment',
    short:
      'Aktuell passende Kunden; E-Mail-Adressen werden ohne Berechtigung für personenbezogene Daten maskiert.',
  },
  'customers.tags': {
    title: 'Tags',
    short: 'Labels für Filter und Segmentregeln (max. 30); in KVKK-Datenkopien enthalten.',
  },
  'customers.save_tags': { title: 'Tags speichern', short: 'Speichert die Tags.' },
  'customers.tag_filter': { title: 'Tag', short: 'Filtert die Liste nach Tag.' },
  'customers.summary': {
    title: 'Kundenübersicht',
    short:
      'Live aus den Bestellungen: Ausgaben gesamt und der letzten 12 Monate, Bestellungen, durchschnittlicher Warenkorb, Bestellintervall, RFM.',
  },
  'customers.notes': { title: 'Interne Notizen', short: 'Notizen des Teams; in KVKK-Datenkopien enthalten.' },
  'customers.save_notes': {
    title: 'Notiz speichern',
    short: 'Speichert die Notiz (im Prüfprotokoll erfasst).',
  },
  'customers.timeline': {
    title: 'Aktivität',
    short:
      'Bestellungen, Rücksendungen, Bewertungen, Fragen, Einwilligungen, Nachrichten und KVKK-Anfragen in einer Liste.',
  },
  'email_routing.transactional': {
    title: 'Transaktions-E-Mails',
    short: 'Bestellungen, Versand, Passwörter und Einladungen.',
  },
  'email_routing.marketing': {
    title: 'Marketing-E-Mails',
    short: 'Newsletter und Kampagnen; ein separater Versandweg schützt die Reputation.',
  },
  'email_routing.save': { title: 'Versandauswahl speichern', short: 'Gilt sofort.' },
  'email_routing.webhooks': {
    title: 'Benachrichtigungs-URLs',
    short: 'Hierhin sendet der Anbieter Bounce-, Beschwerde- und Abmeldeereignisse.',
  },
  'email_routing.rotate': { title: 'URLs neu erzeugen', short: 'Alte URLs funktionieren sofort nicht mehr.' },
  'page.email_templates': {
    title: 'E-Mail-Vorlagen',
    short: 'Blockbasierte Vorlagen für Newsletter und Kampagnen.',
  },
  'page.email_template_editor': {
    title: 'E-Mail-Vorlage',
    short: 'Blöcke hinzufügen und anordnen; Vorschau ansehen und einen Test an sich selbst senden.',
  },
  'email_templates.create': {
    title: 'Neue Vorlage',
    short: 'Öffnet eine leere Vorlage im gewählten Shop und in der gewählten Sprache.',
  },
  'email_templates.save': {
    title: 'Speichern',
    short: 'Speichert die Vorlage; gesundheitsbezogene Aussagen werden erneut geprüft.',
  },
  'email_templates.name': { title: 'Vorlagenname', short: 'Nur in der Verwaltung sichtbar.' },
  'email_templates.subject': { title: 'Betreff', short: 'Betreff im Posteingang, bis zu 200 Zeichen.' },
  'email_templates.preheader': {
    title: 'Preheader',
    short: 'Kurzer Text, der im Posteingang neben dem Betreff angezeigt wird.',
  },
  'email_templates.merge_fields': {
    title: 'Personalisierungsfelder',
    short: 'Der Text nach dem senkrechten Strich wird verwendet, wenn kein Wert vorhanden ist.',
  },
  'email_templates.blocks': {
    title: 'Inhaltsblöcke',
    short: 'Bis zu 40 Blöcke; unvollständige Blöcke verhindern das Speichern.',
  },
  'email_templates.move': { title: 'Neu anordnen', short: 'Am Griff ziehen oder die Pfeiltasten verwenden.' },
  'email_templates.add_block': {
    title: 'Block hinzufügen',
    short: 'Fügt den Block am Ende der Vorlage hinzu.',
  },
  'email_templates.delete': {
    title: 'Löschen',
    short: 'Löscht die Vorlage; andere Sprachen bleiben erhalten.',
  },
  'email_templates.align': { title: 'Ausrichtung', short: 'Links oder zentriert.' },
  'email_templates.heading': { title: 'Überschrift', short: 'Bis zu 200 Zeichen.' },
  'email_templates.level': { title: 'Größe', short: 'Große Überschrift oder Zwischenüberschrift.' },
  'email_templates.text': { title: 'Text', short: 'Reiner Text; Leerzeile für Absätze, **fett**.' },
  'email_templates.image': { title: 'Bild', short: 'PNG, JPEG, WebP oder GIF; 1200 px Breite genügen.' },
  'email_templates.image_alt': {
    title: 'Bildbeschreibung',
    short: 'Wird angezeigt, wenn Bilder deaktiviert sind; Pflicht für verlinkte Bilder.',
  },
  'email_templates.link': {
    title: 'Link',
    short: 'Ein Shop-Pfad (/campaign) oder eine vollständige http(s)-Adresse.',
  },
  'email_templates.width': { title: 'Breite', short: 'Volle Breite oder zentrierte halbe Breite.' },
  'email_templates.button_label': {
    title: 'Schaltflächentext',
    short: 'Kurzer Text, der sagt, was passiert.',
  },
  'email_templates.products_source': {
    title: 'Produktquelle',
    short: 'Neueste, eine Kollektion, eine Kategorie oder manuell ausgewählt.',
  },
  'email_templates.columns': { title: 'Spalten', short: 'Karten nebeneinander auf dem Desktop.' },
  'email_templates.limit': { title: 'Anzahl der Produkte', short: '1–12 Produkte aus der Quelle.' },
  'email_templates.collection': {
    title: 'Kollektion',
    short: 'Zur Kollektion passende Produkte, nach Name.',
  },
  'email_templates.category': { title: 'Kategorie', short: 'Produkte der Kategorie, nach Name.' },
  'email_templates.products_pick': {
    title: 'Produkte',
    short: 'Suche nach Name, SKU oder Barcode; die Reihenfolge bleibt erhalten.',
  },
  'email_templates.show_price': {
    title: 'Preis anzeigen',
    short: 'Niedrigster Preis inkl. Steuern zum Versandzeitpunkt.',
  },
  'email_templates.spacer': { title: 'Abstandshalter', short: 'Fügt vertikalen Abstand hinzu.' },
  'email_templates.cart': {
    title: 'Warenkorb-Block',
    short: 'Wird in der Automation für abgebrochene Warenkörbe befüllt.',
  },
  'email_templates.order': { title: 'Bestellblock', short: 'Wird in Automationen nach dem Kauf befüllt.' },
  'email_templates.quote': {
    title: 'Angebotsblock',
    short: 'Wird in der Automation zum Nachfassen bei Händlerangeboten befüllt.',
  },
  'email_templates.preview': {
    title: 'Vorschau',
    short: 'Aktualisiert sich während der Eingabe; persönliche Felder sind Platzhalter.',
  },
  'email_templates.device': {
    title: 'Bildschirm und Format',
    short: 'Desktop, Smartphone und die Nur-Text-Version.',
  },
  'email_templates.test_send': {
    title: 'Test senden',
    short: 'Sendet die gespeicherte Version nur an Benutzer der Verwaltung.',
  },
  'email_templates.test_to': {
    title: 'Empfänger',
    short: 'E-Mail-Adressen von Verwaltungsbenutzern, bis zu 5.',
  },
  'page.campaigns': {
    title: 'E-Mail-Kampagnen',
    short: 'Senden Sie eine Vorlage an eine Zielgruppe; verfolgen Sie Fortschritt und Ergebnisse.',
  },
  'page.campaign_editor': {
    title: 'E-Mail-Kampagne',
    short: 'Vorlage, Betreff, Zielgruppe, A/B-Test, Geschwindigkeit; Versand und Bericht.',
  },
  'campaigns.create': {
    title: 'Neue Kampagne',
    short: 'Öffnet einen Kampagnenentwurf für den gewählten Shop.',
  },
  'campaigns.save': { title: 'Speichern', short: 'Speichert den Entwurf; es wird keine E-Mail gesendet.' },
  'campaigns.name': {
    title: 'Kampagnenname',
    short: 'Wird in Berichten und im Kampagnencode von Links verwendet.',
  },
  'campaigns.template': {
    title: 'E-Mail-Vorlage',
    short: 'Hauptvorlage; andere Sprachversionen gehen an die entsprechenden Empfänger.',
  },
  'campaigns.subject': {
    title: 'Betreff',
    short: 'Ersetzt den Vorlagenbetreff für Empfänger in der Hauptsprache.',
  },
  'campaigns.preheader': { title: 'Preheader', short: 'Ersetzt den Preheader der Vorlage.' },
  'campaigns.audience': { title: 'Zielgruppe', short: 'Segmente werden beim Versandstart ausgewertet.' },
  'campaigns.subscribers': { title: 'Newsletter-Abonnenten', short: 'Bestätigte Abonnenten dieses Shops.' },
  'campaigns.estimate': {
    title: 'Empfänger zählen',
    short: 'Zeigt, wie viele sie erhalten können und warum andere ausgeschlossen sind.',
  },
  'campaigns.ab': {
    title: 'A/B-Test',
    short: 'Der Betreff mit der höheren Klickrate geht an die übrigen Empfänger.',
  },
  'campaigns.subject_b': {
    title: 'Betreff B',
    short: 'Betreff A ist der Betreff der Kampagne (oder der Vorlage).',
  },
  'campaigns.ab_percent': { title: 'Testgruppe', short: 'Anteil der Empfänger im Test (10–50 %).' },
  'campaigns.ab_wait': { title: 'Wartezeit', short: 'Zeit bis zur Auswahl des Gewinners.' },
  'campaigns.rate': { title: 'Versandgeschwindigkeit', short: 'E-Mails pro Minute (10–3000).' },
  'campaigns.delete': { title: 'Löschen', short: 'Nur Entwürfe können gelöscht werden.' },
  'campaigns.send': {
    title: 'Senden',
    short: 'Zeigt die Empfängerzahl; sendet nach Bestätigung sofort oder später.',
  },
  'campaigns.scheduled_at': { title: 'Versandzeitpunkt', short: 'Bis zu 90 Tage im Voraus.' },
  'campaigns.status': { title: 'Versandstatus', short: 'Wird alle 5 Sekunden aktualisiert.' },
  'campaigns.resume': {
    title: 'Fortsetzen',
    short: 'Setzt den Versand an Empfänger fort, die noch nichts erhalten haben.',
  },
  'campaigns.cancel': {
    title: 'Versand stoppen',
    short: 'Empfänger, an die noch nicht gesendet wurde, erhalten die E-Mail nicht.',
  },
  'campaigns.report': {
    title: 'Bericht',
    short: 'Klicks werden über eine Tracking-Adresse gezählt; Öffnungen werden nicht gemessen.',
  },
  'page.automations': {
    title: 'Marketing-Automationen',
    short: 'Durch Ereignisse ausgelöste E-Mail-Abläufe und Versandregeln.',
  },
  'page.automation_editor': {
    title: 'Automationsablauf',
    short: 'Ein-/Ausschalten; Auslöseverzögerung, Schritte, Vorlagen; Bericht.',
  },
  'automations.settings': {
    title: 'Versandregeln',
    short: 'Frequenzbegrenzung und Ruhezeiten für alle Abläufe.',
  },
  'automations.frequency_cap': {
    title: 'Frequenzbegrenzung',
    short:
      'Überspringt die E-Mail, wenn die Person in diesem Zeitraum bereits eine andere Marketing-E-Mail erhalten hat.',
  },
  'automations.quiet_hours': {
    title: 'Ruhezeiten',
    short: 'In diesem Zeitfenster keine E-Mails (türkische Zeit).',
  },
  'automations.settings_save': { title: 'Regeln speichern', short: 'Gilt ab dem nächsten Versand.' },
  'automations.save': {
    title: 'Speichern',
    short: 'Speichert den Ablauf; das Einschalten erfordert eine Bestätigung.',
  },
  'automations.enabled': {
    title: 'Ablauf aktiv',
    short: 'Erfordert eine Vorlage für jeden E-Mail-Schritt und einen eingerichteten E-Mail-Versand.',
  },
  'automations.trigger': {
    title: 'Auslöser',
    short: 'Das Ereignis, durch das eine Person in den Ablauf aufgenommen wird.',
  },
  'automations.store': { title: 'Shop', short: 'Marke, Links und Vorlagen stammen aus diesem Shop.' },
  'automations.after_hours': {
    title: 'Wie lange danach',
    short: 'Verzögerung nach der Warenkorbänderung oder Bestellung (1–72 Std.).',
  },
  'automations.days': {
    title: 'Tage',
    short: 'Tage nach der Zustellung, der letzten Bestellung oder dem Senden des Angebots (1–365).',
  },
  'automations.steps': {
    title: 'Schritte',
    short: 'Bis zu 10 Schritte; mindestens eine E-Mail; darf nicht mit einer Wartezeit enden.',
  },
  'automations.step_tools': {
    title: 'Schritt verschieben oder löschen',
    short: 'Wird nach dem Speichern wirksam.',
  },
  'automations.add_step': {
    title: 'Schritt hinzufügen',
    short: 'Fügt am Ende einen Warte-, Bedingungs- oder E-Mail-Schritt hinzu.',
  },
  'automations.reset': {
    title: 'Auf Standard zurücksetzen',
    short: 'Stellt die voreingestellten Schritte wieder her (noch nicht gespeichert).',
  },
  'automations.report': {
    title: 'Bericht',
    short: 'Bestellungen werden anhand des Kampagnencodes des Ablaufs in Links gezählt.',
  },
  'page.product_alerts': {
    title: 'Bestands- und Preisbenachrichtigungen',
    short: '„Benachrichtigen“-Anfragen und Preissenkungs-E-Mails für Favoriten.',
  },
  'product_alerts.settings': {
    title: 'Einstellungen',
    short: 'Beide standardmäßig aus; E-Mail-Versand erforderlich.',
  },
  'product_alerts.stock_toggle': {
    title: '„Benachrichtigen“ im Storefront',
    short: 'Zeigt das E-Mail-Formular bei nicht vorrätigen Artikeln.',
  },
  'product_alerts.price_drop': {
    title: 'Preissenkungsbenachrichtigung',
    short: 'Tägliche E-Mail, wenn der Preis eines Favoriten ausreichend sinkt.',
  },
  'product_alerts.store': { title: 'Shop', short: 'Preise, Karten und Links stammen aus diesem Shop.' },
  'product_alerts.min_percent': {
    title: 'Mindestsenkung',
    short: 'Im Vergleich zum niedrigsten bisher gesehenen Preis.',
  },
  'product_alerts.save': {
    title: 'Speichern',
    short: 'Das Aktivieren einer Benachrichtigung erfordert eine Bestätigung.',
  },
  'product_alerts.requests': {
    title: 'Anfragen',
    short: 'Offene Anfragen zeigen das Interesse an ausverkauften Artikeln.',
  },
  'product_alerts.view': { title: 'Anfragen', short: 'Listet die Anfragen der Variante der Reihe nach auf.' },
  'product_alerts.cancel': {
    title: 'Abbrechen',
    short: 'Schließt die Anfrage; es wird keine E-Mail gesendet.',
  },
  'page.message_campaigns': {
    title: 'SMS und WhatsApp',
    short: 'SMS- und WhatsApp-Kampagnen, Einrichtungsstatus und Versandeinstellungen.',
  },
  'page.message_campaign_editor': {
    title: 'SMS-/WhatsApp-Kampagne',
    short: 'Kanal, Vorlage, Zielgruppe, geschätzte Kosten; Versand, Fortschritt und Bericht.',
  },
  'page.sms_templates': {
    title: 'SMS-Vorlagen',
    short: 'In Kampagnen verwendete SMS-Texte und ihre Expertenfreigabe.',
  },
  'page.sms_template_editor': {
    title: 'SMS-Vorlage',
    short: 'Text, persönliche Felder, Live-Längenzählung und Expertenfreigabe.',
  },
  'messaging.create': {
    title: 'Neue SMS-/WhatsApp-Kampagne',
    short: 'Erstellt einen Entwurf; beim Speichern wird nichts gesendet.',
  },
  'messaging.setup': {
    title: 'Einrichtungsstatus',
    short: 'Ein Kanal mit fehlenden Angaben zeigt „Einrichtung erforderlich“.',
  },
  'messaging.settings': {
    title: 'Einstellungen',
    short: 'Gelten für alle SMS-/WhatsApp-Kampagnen; erfordert Zwei-Faktor-Authentifizierung.',
  },
  'messaging.sms_provider': {
    title: 'SMS-Anbieter',
    short: 'Verbinden und testen Sie ihn zuerst unter Integrationen.',
  },
  'messaging.opt_out': {
    title: 'Abmeldetext',
    short: 'Pflicht bei kommerziellen SMS: wie man sich über denselben Kanal abmeldet.',
  },
  'messaging.unit_cost_sms': {
    title: 'Kosten pro SMS-Teil',
    short: 'Wird nur für die Kostenschätzung verwendet.',
  },
  'messaging.unit_cost_whatsapp': {
    title: 'Kosten pro WhatsApp-Nachricht',
    short: 'Wird nur für die Kostenschätzung verwendet.',
  },
  'messaging.frequency_cap': {
    title: 'Frequenzbegrenzung',
    short: 'Innerhalb dieses Zeitraums keine zweite Kampagnennachricht an dieselbe Nummer.',
  },
  'messaging.staging_phones': {
    title: 'Testnummern für Staging',
    short: 'Im Staging erhalten nur diese Nummern Nachrichten.',
  },
  'messaging.settings_save': {
    title: 'Einstellungen speichern',
    short: 'Gilt auch für geplante und laufende Versände.',
  },
  'messaging.save': { title: 'Speichern', short: 'Speichert den Entwurf; sendet nichts.' },
  'messaging.channel': {
    title: 'Kanal',
    short: 'SMS verwendet Ihre Vorlage, WhatsApp eine von Meta genehmigte.',
  },
  'messaging.name': {
    title: 'Kampagnenname',
    short: 'Wird in der Verwaltung angezeigt, nicht den Empfängern.',
  },
  'messaging.sms_template': {
    title: 'SMS-Vorlage',
    short: 'Vorlagen mit gesundheitsbezogenen Aussagen benötigen eine Expertenfreigabe.',
  },
  'messaging.whatsapp_template': {
    title: 'WhatsApp-Vorlage',
    short: 'Genehmigte Vorlagen von Meta; beim Versand erneut geprüft.',
  },
  'messaging.whatsapp_params': {
    title: 'Vorlagenvariablen',
    short: 'Werte für {{1}}, {{2}}… in der Vorlage.',
  },
  'messaging.segments': {
    title: 'Zielgruppe',
    short: 'Segmente werden beim Versand neu berechnet; eine Nachricht pro Nummer.',
  },
  'messaging.estimate': {
    title: 'Empfänger und Kosten berechnen',
    short: 'Empfänger, Ausschlüsse, SMS-Teile und Kosten.',
  },
  'messaging.delete': {
    title: 'Löschen',
    short: 'Löscht einen Entwurf oder eine gestoppte Kampagne, die nie gestartet wurde.',
  },
  'messaging.send': {
    title: 'Senden',
    short: 'Zeigt Empfänger und Kosten, sendet dann sofort oder plant den Versand.',
  },
  'messaging.scheduled_at': { title: 'Versandzeitpunkt', short: 'Bis zu 90 Tage im Voraus; türkische Zeit.' },
  'messaging.status': {
    title: 'Versandstatus',
    short: 'Wird alle 5 Sekunden aktualisiert; erklärt Unterbrechungen.',
  },
  'messaging.resume': {
    title: 'Fortsetzen',
    short: 'Setzt mit den Empfängern fort, an die noch nicht gesendet wurde.',
  },
  'messaging.cancel': {
    title: 'Stoppen',
    short: 'Storniert den Versand an Empfänger, an die noch nicht gesendet wurde.',
  },
  'messaging.report': {
    title: 'Bericht',
    short: 'Gesendet, zugestellt, fehlgeschlagen und übersprungen; Gründe und Kosten.',
  },
  'sms_templates.create': { title: 'Neue SMS-Vorlage', short: 'Öffnet eine leere Vorlage.' },
  'sms_templates.save': {
    title: 'Speichern',
    short: 'Eine Textänderung erfordert erneut eine Expertenfreigabe.',
  },
  'sms_templates.name': { title: 'Vorlagenname', short: 'Wird bei der Vorlagenauswahl angezeigt.' },
  'sms_templates.body': {
    title: 'Text',
    short: 'Fassen Sie sich kurz; die MERSİS-Nummer und der Abmeldetext werden hinzugefügt.',
  },
  'sms_templates.fields': { title: 'Persönliche Felder', short: 'Fügt ein Feld an der Cursorposition ein.' },
  'sms_templates.preview': { title: 'Vorschau', short: 'Zu sendender Text, Zeichensatz und Teile.' },
  'sms_templates.delete': {
    title: 'Löschen',
    short: 'Löscht die Vorlage, sofern keine ausstehende Kampagne sie verwendet.',
  },
  'page.push': {
    title: 'Push-Benachrichtigungen',
    short: 'Browser-Benachrichtigungskampagnen, Storefront-Schaltfläche und Einrichtung.',
  },
  'page.push_editor': {
    title: 'Benachrichtigungskampagne',
    short: 'Titel, Text, Seite, Zielgruppe; Vorschau, Versand und Bericht.',
  },
  'push.create': { title: 'Neue Benachrichtigungskampagne', short: 'Erstellt einen Entwurf; sendet nichts.' },
  'push.setup': {
    title: 'Einrichtung',
    short: 'Fehlende Elemente zeigen „Einrichtung erforderlich“ oder „Fehlt“.',
  },
  'push.create_key': {
    title: 'Schlüssel erstellen',
    short: 'Erzeugt ein Schlüsselpaar auf dem Server; der private Schlüssel bleibt verschlüsselt.',
  },
  'push.replace_key': {
    title: 'Schlüssel ersetzen',
    short: 'Alle Abonnements enden; Besucher müssen Benachrichtigungen erneut aktivieren.',
  },
  'push.contact': { title: 'Kontaktadresse', short: 'mailto: oder https:; von Push-Diensten verlangt.' },
  'push.enabled': {
    title: 'Storefront-Schaltfläche',
    short: 'Die Berechtigung wird erst abgefragt, wenn der Besucher darauf klickt. Standardmäßig aus.',
  },
  'push.settings_save': {
    title: 'Speichern',
    short: 'Zum Aktivieren sind ein Schlüssel und eine Kontaktadresse nötig.',
  },
  'push.save': { title: 'Speichern', short: 'Speichert den Entwurf; sendet nichts.' },
  'push.name': { title: 'Kampagnenname', short: 'Wird in der Verwaltung angezeigt, nicht den Empfängern.' },
  'push.title': { title: 'Titel', short: 'Fassen Sie sich kurz; Browser kürzen eventuell nach ~50 Zeichen.' },
  'push.body': {
    title: 'Text',
    short: 'Max. 180 Zeichen; gesundheitsbezogene Aussagen benötigen eine Expertenfreigabe.',
  },
  'push.url': {
    title: 'Zu öffnende Seite',
    short: 'Ein Shop-Pfad, der mit / beginnt; UTM wird hinzugefügt.',
  },
  'push.audience': {
    title: 'Zielgruppe',
    short: 'Alle Abonnenten oder angemeldete Mitglieder in Segmenten.',
  },
  'push.estimate': { title: 'Browser zählen', short: 'Wie viele Abonnements sie erhalten; sendet nichts.' },
  'push.preview': { title: 'Vorschau', short: 'Ungefähres Erscheinungsbild.' },
  'push.delete': {
    title: 'Löschen',
    short: 'Löscht einen Entwurf oder eine gestoppte Kampagne, die nie gestartet wurde.',
  },
  'push.send': { title: 'Senden', short: 'Zeigt die Anzahl, sendet dann sofort oder plant den Versand.' },
  'push.scheduled_at': { title: 'Versandzeitpunkt', short: 'Bis zu 90 Tage im Voraus.' },
  'push.status': {
    title: 'Versandstatus',
    short: 'Wird alle 5 Sekunden aktualisiert; erklärt Unterbrechungen.',
  },
  'push.resume': {
    title: 'Fortsetzen',
    short: 'Setzt mit den Browsern fort, an die noch nicht gesendet wurde.',
  },
  'push.cancel': {
    title: 'Stoppen',
    short: 'Storniert den Versand an Browser, an die noch nicht gesendet wurde.',
  },
  'push.report': {
    title: 'Bericht',
    short: 'Angenommene Zustellungen, abgelaufene Abonnements, Gründe und Bestellungen.',
  },
  'notifications.section': {
    title: 'Mobile Benachrichtigungen',
    short: 'Ihre Geräte und bei welchen Ereignissen Sie benachrichtigt werden.',
  },
  'notifications.events': {
    title: 'Ereignisse, bei denen ich benachrichtigt werde',
    short: 'Nur Ereignisse, für die Sie eine Berechtigung haben.',
  },
  'notifications.quiet_hours': { title: 'Ruhezeiten', short: 'In diesem Zeitraum keine Benachrichtigungen.' },
  'notifications.save': { title: 'Einstellungen speichern', short: 'Betrifft nur Ihre Benachrichtigungen.' },
  'notifications.test': {
    title: 'Testbenachrichtigung',
    short: 'Sendet einen Test an Ihre registrierten Geräte.',
  },
  'notifications.device_remove': {
    title: 'Gerät entfernen',
    short: 'Dieses Gerät erhält keine Benachrichtigungen mehr. Betrifft nur Ihre eigenen Geräte.',
  },
  'page.promotions': {
    title: 'Automatische Aktionen',
    short:
      'Aktionen ohne Gutscheincode: Produktrabatt, Warenkorbstaffeln, Kaufe X, zahle Y, kostenloser Versand.',
  },
  'page.promotion_editor': {
    title: 'Automatische Aktion',
    short: 'Typ, Regel, Zielprodukte, Zielgruppe, Priorität, Kombinierbarkeit und Zeitraum; Text für Kunden.',
  },
  'promotions.create': {
    title: 'Neue Aktion',
    short: 'Öffnet einen Entwurf; Entwürfe sind für Kunden nicht sichtbar.',
  },
  'promotions.save': { title: 'Speichern', short: 'Speichert einen Entwurf oder eine pausierte Aktion.' },
  'promotions.activate': {
    title: 'Live schalten',
    short: 'Startet die Aktion; erfordert Zwei-Faktor-Authentifizierung.',
  },
  'promotions.pause': { title: 'Pausieren', short: 'Wird sofort nicht mehr auf Warenkörbe angewendet.' },
  'promotions.delete': {
    title: 'Entwurf löschen',
    short: 'Nur ein Entwurf, der nie live war, kann gelöscht werden.',
  },
  'promotions.name': { title: 'Aktionsname', short: 'Nur in der Verwaltung und in Berichten sichtbar.' },
  'promotions.label': {
    title: 'Für Kunden sichtbarer Name',
    short: 'Warenkorb, Kasse und Produktseite; max. 80.',
  },
  'promotions.kind': {
    title: 'Aktionstyp',
    short: 'Produktrabatt, Warenkorbstaffeln, Kaufe X, zahle Y oder kostenloser Versand.',
  },
  'promotions.percent': { title: 'Rabatt in Prozent', short: 'Z. B. 20 oder 7,5; wird abgerundet.' },
  'promotions.buy': { title: 'Kaufen', short: 'Artikel pro Gruppe (2–20).' },
  'promotions.pay': { title: 'Bezahlen', short: 'Bezahlte Artikel pro Gruppe; die günstigsten sind gratis.' },
  'promotions.min_subtotal': { title: 'Mindestwarenkorbwert', short: 'Nach Produktrabatten; leer = immer.' },
  'promotions.basis': { title: 'Schwellenart', short: 'Warenkorbwert oder Anzahl berechtigter Artikel.' },
  'promotions.tier_type': { title: 'Rabattart', short: 'Prozent oder fester Betrag.' },
  'promotions.tiers': { title: 'Staffeln', short: 'Bis zu 5; es gilt die höchste erreichte Staffel.' },
  'promotions.add_tier': {
    title: 'Staffel hinzufügen',
    short: 'Fügt eine Zeile mit Schwelle und Rabatt hinzu.',
  },
  'promotions.remove_tier': { title: 'Staffel entfernen', short: 'Entfernt diese Staffel aus dem Entwurf.' },
  'promotions.currency': { title: 'Währung', short: 'Gilt nur für Warenkörbe in dieser Währung.' },
  'promotions.targets': { title: 'Zielprodukte', short: 'Keine Auswahl = alle Produkte.' },
  'promotions.categories': { title: 'Kategorien', short: 'Unterkategorien sind eingeschlossen.' },
  'promotions.brands': { title: 'Marken', short: 'Produkte der ausgewählten Marken.' },
  'promotions.collections': { title: 'Kollektionen', short: 'Aktuelle Produkte der Kollektion.' },
  'promotions.products': { title: 'Produkte', short: 'Suche nach Name, SKU oder Barcode.' },
  'promotions.audience': {
    title: 'Zielgruppe',
    short: 'Alle, Endkunden oder Händler; optional Gruppen und Segmente.',
  },
  'page.dealer_reports': {
    title: 'Händlerberichte',
    short:
      'Händlerumsätze, Produkte, Kategorien, Forderungsalter und Zahlungseingänge nach Händler, Außendienstmitarbeiter oder Region.',
  },
  'dealer_reports.report': {
    title: 'Bericht',
    short: 'Umsätze, Produkte, Kategorien, Forderungsalter oder Zahlungseingänge.',
  },
  'dealer_reports.period': {
    title: 'Zeitraum',
    short: 'Von- und Bis-Tag einschließlich; höchstens ein Jahr.',
  },
  'dealer_reports.group_by': {
    title: 'Gruppieren nach',
    short: 'Summen nach Händler, Außendienstmitarbeiter oder Region.',
  },
  'dealer_reports.rep': {
    title: 'Außendienstmitarbeiter',
    short: 'Nur Bestellungen oder Händler dieses Außendienstmitarbeiters.',
  },
  'dealer_reports.region': { title: 'Region', short: 'Nur Händler in dieser Region.' },
  'page.international_report': {
    title: 'Umsatz nach Land',
    short:
      'Bestellungen, Export, Brutto, MwSt., Versand, Erstattungen, Netto und TRY-Gegenwert nach Lieferland und Währung.',
  },
  'international_report.period': {
    title: 'Zeitraum',
    short: 'Bestelltag (türkische Zeit), beide Tage inklusive; höchstens ein Jahr.',
  },
  'international_report.store': {
    title: 'Shop',
    short: 'Leer lassen für alle Shops, auf die Sie Zugriff haben.',
  },
  'international_report.csv': {
    title: 'CSV herunterladen',
    short: 'Lädt den Bericht mit denselben Filtern herunter.',
  },
  'international_report.try_total': {
    title: 'TRY-Gegenwert',
    short: 'Nettobeträge, umgerechnet mit dem auf jeder Bestellung gespeicherten TCMB-Kurs.',
  },
  'dealer_reports.csv': {
    title: 'CSV herunterladen',
    short: 'Lädt den Bericht mit denselben Filtern herunter.',
  },
  'pricing.list_market': { title: 'Markt', short: 'Falls gesetzt, gilt die Liste nur in diesem Markt.' },
  'products.compliance': {
    title: 'Länderzulassung',
    short: 'Verkaufsstatus, Registrierungs-/Genehmigungsnummer und Verifizierung je Land.',
  },
  'products.compliance_status': {
    title: 'Verkaufsstatus',
    short: 'Wenn nicht verkäuflich, ist eine Begründung erforderlich.',
  },
  'products.compliance_reason': {
    title: 'Begründung',
    short: 'Warum es nicht verkauft werden kann (intern).',
  },
  'products.compliance_registration': {
    title: 'Registrierungs-/Genehmigungsnummer',
    short: 'Wird nach der Verifizierung auf der Produktseite angezeigt.',
  },
  'products.compliance_verification': {
    title: 'Verifizierung',
    short: 'Ob die Nummer mit der amtlichen Quelle abgeglichen wurde.',
  },
  'page.fx': {
    title: 'Wechselkurse',
    short: 'Tägliche Indikativkurse der TCMB, Abrufstatus und kursbasierte Preislisten.',
  },
  'fx.refresh': {
    title: 'Kurse jetzt aktualisieren',
    short: 'Ruft jetzt das neueste TCMB-Bulletin ab (höchstens einmal pro Minute).',
  },
  'fx.rates': {
    title: 'Kurse',
    short: 'Devisen- und Sortenankaufs- und -verkaufskurse des neuesten Bulletins.',
  },
  'fx.derived': {
    title: 'Kursbasierte Listen',
    short: 'Listen, deren Preise anhand des Kurses aus einer Basisliste berechnet werden.',
  },
  'fx.recompute': { title: 'Jetzt neu berechnen', short: 'Berechnet die Liste mit dem neuesten Kurs neu.' },
  'pricing.fx': {
    title: 'Aus Kurs ableiten',
    short: 'Berechnet die Listenpreise aus einer Basisliste in einer anderen Währung mit dem TCMB-Kurs.',
  },
  'pricing.fx_base': { title: 'Basisliste', short: 'Eine manuell bepreiste Liste in einer anderen Währung.' },
  'pricing.fx_rate_type': { title: 'Kursart', short: 'Devisen- oder Sorten-Ankaufs- bzw. -Verkaufskurs.' },
  'pricing.fx_markup': {
    title: 'Aufschlag',
    short: 'Prozentsatz, der nach der Umrechnung aufgeschlagen wird; ein negativer Wert senkt den Preis.',
  },
  'pricing.fx_ending': { title: 'Preisendung', short: 'Rundet auf eine ganze Zahl, ,90 oder ,99 auf.' },
  'pricing.fx_auto': {
    title: 'Automatisch aktualisieren',
    short: 'Berechnet bei jedem neuen Bulletin und bei Änderung der Basispreise neu.',
  },
  'pricing.fx_clear': {
    title: 'Zu manuellen Preisen wechseln',
    short: 'Entfernt die Kursbindung; aktuelle Preise bleiben erhalten.',
  },
  'orders.fx': {
    title: 'Wechselkurs',
    short: 'TCMB-Devisenankaufskurs zum Bestellzeitpunkt; ändert sich nie.',
  },
  'payments.card_currencies': {
    title: 'Kartenzahlungen in Fremdwährung',
    short: 'Kartenzahlung wird nur in angehakten Währungen angeboten, die der Anbieter unterstützt.',
  },
  'shipping.currency': {
    title: 'Gebührenwährung',
    short: 'Die Methode wird nur bei Bestellungen in dieser Währung angeboten.',
  },
  'promotions.companies': {
    title: 'Händlerfirmen',
    short: 'Aktion nur für Händler: gilt nur für Benutzer der ausgewählten freigegebenen Händlerfirmen.',
  },
  'promotions.groups': { title: 'Kundengruppen', short: 'Gäste zählen als Mitglieder der Endkundengruppen.' },
  'promotions.segments': { title: 'Segmente', short: 'Angemeldete Mitglieder der ausgewählten Segmente.' },
  'promotions.priority': { title: 'Priorität', short: 'Höhere Werte werden zuerst geprüft (−100 bis 100).' },
  'promotions.stacking': {
    title: 'Kombinierbarkeit',
    short: 'Exklusiv oder mit anderen Aktionen kombinierbar.',
  },
  'promotions.allow_coupon': {
    title: 'Mit Gutscheincode',
    short: 'Wenn aus, werden Gutscheincodes abgelehnt, solange die Aktion gilt.',
  },
  'promotions.starts_at': { title: 'Beginn', short: 'Leer = beim Live-Schalten.' },
  'promotions.ends_at': { title: 'Ende', short: 'Leer = kein Ende; wird auf Produktseiten angezeigt.' },
  'promotions.preview': { title: 'Was Kunden sehen', short: 'Name, Bedingung und Enddatum.' },
  'promotions.excluded': {
    title: 'Produkte ohne Rabatt',
    short: 'Preis innerhalb von 10 Tagen vor dem Beginn erhöht.',
  },
  'promotions.usage': { title: 'Nutzung', short: 'Bestellungen, Gesamtrabatt und Bestellsumme.' },
  'page.gift_cards': {
    title: 'Geschenkkarten',
    short:
      'Prepaid-Karten, die an der Kasse per Code eingelöst werden: Erstellung, Guthaben, Status und Ablauf.',
  },
  'page.gift_card_detail': {
    title: 'Geschenkkarte',
    short:
      'Kartenguthaben, Buchungen (Ausgabe, Einlösung bei Bestellung, Erstattung, Stornierung, Korrektur) und Einstellungen.',
  },
  'gift_cards.create': {
    title: 'Geschenkkarte erstellen',
    short:
      'Erstellt eine Karte mit Betrag und optionalem Ablaufdatum. Der Code wird nur einmal angezeigt; Sie geben ihn weiter.',
  },
  'gift_cards.search': {
    title: 'Letzte vier Zeichen',
    short: 'Sucht nach den letzten vier Zeichen; vollständige Codes werden nicht gespeichert.',
  },
  'gift_cards.state_filter': {
    title: 'Status',
    short: 'Nutzbar, deaktiviert, abgelaufen oder ohne Guthaben.',
  },
  'gift_cards.amount': { title: 'Betrag', short: 'Anfangsguthaben der Karte (mindestens 1,00).' },
  'gift_cards.currency': {
    title: 'Währung',
    short: 'Die Karte funktioniert nur in Shops, die in dieser Währung verkaufen.',
  },
  'gift_cards.expires_at': {
    title: 'Ablaufdatum',
    short:
      'Gültig bis Tagesende; leer bedeutet kein Ablauf. Klären Sie dies vorher mit Ihrem Rechtsbeistand.',
  },
  'gift_cards.note': { title: 'Interne Notiz', short: 'Nur in der Verwaltung sichtbar.' },
  'gift_cards.copy_code': {
    title: 'Code kopieren',
    short: 'Kopiert den Code. Nach dem Schließen des Fensters wird er nicht erneut angezeigt.',
  },
  'gift_cards.adjust': {
    title: 'Guthaben korrigieren',
    short: 'Erhöht oder verringert das Guthaben mit Begründung. Das Guthaben kann nicht negativ werden.',
  },
  'gift_cards.toggle': {
    title: 'Karte deaktivieren / wieder aktivieren',
    short: 'Eine deaktivierte Karte wird an der Kasse abgelehnt; frühere Bestellungen sind nicht betroffen.',
  },
  'gift_cards.save': { title: 'Speichern', short: 'Speichert das Ablaufdatum und die interne Notiz.' },
  'tenders.adjust_direction': { title: 'Vorgang', short: 'Guthaben hinzufügen oder abziehen.' },
  'tenders.adjust_amount': {
    title: 'Betrag',
    short: 'Positiver Betrag; die Richtung wählen Sie unter „Vorgang“.',
  },
  'tenders.adjust_note': {
    title: 'Begründung',
    short: 'Pflichtangabe; wird im Verlauf und im Prüfprotokoll erfasst.',
  },
  'customers.store_credit_adjust': {
    title: 'Guthaben hinzufügen / abziehen',
    short:
      'Fügt Shop-Guthaben mit Begründung hinzu (z. B. aus Kulanz) oder zieht es ab. Der Kunde sieht nur den Betrag.',
  },
  'customers.store_credit_currency': {
    title: 'Währung',
    short: 'Guthaben wird je Währung geführt und nur bei Bestellungen in derselben Währung verwendet.',
  },
  'customers.gift_card_issue': {
    title: 'Geschenkkarte ausstellen',
    short: 'Erstellt eine für diesen Kunden erfasste Geschenkkarte. Der Code wird einmal angezeigt.',
  },
  'orders.refund_payment': {
    title: 'Zu erstattende Zahlung',
    short: 'Bei mehreren Zahlungen (z. B. Geschenkkarte + Karte) wählen Sie, welche erstattet wird.',
  },
  'orders.refund_store_credit': {
    title: 'Als Shop-Guthaben erstatten',
    short:
      'Schreibt den Betrag statt einer Geldauszahlung als Shop-Guthaben gut; nur mit Einwilligung, nie bei Widerruf.',
  },
  'orders.refund_consent': {
    title: 'Einwilligung des Kunden',
    short: 'Bestätigen Sie, dass der Kunde Shop-Guthaben akzeptiert hat; Pflicht.',
  },
  'page.loyalty': {
    title: 'Treueprogramm',
    short: 'Sammel- und Einlösequoten, Warte- und Gültigkeitsfristen, VIP-Stufen und Programmübersicht.',
  },
  'loyalty.enabled': {
    title: 'Treueprogramm aktiv',
    short: 'Wenn aktiv, sammeln und nutzen Mitglieder Punkte und Stufenvorteile gelten.',
  },
  'loyalty.currency': {
    title: 'Währung',
    short: 'Punkte werden nur bei Bestellungen in dieser Währung gesammelt und eingelöst.',
  },
  'loyalty.earn': { title: 'Punkte pro 1 Einheit', short: 'Z. B. 10: 10 Punkte für jede ausgegebene 1 TRY.' },
  'loyalty.value': { title: 'Punkte für 1 Einheit', short: 'Z. B. 100: 100 Punkte = 1 TRY.' },
  'loyalty.min_redeem': {
    title: 'Mindestpunkte zum Einlösen',
    short: 'Mindestguthaben, um Punkte an der Kasse einzulösen.',
  },
  'loyalty.hold_days': {
    title: 'Wartezeit nach Zustellung',
    short: 'Punkte werden so viele Tage nach der Zustellung gutgeschrieben.',
  },
  'loyalty.expiry': {
    title: 'Punktegültigkeit',
    short: 'Gesammelte Punkte verfallen nach so vielen Monaten; 0 = nie.',
  },
  'loyalty.tiers': {
    title: 'VIP-Stufen',
    short: 'Stufen nach Nettoausgaben der letzten 12 Monate; Rabatt und kostenloser Versand.',
  },
  'loyalty.tier_name': { title: 'Stufenname', short: 'Für Kunden sichtbar.' },
  'loyalty.tier_spend': {
    title: 'Mindestausgaben (12 Monate)',
    short: 'Mitglieder ab diesem Ausgabenbetrag erhalten die Stufe.',
  },
  'loyalty.tier_discount': {
    title: 'Stufenrabatt',
    short: 'Prozentrabatt auf Produkte (max. 50 %), nach Aktionen.',
  },
  'loyalty.tier_shipping': { title: 'Kostenloser Versand', short: 'Keine Versandkosten für diese Stufe.' },
  'loyalty.tier_add': {
    title: 'Stufe hinzufügen',
    short: 'Fügt eine Stufenzeile hinzu; wirksam nach dem Speichern.',
  },
  'loyalty.tier_remove': {
    title: 'Stufe entfernen',
    short: 'Entfernt die Stufenzeile; wirksam nach dem Speichern.',
  },
  'loyalty.save': {
    title: 'Speichern',
    short: 'Speichert die Einstellungen (Zwei-Faktor-Authentifizierung).',
  },
  'loyalty.adjust_points': {
    title: 'Punkte',
    short: 'Hinzuzufügende oder abzuziehende Punkte (positive ganze Zahl).',
  },
  'customers.loyalty_adjust': {
    title: 'Punkte hinzufügen / abziehen',
    short: 'Fügt Treuepunkte mit Begründung hinzu oder zieht sie ab; der Kunde sieht die Begründung nicht.',
  },
  'page.referrals': {
    title: 'Empfehlungsprogramm',
    short:
      'Freunde einladen: Rabatt auf die erste Bestellung, Prämien-Gutscheincode für den Empfehlenden, Wartezeit, Limit und letzte Einladungen.',
  },
  'referrals.enabled': {
    title: 'Empfehlungsprogramm aktiv',
    short: 'Wenn aktiv, können Mitglieder Einladungscodes erhalten und die Codes funktionieren im Warenkorb.',
  },
  'referrals.currency': {
    title: 'Währung',
    short: 'Einladungscodes und Prämien-Gutscheincodes funktionieren nur in Warenkörben dieser Währung.',
  },
  'referrals.friend': {
    title: 'Rabatt für Freunde',
    short: 'Gutscheinrabatt für Freunde, die mit einem Einladungscode ihre erste Bestellung aufgeben.',
  },
  'referrals.referrer': {
    title: 'Prämie für Empfehlende',
    short: 'Einmal-Gutscheincode für den Empfehlenden nach Zustellung und Ablauf der Wartezeit.',
  },
  'referrals.reward_type': { title: 'Rabattart', short: 'Prozent (max. 50 %) oder fester Betrag.' },
  'referrals.reward_value': {
    title: 'Rabatt',
    short: 'Prozent (z. B. 10) oder fester Betrag (z. B. 50,00).',
  },
  'referrals.reward_min': {
    title: 'Mindestwarenkorbwert',
    short: 'Mindestwarenkorbwert für den Code. Leer: kein Minimum.',
  },
  'referrals.hold_days': {
    title: 'Wartezeit nach Zustellung',
    short: 'Die Prämie wird so viele Tage nach Zustellung der Bestellung des Freundes ausgegeben.',
  },
  'referrals.valid_days': {
    title: 'Gültigkeit des Prämien-Gutscheincodes',
    short: 'Tage, die der Prämien-Gutscheincode gültig bleibt (7–365).',
  },
  'referrals.max_rewards': {
    title: 'Maximale Prämien pro Mitglied',
    short: 'Höchstzahl an Prämien, die ein Mitglied erhalten kann.',
  },
  'referrals.save': {
    title: 'Speichern',
    short:
      'Speichert die Einstellungen und aktualisiert alle Einladungscodes (Zwei-Faktor-Authentifizierung).',
  },
  'page.affiliates': {
    title: 'Affiliates',
    short:
      'Affiliates und Influencer, Zuordnung per Gutscheincode oder Link, Provisionen und manuelle Auszahlung.',
  },
  'affiliates.create': {
    title: 'Affiliate hinzufügen',
    short: 'Legt einen neuen Affiliate oder Influencer an.',
  },
  'affiliates.edit': {
    title: 'Bearbeiten',
    short: 'Ändert Satz, Gutscheincode, Quelle oder Status für künftige Bestellungen.',
  },
  'affiliates.name': { title: 'Name', short: 'Name des Partners oder Kanals; erscheint in der CSV.' },
  'affiliates.kind': { title: 'Typ', short: 'Affiliate oder Influencer; nur zur Gruppierung.' },
  'affiliates.email': {
    title: 'E-Mail',
    short: 'Interner Kontakteintrag; das System sendet Partnern keine E-Mails.',
  },
  'affiliates.source': {
    title: 'Linkquelle (utm_source)',
    short:
      'Wert, der den Links des Partners hinzugefügt wird; Kleinbuchstaben, Ziffern, Bindestrich, Unterstrich.',
  },
  'affiliates.discount': {
    title: 'Gutscheincode',
    short: 'Gutscheincode des Partners; Bestellungen damit werden vor Links zugeordnet.',
  },
  'affiliates.rate': {
    title: 'Provisionssatz',
    short: 'Prozentsatz des Produktbetrags ohne MwSt. und Versand (max. 50 %).',
  },
  'affiliates.status': {
    title: 'Status',
    short: 'Pausierten Partnern werden keine neuen Bestellungen zugeordnet.',
  },
  'affiliates.note': { title: 'Interne Notiz', short: 'Interne Notizen; für den Partner nicht sichtbar.' },
  'affiliates.save': {
    title: 'Speichern',
    short: 'Speichert den Affiliate (Zwei-Faktor-Authentifizierung).',
  },
  'commissions.filter_affiliate': {
    title: 'Affiliate-Filter',
    short: 'Beschränkt Liste und CSV auf einen Affiliate.',
  },
  'commissions.filter_status': {
    title: 'Statusfilter',
    short: 'Ohne Status enthält die CSV die freigegebenen Provisionen.',
  },
  'commissions.export': { title: 'CSV herunterladen', short: 'Excel-kompatible CSV für Auszahlungen.' },
  'commissions.select': { title: 'Auswahl', short: 'Nur freigegebene Provisionen können ausgewählt werden.' },
  'commissions.mark_paid': {
    title: 'Als bezahlt markieren',
    short:
      'Markiert ausgewählte genehmigte Provisionen als bezahlt; dies kann nicht rückgängig gemacht werden.',
  },
  'commissions.paid_note': {
    title: 'Zahlungsnotiz',
    short: 'Belegnummer, Datum usw. (mindestens 3 Zeichen).',
  },
  'page.support_tickets': {
    title: 'Support-Tickets',
    short:
      'Kundenanfragen: Status, Zuweisung, Priorität; gespeicherte Antworten; Bericht zu Support, Zufriedenheit und Rücksendegründen.',
  },
  'page.support_ticket': {
    title: 'Support-Ticket',
    short:
      'Verlauf, Antwort an den Kunden, interne Notiz, gespeicherte Antwort; Status, Priorität, Thema und zuständige Person.',
  },
  'support.filter_status': {
    title: 'Statusfilter',
    short: '„Offen und wartend“ zeigt Tickets, die auf eine Antwort oder auf den Kunden warten.',
  },
  'support.filter_assigned': {
    title: 'Filter nach zuständiger Person',
    short: 'Tickets, die Ihnen, niemandem oder einem bestimmten Mitarbeiter zugewiesen sind.',
  },
  'support.filter_priority': {
    title: 'Prioritätsfilter',
    short: 'Nur Tickets mit der ausgewählten Priorität.',
  },
  'support.search': {
    title: 'Suche',
    short: 'Ticketnummer, vollständige E-Mail-Adresse des Kunden oder ein Wort aus dem Betreff.',
  },
  'support.report_period': { title: 'Berichtszeitraum', short: 'Letzte 7, 30, 90 oder 365 Tage.' },
  'support.macro_add': {
    title: 'Gespeicherte Antwort hinzufügen',
    short: 'Speichert eine häufig verwendete Antwort unter einem Namen.',
  },
  'support.macro_edit': {
    title: 'Bearbeiten',
    short: 'Ändert den Namen oder Text der gespeicherten Antwort.',
  },
  'support.macro_delete': {
    title: 'Löschen',
    short: 'Löscht die gespeicherte Antwort; bereits gesendete Antworten sind nicht betroffen.',
  },
  'support.macro_title': { title: 'Name', short: 'Kurzer, eindeutiger Name (2–80 Zeichen).' },
  'support.macro_body': {
    title: 'Antworttext',
    short: '{{name}} wird beim Einfügen durch den Namen des Kunden ersetzt.',
  },
  'support.status': {
    title: 'Status',
    short:
      'Wartet auf Antwort, wartet auf Kunden, gelöst (wird bei einer Antwort wieder geöffnet) oder geschlossen.',
  },
  'support.priority': {
    title: 'Priorität',
    short: 'Dringende und hohe Tickets erscheinen zuerst; Kunden sehen die Priorität nicht.',
  },
  'support.category': { title: 'Thema', short: 'Thema des Tickets; wird im Bericht gezählt.' },
  'support.assignee': {
    title: 'Zuständige Person',
    short: 'Nur aktive Mitarbeiter, die auf Tickets antworten dürfen.',
  },
  'support.notify_resolved': {
    title: 'E-Mail bei Lösung',
    short: 'Sendet dem Kunden eine E-Mail mit einem Bewertungslink.',
  },
  'support.save_details': {
    title: 'Änderungen speichern',
    short: 'Speichert Status, Priorität, Thema und zuständige Person.',
  },
  'support.insert_macro': {
    title: 'Gespeicherte Antwort einfügen',
    short: 'Fügt die gespeicherte Antwort in das Textfeld ein.',
  },
  'support.reply_body': { title: 'Antwort oder interne Notiz', short: 'Bis zu 5000 Zeichen.' },
  'support.send_reply': {
    title: 'Antwort senden',
    short: 'Wird auf der Ticketseite angezeigt und dem Kunden per E-Mail gesendet.',
  },
  'support.send_resolve': {
    title: 'Senden und als gelöst markieren',
    short: 'Sendet die Antwort und markiert das Ticket als gelöst.',
  },
  'support.add_note': { title: 'Interne Notiz hinzufügen', short: 'Nur für das Team sichtbar.' },
  'support.convert_subject': {
    title: 'Betreff des Tickets',
    short: 'Wird in der Liste und in der E-Mail an den Kunden angezeigt.',
  },
  'messages.convert': {
    title: 'In Ticket umwandeln',
    short: 'Wandelt die Nachricht in ein Support-Ticket um; die Nachricht wird als bearbeitet markiert.',
  },
  'page.live_chat': {
    title: 'Live-Chat',
    short:
      'Live-Support-Chats aus dem Storefront: Verfügbarkeit, wartende/laufende Chats, Antworten, Schließen, Umwandeln und Einstellungen.',
  },
  'chat.available': {
    title: 'Für Chats verfügbar',
    short: 'Besucher können Chats starten, solange dies aktiviert und die Seite geöffnet ist.',
  },
  'chat.claim': { title: 'Chat übernehmen', short: 'Weist Ihnen den wartenden Chat zu.' },
  'chat.reply': { title: 'Antwort', short: 'Bis zu 2000 Zeichen.' },
  'chat.send': { title: 'Senden', short: 'Sendet die Nachricht an den Besucher.' },
  'chat.close': {
    title: 'Chat schließen',
    short: 'Beendet den Chat; der Besucher kann einen neuen starten.',
  },
  'chat.convert': {
    title: 'In Ticket umwandeln',
    short: 'Überführt den Chat in ein Support-Ticket (E-Mail-Adresse erforderlich).',
  },
  'chat.enabled': {
    title: 'Live-Support-Fenster',
    short: 'Zeigt die Live-Support-Schaltfläche im Storefront an; standardmäßig deaktiviert.',
  },
  'chat.retention': {
    title: 'Aufbewahrung',
    short: 'Geschlossene Chats werden nach dieser Anzahl von Tagen gelöscht.',
  },
  'chat.idle': {
    title: 'Inaktivität',
    short: 'Chats ohne Nachrichten werden nach dieser Anzahl von Minuten geschlossen.',
  },
  'chat.save': { title: 'Einstellungen speichern', short: 'Speichert die Chat-Einstellungen.' },
  'page.google_ads_campaigns': {
    title: 'Google-Ads-Kampagnen',
    short:
      'Erstellt Performance-Max- und Shopping-Kampagnen aus Ihren Produkten, lässt sie freigeben und wendet sie über die Google Ads API an.',
  },
  'page.google_ads_campaign': {
    title: 'Google-Ads-Kampagne',
    short: 'Werte, Produkte und Google-Status der Kampagne; Änderungsanfragen und Verlauf.',
  },
  'gads.settings': {
    title: 'Budgetobergrenze und Einstellungen',
    short: 'Monatliche Werbebudget-Obergrenze und automatischer Stopp bei Erreichen.',
  },
  'gads.new_campaign': {
    title: 'Neue Kampagne',
    short: 'Assistent: Shop, Typ, Produkte, Budget und Gebote, Regionen/Sprachen/Daten, Zusammenfassung.',
  },
  'gads.stop_all': {
    title: 'Alle Google-Kampagnen stoppen',
    short: 'Notfall: pausiert alle aktiven Kampagnen und bricht ausstehende Ausgabenanfragen ab.',
  },
  'gads.month': {
    title: 'Dieser Monat',
    short: 'Ausgaben im Monat, Obergrenze, geschätztes Monatsmaximum und API-Vorgänge heute.',
  },
  'gads.campaigns': {
    title: 'Kampagnen',
    short: 'Alle Google-Ads-Kampagnen: Status, Budget, Monatsmaximum, Ausgaben, Hinweise.',
  },
  'gads.approvals': {
    title: 'Warten auf Freigabe',
    short: 'Ausgabenwirksame Anfragen und ihre finanziellen Auswirkungen.',
  },
  'gads.decide': {
    title: 'Freigeben / Ablehnen',
    short: 'Freigabe erfordert Zwei-Faktor-Bestätigung; eigene Anfragen gibt nur der Inhaber frei.',
  },
  'gads.decision_note': {
    title: 'Notiz',
    short: 'Begründung der Entscheidung; wird im Prüfprotokoll gespeichert.',
  },
  'gads.monthly_cap': {
    title: 'Monatliche Werbebudget-Obergrenze',
    short: 'Monatliche Obergrenze für alle Google-Ads-Kampagnen.',
  },
  'gads.auto_stop': {
    title: 'Automatischer Stopp',
    short:
      'Standardmäßig aus. Erreichen die Monatsausgaben die Obergrenze, werden aktive Kampagnen ohne Freigabe pausiert.',
  },
  'gads.daily_limit': {
    title: 'Tägliches API-Vorgangslimit',
    short: 'Mit Explorer-Zugriff begrenzt Google die täglichen Vorgänge in echten Konten.',
  },
  'gads.submit': {
    title: 'Entwurf speichern / Zur Freigabe senden',
    short: 'Ein Entwurf geht nicht an Google; nach Freigabe wird die Kampagne pausiert angelegt.',
  },
  'gads.store': {
    title: 'Shop',
    short: 'Google-Ads- und Merchant-Center-Konten kommen aus den Google-Verbindungen des Shops.',
  },
  'gads.kind': {
    title: 'Kampagnentyp',
    short: 'Performance Max läuft in allen Google-Kanälen; Shopping nur in Google Shopping.',
  },
  'gads.name': {
    title: 'Kampagnenname',
    short: 'In Google Ads angezeigter Name; muss im Konto eindeutig sein.',
  },
  'gads.daily_budget': {
    title: 'Tagesbudget',
    short: 'Google kann an einem Tag bis zum Doppelten ausgeben; Monatsmaximum ≈ Tagesbudget × 30,4.',
  },
  'gads.bidding': {
    title: 'Gebotsstrategie',
    short: 'Performance Max: Conversion-Wert oder Conversions; Shopping: Conversion-Wert oder Klicks.',
  },
  'gads.target_roas': {
    title: 'Ziel-ROAS',
    short: 'Angestrebter Conversion-Wert je ausgegebener Einheit (Prozent). Senken kann Ausgaben erhöhen.',
  },
  'gads.target_cpa': {
    title: 'Ziel-Kosten pro Conversion',
    short: 'Angestrebte durchschnittliche Kosten pro Kauf. Erhöhen kann Ausgaben steigern.',
  },
  'gads.request_note': {
    title: 'Notiz',
    short: 'Begründung für die freigebende Person; im Prüfprotokoll gespeichert.',
  },
  'gads.products': {
    title: 'Produkte',
    short: 'Nur Produkte im Google-Feed, die in keiner anderen Kampagne sind, werden hinzugefügt.',
  },
  'gads.countries': {
    title: 'Länder',
    short: 'Länder, in denen Anzeigen erscheinen. Hinzufügen erfordert Freigabe, Entfernen nicht.',
  },
  'gads.languages': {
    title: 'Sprachen',
    short: 'Ausgerichtete Nutzersprachen. Hinzufügen erfordert Freigabe.',
  },
  'gads.dates': {
    title: 'Daten',
    short: 'Start und Ende (Zeitzone des Kontos). Verlängern oder Entfernen des Endes erfordert Freigabe.',
  },
  'gads.label': {
    title: 'Produktlabel',
    short:
      'Als custom_label_0 an Merchant gesendetes Kampagnenlabel; der Produktfilter enthält dieses Label.',
  },
  'gads.accept_google': {
    title: 'Googles Werte übernehmen',
    short: 'Passt den Systemeintrag an Google Ads an; nichts wird an Google gesendet.',
  },
  'gads.operations': {
    title: 'Kampagnenaktionen',
    short:
      'Freigabepflichtige Aktionen gehen zur Freigabe, andere nach dem Häkchen direkt in die Warteschlange.',
  },
  'gads.product_states': {
    title: 'Produktstatus',
    short:
      'In Warteschlange → An Google gesendet → Zulässig / Eingeschränkt / Nicht zulässig (mit Googles Gründen).',
  },
  'gads.operation_history': {
    title: 'Änderungen',
    short: 'Wer angefordert und freigegeben hat, ob angewendet wurde; bei Fehlern der Grund.',
  },
  'gads.recommendations': {
    title: 'Google-Empfehlungen',
    short: 'Bei Bedarf von Google gelesen; Anwenden erfordert immer Freigabe.',
  },
  'gads.change_history': {
    title: 'Änderungsverlauf',
    short: 'Google-Ads-Änderungsprotokoll der letzten 14 Tage; nicht gespeichert.',
  },
  'gads.send_operation': {
    title: 'Senden',
    short:
      'Freigabepflichtiges geht zur Freigabe, anderes in die Warteschlange; zuerst Prüfung, dann Anwendung und Rücklesen.',
  },
  'gads.product_box': {
    title: 'Google-Anzeigen',
    short: 'Google-Ads-Kampagne des Produkts, Status in Google und Zulässigkeit.',
  },
  'gads.bulk': {
    title: 'Zu Google-Kampagne hinzufügen / entfernen',
    short: 'Fügt ausgewählte Produkte einer Kampagne hinzu oder entfernt sie; unzulässige mit Gründen.',
  },
  'gads.filter': { title: 'Filter Google-Anzeigen', short: 'Filtert Produkte nach ihrem Kampagnenstatus.' },
  'gads.search_source': {
    title: 'Entwurfsquelle',
    short: 'Produkt oder Kategorie, aus der der Anzeigenentwurf erstellt wird.',
  },
  'gads.search_suggest': {
    title: 'Entwurf aus Daten',
    short:
      'Schlägt Anzeigentitel, Beschreibungen, Anzeigepfad und Keywords vor; Sie bearbeiten und senden zur Freigabe.',
  },
  'gads.search_final_url': {
    title: 'Finale URL',
    short: 'Die Seite, die nach dem Klick geöffnet wird; muss auf der Shop-Domain liegen.',
  },
  'gads.search_path': {
    title: 'Anzeigepfad',
    short: 'Zwei Teile hinter der Domain in der Anzeige (je bis zu 15 Zeichen).',
  },
  'gads.search_headlines': {
    title: 'Anzeigentitel',
    short: '3–15 Titel mit je bis zu 30 Zeichen; Google testet die Kombinationen.',
  },
  'gads.search_descriptions': {
    title: 'Beschreibungen',
    short: '2–4 Beschreibungen mit je bis zu 90 Zeichen.',
  },
  'gads.search_keywords': {
    title: 'Keywords',
    short: 'Für welche Suchanfragen die Anzeige erscheinen kann, mit Option (weitgehend, Wortgruppe, genau).',
  },
  'gads.search_negatives': {
    title: 'Ausschließende Keywords',
    short: 'Suchanfragen, bei denen die Anzeige nicht erscheinen soll (z. B. „kostenlos“).',
  },
  'gads.search_ideas': {
    title: 'Keywords vorschlagen',
    short: 'Vorschläge und monatliche Suchanfragen aus dem Planungsdienst von Google.',
  },
  'gads.text_review': {
    title: 'Expertenprüfung',
    short: 'Enthält der Anzeigentext Gesundheitsaussagen, muss die tierärztliche Prüfung zustimmen.',
  },
  'gads.text_review_decide': {
    title: 'Text freigeben / ablehnen',
    short: 'Expertenentscheidung; Ablehnung braucht eine Begründung.',
  },
  'gads.search_edit_ad': {
    title: 'Anzeigentext bearbeiten',
    short: 'Im Entwurf direkt gespeichert; in der Live-Kampagne ist eine Textänderung freigabepflichtig.',
  },
  'gads.search_edit_keywords': {
    title: 'Keywords bearbeiten',
    short: 'Hinzufügen braucht Freigabe; nur Entfernen wird nach Bestätigung sofort angewendet.',
  },
  'gads.search_edit_negatives': {
    title: 'Ausschlüsse bearbeiten',
    short: 'Hinzufügen wird nach Bestätigung sofort angewendet; Entfernen braucht Freigabe.',
  },
  'gads.search_google_state': {
    title: 'Status bei Google',
    short: 'Richtlinienfreigabe, Prüfstatus und Anzeigeneffektivität von Google (stündlich gelesen).',
  },
  'gads.access_level': {
    title: 'Google Ads API-Zugriffsstufe',
    short: 'Die Stufe, die Google Ihrem Cloud-Projekt gewährt hat: Explorer, Basic oder Standard.',
  },
  'page.needs_finder': {
    title: 'Bedarfsfinder',
    short:
      'Bearbeitet den Fragensatz „Was mein Tier braucht“, lässt ihn fachlich freigeben und veröffentlicht ihn im Shop.',
  },
  'needs_finder.generate': {
    title: 'Erster Entwurf aus Produktdaten',
    short: 'Erzeugt Bereiche aus Kategorien, Regeln aus Produkten und eine allgemeine Notfallfrage.',
  },
  'needs_finder.start_empty': {
    title: 'Leerer Entwurf',
    short: 'Beginnt ohne Bereiche, Fragen oder Regeln; Sie fügen alles hinzu.',
  },
  'needs_finder.save': {
    title: 'Entwurf speichern',
    short: 'Speichert Ihre Änderungen; ändert sich der Inhalt, verfällt die Freigabe.',
  },
  'needs_finder.status': {
    title: 'Status und Veröffentlichung',
    short: 'Prüfstatus, veröffentlichte Version und Prüfliste.',
  },
  'needs_finder.problems': {
    title: 'Prüfliste',
    short: 'Punkte, die die Veröffentlichung verhindern (rot), und Hinweise (gelb).',
  },
  'needs_finder.submit': {
    title: 'Zur Prüfung einreichen',
    short: 'Reicht den gespeicherten Entwurf zur tierärztlichen Freigabe ein.',
  },
  'needs_finder.review': {
    title: 'Fachliche Entscheidung',
    short: 'Die tierärztliche Prüfung gibt den Entwurf frei oder lehnt ihn begründet ab.',
  },
  'needs_finder.review_note': {
    title: 'Notiz der Fachperson',
    short: 'Begründung; bei Ablehnung Pflicht, wird protokolliert.',
  },
  'needs_finder.findings': {
    title: 'Gesundheitsversprechen',
    short:
      'Ausdrücke wie Vorbeugen oder Heilen von Krankheiten; keine endgültige Entscheidung, die Fachperson entscheidet.',
  },
  'needs_finder.products': {
    title: 'Mögliche Produktvorschläge',
    short: 'Alle Produkte aus Regeln und Alternativen mit Begründungen und Shopstatus.',
  },
  'needs_finder.publish': {
    title: 'Veröffentlichen',
    short: 'Veröffentlicht den freigegebenen Entwurf im Shop unter /ihtiyac-bulucu.',
  },
  'needs_finder.unpublish': {
    title: 'Zurückziehen',
    short: 'Schließt die Shopseite; Entwurf und Freigabe bleiben erhalten.',
  },
  'needs_finder.regenerate': {
    title: 'Entwurf neu erzeugen',
    short: 'Ersetzt den Entwurf durch einen neuen aus Produktdaten; Änderungen und Freigabe gehen verloren.',
  },
  'needs_finder.pet_profiles': {
    title: 'Mein Tier speichern',
    short:
      'Mitglieder können die Grunddaten ihres Tieres mit ausdrücklicher Einwilligung speichern. Standardmäßig aus.',
  },
  'needs_finder.translations': {
    title: 'Weitere Sprachen',
    short: 'Englische, deutsche und arabische Fassung; leer bedeutet Türkisch.',
  },
  'needs_finder.condition': {
    title: 'Bedingung',
    short:
      'Wann Regel, Frage oder Ausschluss gilt: Tierart, Alter, Gewicht, Kastration, Trächtigkeit, Bereich, Antwort.',
  },
  'needs_finder.row_actions': {
    title: 'Sortieren, bearbeiten, löschen',
    short: 'Ändert Reihenfolge, bearbeitet oder entfernt das Element; bis zum Speichern umkehrbar.',
  },
  'needs_finder.areas': {
    title: 'Bereiche',
    short: 'Themen, die Besucher wählen; nur für markierte Tierarten sichtbar.',
  },
  'needs_finder.area_label': {
    title: 'Name des Bereichs',
    short: 'Im Shop angezeigter Name (z. B. „Gelenke“).',
  },
  'needs_finder.area_description': {
    title: 'Kurzbeschreibung',
    short: 'Ein Satz unter dem Bereich; optional.',
  },
  'needs_finder.area_species': {
    title: 'Sichtbar für Tierarten',
    short: 'Der Bereich erscheint nur bei diesen Tierarten.',
  },
  'needs_finder.area_categories': {
    title: 'Zugehörige Kategorien',
    short: 'Aus welchen Produktkategorien der Bereich stammt.',
  },
  'needs_finder.id': { title: 'ID', short: 'Kurzname in Bedingungen; nach dem Speichern nicht ändern.' },
  'needs_finder.questions': {
    title: 'Fragen',
    short: 'Fragen in Reihenfolge; Verzweigung nach Bereich oder früheren Antworten.',
  },
  'needs_finder.question_text': {
    title: 'Fragetext',
    short: 'Der Satz, der gefragt wird. Keine Dosierungs- oder Diagnosefragen.',
  },
  'needs_finder.question_help': { title: 'Hilfetext', short: 'Kurze Erklärung unter der Frage; optional.' },
  'needs_finder.question_area': {
    title: 'Zugehöriger Bereich',
    short: 'Wenn gesetzt, nur bei diesem Bereich; „Allgemein“ fragt alle.',
  },
  'needs_finder.question_multiple': {
    title: 'Mehrfachauswahl',
    short: 'Besucher können mehrere Optionen wählen.',
  },
  'needs_finder.options': { title: 'Optionen', short: 'Die Antworten auf die Frage (2–12).' },
  'needs_finder.option_label': { title: 'Optionstext', short: 'Wird im Shop als Antwort angezeigt.' },
  'needs_finder.option_emergency': {
    title: 'Notfalloption',
    short: 'Bei Auswahl keine Produkte; „Bitte zuerst an Tierarzt wenden“ wird angezeigt.',
  },
  'needs_finder.option_exclusive': {
    title: 'Nur allein wählbar',
    short: 'Wie „Nichts davon“: Auswahl hebt die anderen auf.',
  },
  'needs_finder.rules': {
    title: 'Empfehlungsregeln',
    short: 'Trifft die Bedingung zu, wird das Produkt mit Begründung vorgeschlagen; nach Priorität sortiert.',
  },
  'needs_finder.rule_product': {
    title: 'Vorgeschlagenes Produkt',
    short: 'Das Produkt, das bei zutreffender Bedingung vorgeschlagen wird.',
  },
  'needs_finder.rule_reason': {
    title: 'Warum vorgeschlagen',
    short: 'Freigegebene Begründung unter dem Produkt. Keine Heil- oder Vorbeugeversprechen.',
  },
  'needs_finder.rule_priority': { title: 'Priorität', short: 'Höhere Priorität steht weiter oben (0–100).' },
  'needs_finder.rule_alternatives': {
    title: 'Alternativen',
    short: 'Freigegebene Alternativen bei fehlendem Bestand (höchstens 5).',
  },
  'needs_finder.exclusions': {
    title: 'Ausschlüsse',
    short: 'Trifft die Bedingung zu, werden die Produkte von keiner Regel vorgeschlagen; hat immer Vorrang.',
  },
  'needs_finder.exclusion_products': {
    title: 'Nicht vorzuschlagende Produkte',
    short: 'Produkte, die bei zutreffender Bedingung keine Regel vorschlägt.',
  },
  'needs_finder.exclusion_note': {
    title: 'Notiz',
    short: 'Grund des Ausschlusses (im Panel und in der Vorschau).',
  },
  'needs_finder.texts': { title: 'Texte', short: 'Einleitung, Notfalltext und Paketaktion.' },
  'needs_finder.intro': {
    title: 'Einleitungstext',
    short: 'Oben auf der Seite; leer bedeutet Standardtext.',
  },
  'needs_finder.emergency_text': {
    title: 'Notfalltext',
    short: 'Unter „Bitte zuerst an Tierarzt wenden“ bei einem Notfall.',
  },
  'needs_finder.bundle': {
    title: 'Bedarfspaket-Aktion',
    short: 'Optionale automatische Aktion (standardmäßig aus); läuft sie, erscheinen Name und Bedingung.',
  },
  'needs_finder.preview': {
    title: 'Vorschau',
    short: 'Gleiche Berechnung wie im Shop; Bestand wird ignoriert.',
  },
  'needs_finder.report': { title: 'Bericht', short: 'Nur Summen; keine Besucheridentität oder Antworten.' },
  'needs_finder.report_days': { title: 'Zeitraum', short: 'Letzte 7, 30 oder 90 Tage (Istanbuler Zeit).' },
  'page_designs.needs_finder': {
    title: 'Design „Was mein Tier braucht“',
    short: 'Darstellung des Fragenablaufs: Karten, Chat oder eine Seite mit Live-Übersicht.',
  },
  'page.ads': {
    title: 'Werbezentrale',
    short:
      'Zeigt Werbeausgaben zusammen mit den tatsächlichen Bestellungen des Shops: Kampagnenleistung, ROAS, Kosten pro Bestellung, Gewinn, Warnungen und genehmigte Budgetänderungen.',
  },
  'ads.connections': {
    title: 'Werbeverbindungen',
    short: 'Meta und TikTok werden auf der Seite „Verbindungen“ verbunden (nur Lesezugriff).',
  },
  'ads.sync_now': {
    title: 'Jetzt synchronisieren',
    short:
      'Liest die letzten 7 Tage (bei der ersten Synchronisierung 30) aus den Live-Verbindungen zu Meta/TikTok.',
  },
  'ads.add_account': {
    title: 'Konto hinzufügen',
    short: 'Fügt ein Werbekonto hinzu, dessen Bericht Sie als Datei hochladen.',
  },
  'ads.save_account': {
    title: 'Speichern',
    short: 'Speichert das Konto. Archivierte Konten werden im Bericht ausgeblendet.',
  },
  'ads.account_platform': {
    title: 'Plattform',
    short: 'Wo die Anzeigen geschaltet werden; legt die Standard-Zuordnungsquellen fest.',
  },
  'ads.account_name': { title: 'Kontoname', short: 'Im Bericht angezeigter Name (2–80 Zeichen).' },
  'ads.account_external_id': {
    title: 'Konto-ID auf der Plattform',
    short: 'Optional; muss pro Plattform eindeutig sein.',
  },
  'ads.account_currency': {
    title: 'Währung',
    short: 'Die Währung des Werbekontos; hochgeladene Beträge müssen in dieser Währung angegeben sein.',
  },
  'ads.account_utm_sources': {
    title: 'Zuordnungsquellen (utm_source)',
    short:
      'Bestellungen, deren letzte Besuchsquelle eine dieser Quellen ist, werden diesem Konto zugeordnet.',
  },
  'ads.account_status': {
    title: 'Status',
    short: 'Archivierte Konten werden im Bericht nicht berücksichtigt; die Daten bleiben erhalten.',
  },
  'ads.account_actions': {
    title: 'Bericht hochladen und bearbeiten',
    short: 'Laden Sie einen Plattformbericht hoch oder bearbeiten Sie das Konto.',
  },
  'ads.upload_file': {
    title: 'Berichtsdatei',
    short: 'CSV oder XLSX bis 10 MB mit den Spalten Datum, Kampagne und Ausgaben.',
  },
  'ads.apply_import': {
    title: 'Daten speichern',
    short:
      'Speichert die in der Vorschau angezeigten gültigen Zeilen; vorhandene Zeilen werden überschrieben.',
  },
  'ads.report': {
    title: 'Kampagnenleistung',
    short: 'CTR, CPC, CPM, Kosten pro Bestellung und ROAS auf Basis tatsächlicher Bestellungen.',
  },
  'ads.period': { title: 'Zeitraum', short: 'Letzte 7, 30 oder 90 Tage (Istanbuler Zeit).' },
  'ads.platform_filter': { title: 'Plattform', short: 'Beschränkt den Bericht auf eine Plattform.' },
  'ads.compare': {
    title: 'Vergleichen',
    short: 'Wählen Sie bis zu 4 Kampagnen für einen direkten Vergleich aus.',
  },
  'ads.anomalies': {
    title: 'Warnungen',
    short: 'Ausgabenspitzen, CTR-Rückgänge, Ausgaben ohne Klicks und veraltete Synchronisierungen.',
  },
  'ads.product_ads': {
    title: 'Produkte in Shopping-Anzeigen',
    short:
      'Ausgaben, Klicks und Conversions pro Produkt aus Google-Shopping-Anzeigen, mit Nettoumsatz und Produkt-ROAS.',
  },
  'ads.products': {
    title: 'Produktleistung',
    short: 'Produkte in Bestellungen, die Anzeigen zugeordnet sind.',
  },
  'ads.request_change': {
    title: 'Änderung anfordern',
    short: 'Sendet eine Budget- oder Auslieferungsänderung zur Genehmigung.',
  },
  'ads.change_kind': { title: 'Änderung', short: 'Tagesbudget oder Auslieferungsstatus.' },
  'ads.change_budget': { title: 'Neues Tagesbudget', short: 'In der Kontowährung.' },
  'ads.change_status': { title: 'Neuer Status', short: 'Kampagne pausieren oder fortsetzen.' },
  'ads.change_note': { title: 'Grund', short: 'Warum die Änderung erforderlich ist (3–500 Zeichen).' },
  'ads.send_request': {
    title: 'Zur Genehmigung senden',
    short: 'Speichert die Anfrage; auf der Plattform ändert sich nichts.',
  },
  'ads.history': {
    title: 'Änderungsverlauf',
    short: 'Anfragen, Genehmigungen, Uploads und Synchronisierungen.',
  },
  'ads.change_actions': {
    title: 'Entscheiden, angewendet, zurückziehen',
    short: 'Anfrage genehmigen/ablehnen, als angewendet markieren oder zurückziehen.',
  },
  'ads.approve': {
    title: 'Genehmigen',
    short: 'Genehmigt die Anfrage (Zwei-Faktor-Authentifizierung erforderlich).',
  },
  'ads.reject': { title: 'Ablehnen', short: 'Lehnt die Anfrage ab.' },
  'ads.decision_note': {
    title: 'Entscheidungsnotiz',
    short: 'Optionale Notiz, die im Verlauf angezeigt wird.',
  },
  'payments.go_live_skip_sandbox': {
    title: 'Ohne Testzahlung live gehen',
    short:
      'Nur für den Inhaber. Ermöglicht den Live-Betrieb ohne erfolgreiche Sandbox-Zahlung; erfordert Zwei-Faktor-Authentifizierung und wird im Prüfprotokoll erfasst.',
  },
  'payments.go_live_confirm': {
    title: 'Live schalten',
    short:
      'Schaltet Kartenzahlungen auf Live um; echte Karten werden belastet. Erfordert einen erfolgreichen Live-Verbindungstest.',
  },
  'payments.first_live_order': {
    title: 'Prüfung der ersten Live-Bestellung',
    short:
      'Geben Sie mit Ihrer eigenen Karte eine kleine Bestellung auf, prüfen Sie, ob sie bezahlt ist, und erstatten Sie sie. Dieser Hinweis verschwindet nach der Erstattung.',
  },
  'page.retention': {
    title: 'Aufbewahrung und Löschung',
    short:
      'Automatische Löschung personenbezogener Daten nach Ablauf der Aufbewahrungsfrist (standardmäßig deaktiviert), feste Fristen und das Löschprotokoll.',
  },
  'page.sales_regions': {
    title: 'Vertriebsregionen',
    short:
      'Regionen aus Provinzen/Bezirken und ihre Verantwortlichen; Händler nach Standort Regionen zuweisen.',
  },
  'sales_regions.new': {
    title: 'Region hinzufügen',
    short: 'Neue Vertriebsregion mit Name, Orten und optionalem Verantwortlichen.',
  },
  'sales_regions.auto_assign': {
    title: 'Nach Provinz/Bezirk zuweisen',
    short:
      'Ordnet die Provinz/den Bezirk der Händler den Regionen zu: zuerst Vorschau, dann anwenden. Standardmäßig nur Händler ohne Region.',
  },
  'sales_regions.manager': {
    title: 'Regionalleiter',
    short:
      'Sieht alle Händler der Region (mit dem Bereich „nur zugewiesene Händler“). Muss ein aktiver Benutzer der Verwaltung sein, der Händler sehen darf.',
  },
  'sales_regions.places': {
    title: 'Provinzen und Bezirke',
    short:
      'Lassen Sie den Bezirk leer, um die gesamte Provinz einzuschließen; wählen Sie einen Bezirk, um nur diesen einzuschließen.',
  },
  'dealers.sales_assignment': {
    title: 'Außendienstmitarbeiter und Region',
    short:
      'Außendienstmitarbeiter und Region des Händlers. Mitarbeiter mit eingeschränktem Bereich und Regionalleiter sehen diesen Händler; Bestellungen erfassen den zum Bestellzeitpunkt zuständigen Mitarbeiter.',
  },
  'dealers.sales_rep': {
    title: 'Außendienstmitarbeiter',
    short: 'Ein aktiver Benutzer der Verwaltung, der Händler sehen darf. Leer: kein Außendienstmitarbeiter.',
  },
  'dealers.sales_region': {
    title: 'Vertriebsregion',
    short:
      'Wird hier ausgewählt oder auf der Seite „Vertriebsregionen“ gesammelt nach Provinz/Bezirk zugewiesen.',
  },
  'dealers.sales_filter': {
    title: 'Filter nach Außendienstmitarbeiter und Region',
    short:
      'Händler eines Außendienstmitarbeiters oder einer Region; „Kein Mitarbeiter“ / „Keine Region“ zeigt nicht zugewiesene Händler.',
  },
  'page.bizimhesap': {
    title: 'BizimHesap',
    short:
      'BizimHesap ist die maßgebliche Quelle für Händlerkonten: Kassenkonten, Zuordnung Händler ↔ Konto, Schreibberechtigungen, Korrektur von ohne Kassenkonto gesendeten Rechnungen und die Warteschlange für Zahlungseingänge.',
  },
  'dealers.bizimhesap_code': {
    title: 'BizimHesap-Konto',
    short:
      'Der BizimHesap-Kontocode des Händlers; solange keine Verknüpfung besteht, werden keine Rechnungen gesendet.',
  },
  'dealers.bizimhesap_link': { title: 'Kontocode', short: 'Wird mit der BizimHesap-Kontoliste abgeglichen.' },
  'bizimhesap.statement': {
    title: 'BizimHesap-Kontoauszug',
    short: 'Kontobewegungen und Saldo in BizimHesap.',
  },
  'bizimhesap.refresh_one': {
    title: 'Diesen Händler aktualisieren',
    short: 'Ruft jetzt den Kontoauszug dieses Händlers ab (nur Lesezugriff).',
  },
  'bizimhesap.refresh_all': {
    title: 'Jetzt aktualisieren',
    short: 'Stellt die Kontoauszüge aller verknüpften Händler in die Warteschlange.',
  },
  'bizimhesap.diff': {
    title: 'Differenzbericht',
    short: 'BizimHesap-Saldo + nicht Übertragenes im Vergleich zum Hauptbuch dieses Systems.',
  },
  'invoices.provider_cancel': {
    title: 'BizimHesap-Stornierung',
    short:
      'Storniert den BizimHesap-Rechnungsdatensatz einer vollständig stornierten Bestellung; für E-Belege ist eine Retourenrechnung erforderlich.',
  },
  'invoices.provider_cancel_resolve': {
    title: 'Ergebnis der Stornierung',
    short: 'Was Sie im BizimHesap-Panel gesehen haben.',
  },
  'bizimhesap.cash_map': {
    title: 'Kassenkonten',
    short: 'Ein BizimHesap-Kassenkonto pro Zahlungsart. Bezahlte Bestellungen werden damit fakturiert.',
  },
  'bizimhesap.refresh_cashiers': {
    title: 'Kassenkonten abrufen',
    short: 'Ruft die Kassenkonten aus BizimHesap ab.',
  },
  'bizimhesap.cash_card': {
    title: 'Konto für Kartenzahlungen',
    short: 'Über iyzico/PayTR bezahlte Bestellungen.',
  },
  'bizimhesap.cash_bank_transfer': {
    title: 'Konto für Banküberweisungen',
    short: 'Per Banküberweisung bezahlte Bestellungen.',
  },
  'bizimhesap.cash_cash_on_delivery': {
    title: 'Konto für Nachnahme',
    short: 'An der Haustür kassierte Bestellungen.',
  },
  'bizimhesap.cash_manual': {
    title: 'Konto für manuelle Zahlungen',
    short: 'In der Verwaltung mit „Zahlung erhalten“ markierte Zahlungen.',
  },
  'bizimhesap.cash_cash': {
    title: 'Konto für Bareinnahmen',
    short: 'Für Händlerkonten erfasste Bareinnahmen.',
  },
  'bizimhesap.save_cash': {
    title: 'Kassenkonten speichern',
    short: 'Speichert die Auswahl; wird im Prüfprotokoll erfasst.',
  },
  'bizimhesap.links': {
    title: 'Händler ↔ BizimHesap-Konto',
    short:
      'Händlerrechnungen verwenden diesen Kontocode. Rechnungen nicht verknüpfter Händler werden nicht gesendet.',
  },
  'bizimhesap.import_customers': {
    title: 'Konten importieren',
    short:
      'Gleicht die BizimHesap-Kontoliste mit den Händlern ab (zuerst Händlercode, dann Steuernummer). Zuerst Vorschau.',
  },
  'bizimhesap.ambiguous': {
    title: 'Mehrdeutige Zuordnung',
    short: 'Mehrere Kandidaten; wählen Sie das richtige Konto oder lassen Sie es unverknüpft.',
  },
  'bizimhesap.writes': {
    title: 'Schreiben in BizimHesap',
    short:
      'Schritte, die in Ihr echtes Buchhaltungskonto schreiben. Standardmäßig deaktiviert; Inhaber/Administrator und Bestätigung erforderlich.',
  },
  'bizimhesap.write_collections': {
    title: 'Zahlungseingänge senden',
    short: 'Zahlungseingänge in der Warteschlange werden mit „Senden“ übermittelt.',
  },
  'bizimhesap.write_customers': {
    title: 'Konten hinzufügen',
    short: 'Eröffnet in BizimHesap ein Konto für einen Händler, der noch keines hat.',
  },
  'bizimhesap.write_cancel': {
    title: 'Rechnungen stornieren',
    short:
      'Storniert den BizimHesap-Datensatz einer vollständig stornierten Bestellung. Für eine E-Rechnung ist stattdessen eine Retoure erforderlich (Bestätigung durch den Steuerberater ausstehend).',
  },
  'bizimhesap.save_writes': {
    title: 'Schreibeinstellungen speichern',
    short: 'Erfordert Zwei-Faktor-Authentifizierung; wird im Prüfprotokoll erfasst.',
  },
  'bizimhesap.corrections': {
    title: 'Ohne Kassenkonto gesendete bezahlte Bestellungen',
    short: 'Legen Sie dafür Zahlungseingangsdatensätze an.',
  },
  'bizimhesap.queue_corrections': {
    title: 'Zahlungseingangsdatensätze anlegen',
    short: 'An BizimHesap wird nichts gesendet.',
  },
  'bizimhesap.collections': {
    title: 'Warteschlange für Zahlungseingänge',
    short: 'Ausstehende, gesendete, fehlgeschlagene und unklare Zahlungseingänge.',
  },
  'bizimhesap.collection_actions': {
    title: 'Senden · Kontoauszug prüfen · Datensatz stornieren',
    short:
      'Unklare Zahlungseingänge werden geprüft, indem im Kontoauszug nach der Eingangsnummer gesucht wird.',
  },
  'bizimhesap.cancel_note': {
    title: 'Grund',
    short: 'Z. B. „manuell in BizimHesap erfasst“. Wird im Prüfprotokoll erfasst.',
  },
  'bizimhesap.samples': {
    title: 'Antwortbeispiele',
    short: 'Erste echte Antworten mit maskierten personenbezogenen Daten.',
  },
  'page.sales_commissions': {
    title: 'Provisionen der Außendienstmitarbeiter',
    short:
      'Provisionsregeln und monatliche Abrechnungen der Außendienstmitarbeiter: Berechnung, Genehmigung, Zahlungsvermerk und CSV.',
  },
  'sales_commissions.rules': {
    title: 'Provisionsregeln',
    short:
      'Für jede Position gilt die spezifischste Regel: mitarbeiterspezifisch vor allgemein, Produkt vor Kategorie, Kategorie vor allen Produkten; bei Gleichstand gilt die neueste Regel.',
  },
  'sales_commissions.rate': {
    title: 'Provisionssatz',
    short:
      'Prozentsatz des Nettobetrags (höchstens 50 %). Wird bei der ersten Berechnung der Bestellung festgeschrieben.',
  },
  'sales_commissions.rule_rep': {
    title: 'Außendienstmitarbeiter',
    short: 'Leer: gilt für alle Außendienstmitarbeiter. Ausgewählt: nur Bestellungen dieses Mitarbeiters.',
  },
  'sales_commissions.rule_scope': {
    title: 'Kategorie- und Produktbereich',
    short: 'Keine Kategorien oder Produkte ausgewählt: gilt für alle Produkte.',
  },
  'sales_commissions.period': {
    title: 'Monatliche Abrechnungen',
    short:
      'Eine Abrechnung pro Außendienstmitarbeiter und Monat. „Genehmigen“ sperrt sie (bleibt bei Neuberechnung erhalten); „Als bezahlt markieren“ erfasst die außerhalb des Systems geleistete Zahlung.',
  },
  'sales_commissions.calculate': {
    title: 'Zeitraum berechnen',
    short:
      'Berechnet die Bestellungen des Monats und die Zahlungs-/Erstattungsänderungen früherer Bestellungen; erstellt Abrechnungsentwürfe neu.',
  },
  'sales_commissions.csv': {
    title: 'CSV herunterladen',
    short:
      'Alle Abrechnungspositionen des Zeitraums (Bestellung, Typ, eingezogener Anteil, Bemessungsgrundlage, Provision).',
  },
  'sales_commissions.paid_note': {
    title: 'Zahlungsnotiz',
    short: 'Wie die Zahlung erfolgte (z. B. Gehaltsabrechnung Oktober). Wird im Prüfprotokoll erfasst.',
  },
  'page.quotes': {
    title: 'Angebote',
    short: 'Von Händlern angefragte und von Ihnen erstellte Angebote; Status und Gültigkeit.',
  },
  'page.quote_detail': {
    title: 'Angebot',
    short: 'Positionen, Preise, Gültigkeit und Notiz; senden, stornieren und drucken.',
  },
  'quotes.new': {
    title: 'Neues Angebot',
    short: 'Legt ein leeres Angebot für einen freigegebenen Händler an.',
  },
  'quotes.status_filter': {
    title: 'Status',
    short: 'Angefragt, Entwurf, gesendet, bestellt, abgelehnt oder storniert.',
  },
  'quotes.search': { title: 'Suche', short: 'Teil der Angebotsnummer oder des Händlernamens.' },
  'quotes.dealer': {
    title: 'Händler',
    short: 'Nur freigegebene Händler; die Preise richten sich nach der Händlergruppe.',
  },
  'quotes.print': {
    title: 'Drucken',
    short:
      'Druckt das Angebot; speichern Sie es über den Browser als PDF. Die interne Notiz wird nicht gedruckt.',
  },
  'quotes.save': {
    title: 'Speichern',
    short: 'Speichert Positionen, Preise und Bedingungen; ein gesendetes Angebot wird wieder zum Entwurf.',
  },
  'quotes.send': {
    title: 'Senden',
    short:
      'Erstellt eine unveränderliche Version, die der Händler unter „Mein Konto“ sieht; sendet eine E-Mail, wenn der E-Mail-Versand eingerichtet ist.',
  },
  'quotes.cancel': {
    title: 'Stornieren',
    short: 'Das Angebot kann nicht mehr angenommen werden; ein Grund ist erforderlich.',
  },
  'quotes.valid_until': {
    title: 'Gültig bis',
    short: 'Letzter Tag, an dem das Angebot angenommen werden kann; zum Senden erforderlich.',
  },
  'quotes.note': { title: 'Notiz an den Händler', short: 'Wird dem Händler im Angebot angezeigt.' },
  'quotes.internal_note': {
    title: 'Interne Notiz',
    short: 'Nur in der Verwaltung; wird dem Händler weder angezeigt noch gedruckt.',
  },
  'quotes.cancel_reason': {
    title: 'Stornierungsgrund',
    short: 'Mindestens 3 Zeichen; wird in der Verwaltung und im Prüfprotokoll gespeichert.',
  },
  'page.ledger': {
    title: 'Händlerkonten',
    short: 'Händlersalden, überfällige Beträge und älteste Fälligkeitsdaten; Import von Anfangssalden.',
  },
  'page.company_ledger': {
    title: 'Händlerkonto',
    short:
      'Saldo, Altersstruktur und Kontoauszug (Übertrag und laufender Saldo); Zahlungen und Korrekturen erfassen.',
  },
  'ledger.search': { title: 'Händler suchen', short: 'Teil des Firmennamens oder Händlercodes.' },
  'ledger.only_overdue': {
    title: 'Nur überfällige',
    short: 'Zeigt Händler mit unbezahlten Beträgen, deren Fälligkeitsdatum überschritten ist.',
  },
  'ledger.import': {
    title: 'Anfangssalden importieren',
    short:
      'Importieren Sie Händlersalden aus Ihrer bisherigen Buchhaltung (CSV/Excel): Händlercode oder Steuernummer, Betrag (Soll positiv, Haben negativ), optional Währung, Datum, Fälligkeitsdatum, Beschreibung. Zuerst Vorschau.',
  },
  'page.ledger_bank_import': {
    title: 'Banktransaktionen',
    short: 'Laden Sie Ihren Kontoauszug hoch und buchen Sie Händlerzahlungen gesammelt auf deren Konten.',
  },
  'ledger.bank_import': {
    title: 'Banktransaktionen hochladen',
    short: 'Bucht Händlerzahlungen aus einem Kontoauszug nach einer Vorschau als Zahlungseingänge.',
  },
  'ledger.bank_import_file': {
    title: 'Kontoauszug',
    short:
      'CSV oder XLSX mit Datum und Betrag (oder Haben/Soll). Ausgehende Transaktionen werden übersprungen.',
  },
  'ledger.bank_import_dealer': {
    title: 'Händler',
    short: 'Prüfen Sie den vorgeschlagenen Händler oder wählen Sie einen aus der Liste.',
  },
  'ledger.bank_import_apply': {
    title: 'Ausgewählte buchen',
    short:
      'Bucht die ausgewählten Transaktionen als Zahlungseingänge per Banküberweisung; eine Vorschau wird nur einmal angewendet.',
  },
  'organization.system_name': {
    title: 'Panelname (Systemname)',
    short:
      'Der Name oben im Verwaltungsbereich, im Browser-Tab und auf der Anmeldeseite. Leer lassen für „Zootopia Panel“.',
  },
  'integrations.verify_all': {
    title: 'Alle Verbindungen prüfen',
    short:
      'Testet alle gespeicherten Verbindungen sofort und zeigt das Ergebnis. Es laufen nur Tests ohne Nachrichten, ohne Zahlungen und ohne Kosten; der Verbindungsstatus ändert sich nicht.',
  },
  'ledger.balance_payments': {
    title: 'Saldozahlungen per Karte',
    short:
      'Kartenzahlungen, die der Händler über „Mein Konto“ geleistet hat; verifizierte Zahlungen werden als Karteneingänge gebucht.',
  },
  'dashboard.dealer_payments': {
    title: 'Letzte Händlerzahlungen',
    short:
      'Kartenzahlungen, die Händler in den letzten 30 Tagen über „Mein Konto → Kontoauszug“ geleistet haben (neueste 10). Jede Zahlung wird automatisch im Händlerkonto gebucht; das Geld geht an Ihr Konto beim Zahlungsanbieter.',
  },
  'dashboard.dealer_payment_email': {
    title: 'E-Mail bei Händlerzahlung',
    short:
      'Wenn aktiv, erhalten die Alarmempfänger (sonst Inhaber und Admins) bei einer Kartenzahlung eines Händlers eine E-Mail mit Betrag und Zahlungsschlüssel. Die Android-Benachrichtigung ist davon nicht betroffen.',
  },
  'ledger.import_apply': {
    title: 'Ins Konto buchen',
    short:
      'Bucht die gültigen Vorschauzeilen als Anfangssalden. Ungültige Zeilen werden übersprungen; eine Datei wird nur einmal angewendet.',
  },
  'ledger.currency': {
    title: 'Währung',
    short: 'Jede Währung hat ein eigenes Konto und einen eigenen Saldo.',
  },
  'ledger.period': {
    title: 'Zeitraum',
    short: 'Anfangs- und Enddatum des Kontoauszugs; frühere Buchungen werden als Übertrag geführt.',
  },
  'ledger.payment': {
    title: 'Zahlung erfassen',
    short:
      'Erfasst vom Händler erhaltenes Geld als Haben-Buchung, verrechnet mit der ältesten fälligen Schuld.',
  },
  'ledger.adjustment': {
    title: 'Korrektur erfassen',
    short: 'Manuelle Soll- oder Haben-Buchung (z. B. Versanddifferenz); eine Beschreibung ist erforderlich.',
  },
  'ledger.reverse': {
    title: 'Stornieren',
    short:
      'Hebt eine manuelle Buchung durch eine Gegenbuchung auf. Es wird nichts gelöscht; Bestellbuchungen folgen der Bestellung.',
  },
  'ledger.statement_csv': {
    title: 'Kontoauszug herunterladen (CSV)',
    short: 'Kontoauszug des ausgewählten Zeitraums.',
  },
  'ledger.print': {
    title: 'Drucken',
    short: 'Drucken Sie den Kontoauszug oder speichern Sie ihn über den Browser als PDF.',
  },
  'ledger.amount': {
    title: 'Betrag',
    short: 'Mit Dezimalstellen (z. B. 1.250,50). Die Richtung ergibt sich aus dem Typ.',
  },
  'ledger.entry_date': {
    title: 'Buchungsdatum',
    short: 'Der Tag der Zahlung oder Korrektur; nicht in der Zukunft.',
  },
  'ledger.method': {
    title: 'Zahlungsart',
    short: 'Banküberweisung, Bargeld, Scheck, Solawechsel, Karte oder Sonstiges.',
  },
  'ledger.reference': { title: 'Belegnr.', short: 'Beleg-, Scheck- oder Wechselnummer (optional).' },
  'ledger.direction': {
    title: 'Richtung',
    short: 'Soll erhöht die Schuld des Händlers; Haben verringert sie.',
  },
  'ledger.due_date': {
    title: 'Fälligkeitsdatum',
    short: 'Wann eine Soll-Korrektur fällig ist; bei leerem Feld das Buchungsdatum.',
  },
  'ledger.open_items': {
    title: 'Offene Posten',
    short:
      'Unbezahlte Beträge, älteste Fälligkeit zuerst. Zahlungen und Erstattungen werden mit der ältesten fälligen Schuld verrechnet. Der Händler sieht dieselbe Liste unter „Mein Konto“.',
  },
  'ledger.reminders': {
    title: 'Zahlungserinnerungen',
    short:
      'Wenn aktiviert, erhält der Händler (Firmen-E-Mail und verantwortliche Personen der Firma) jeden Morgen um 09:10 Uhr eine E-Mail zu bald fälligen oder überfälligen Posten. An jeden Posten wird pro Stufe höchstens einmal erinnert. Standardmäßig deaktiviert.',
  },
  'ledger.reminders_enabled': {
    title: 'Erinnerungen aktivieren',
    short:
      'Durch das Aktivieren werden echte E-Mails an Ihre Händler gesendet; Sie werden gebeten, ein Bestätigungskästchen anzukreuzen. Ohne eingerichteten E-Mail-Versand wird nichts gesendet.',
  },
  'ledger.reminders_days': {
    title: 'Tage vorher',
    short:
      'Wie viele Tage vor dem Fälligkeitsdatum erinnert wird (0–30). Bei 0 wird nur an überfällige Posten erinnert.',
  },
  'ledger.reminders_save': {
    title: 'Speichern',
    short:
      'Speichert die Erinnerungseinstellung. Die Änderung wird im Prüfprotokoll erfasst; die erste Ausführung erfolgt am nächsten Morgen um 09:10 Uhr.',
  },
  'dealers.member_role': {
    title: 'Rolle in der Firma',
    short:
      'Verantwortliche Person: bestellt, sieht Firmenbestellungen, Kontoauszug und Benutzer, verwaltet Standorte. Einkäufer: bestellt und sieht eigene Bestellungen; ein Schwellenwert kann gelten. Nur Ansicht: sieht Preise, Firmenbestellungen und Kontoauszug; kann nicht bestellen. Eine Firma behält mindestens eine verantwortliche Person.',
  },
  'dealers.order_limit': {
    title: 'Bestellschwelle',
    short:
      'Ein Einkäufer kann keine Bestellung über diesem Betrag (inklusive Versand) aufgeben; der Storefront fordert die Genehmigung der verantwortlichen Person an. Leer: kein Schwellenwert. Gilt nicht für in der Verwaltung aufgegebene Bestellungen.',
  },
  'dealers.locations': {
    title: 'Standorte',
    short:
      'Filial-, Liefer- und Rechnungsadressen. Firmenbenutzer wählen an der Kasse eine davon aus; die Bestellung speichert eine Kopie. Die verantwortliche Person der Firma kann Standorte auch unter „Mein Konto“ hinzufügen.',
  },
  'dealers.location_label': {
    title: 'Standortname',
    short:
      'Kurzer Name, der in Listen und an der Kasse angezeigt wird (z. B. „Zentrale“); bis zu 40 Zeichen.',
  },
  'orders.location_fill': {
    title: 'Aus Firmenstandort übernehmen',
    short:
      'Wählen Sie einen der Firmenstandorte des Händlerkunden, um die Lieferadresse auszufüllen; Sie können sie weiterhin bearbeiten.',
  },
  'dealers.credit_limit': {
    title: 'Kreditlimit',
    short:
      'Der Saldo des offenen Kontos zuzüglich einer neuen Bestellung darf diesen Betrag nicht überschreiten. Leer: kein Limit.',
  },
  'dealers.credit_currency': {
    title: 'Limitwährung',
    short:
      'Das Limit gilt für den Saldo in dieser Währung; andere Währungen sind für das offene Konto gesperrt.',
  },
  'dealers.block_when_overdue': {
    title: 'Offenes Konto bei Überfälligkeit sperren',
    short: 'Neue Bestellungen auf offenes Konto sind gesperrt, solange der Händler überfällige Schulden hat.',
  },
  'orders.credit_override': {
    title: 'Grund für die Ausnahme',
    short:
      'Die Bestellung überschreitet das Kreditlimit oder der Händler ist im Zahlungsverzug. Mit Angabe eines Grundes wird die Bestellung trotzdem aufgegeben; dies wird im Prüfprotokoll erfasst.',
  },
  'dealers.payment_term': {
    title: 'Zahlungsziel (Tage)',
    short:
      'Bestellungen auf offenes Konto werden so viele Tage nach dem Bestelldatum fällig. Gilt für neue Bestellungen.',
  },
  'retention.rules': {
    title: 'Regeln für die automatische Löschung',
    short: 'Aufbewahrungsfrist und automatische Löschung pro Datentyp. Standardmäßig alle deaktiviert.',
  },
  'retention.days': { title: 'Aufbewahrungsfrist (Tage)', short: 'Ältere Datensätze werden gelöscht.' },
  'retention.due': {
    title: 'Heute fällig',
    short: 'Datensätze, die mit der gespeicherten Frist heute Nacht gelöscht würden.',
  },
  'retention.enabled': {
    title: 'Automatische Löschung',
    short: 'Wenn aktiviert, werden abgelaufene Datensätze jede Nacht endgültig gelöscht.',
  },
  'retention.save': {
    title: 'Speichern',
    short:
      'Speichert Fristen und den Ein-/Aus-Status. Beim Aktivieren einer Regel wird zuerst eine Bestätigung angefordert.',
  },
  'retention.confirm': {
    title: 'Aktivieren und speichern',
    short: 'Aktivierte Regeln löschen ab heute Nacht abgelaufene Datensätze endgültig.',
  },
  'retention.fixed': {
    title: 'Feste Aufbewahrungsfristen',
    short: 'Eigene Bereinigungsfrist jeder Funktion; wird ebenfalls im Löschprotokoll erfasst.',
  },
  'retention.log': {
    title: 'Löschprotokoll',
    short: 'Wie viele Datensätze welchen Datentyps an welchem Tag gelöscht oder anonymisiert wurden.',
  },
  'retention.log_period': { title: 'Zeitraum', short: 'Letzte 30, 90, 365 oder 1095 Tage.' },
  'page.google_reports': {
    title: 'Google-Berichte',
    short:
      'GA4-Bericht nach Kanal/Tag sowie Standortdetails und Bewertungen aus Google Business Profile für den Shop.',
  },
  'google_reports.ga4': {
    title: 'Analytics-Bericht (GA4)',
    short: 'Wird aus der mit dem Shop verknüpften GA4-Property gelesen.',
  },
  'google_reports.period': { title: 'Zeitraum', short: 'Letzte 7, 28 oder 90 Tage (bis gestern).' },
  'google_reports.business_profile': {
    title: 'Google Business Profile',
    short: 'Standortdetails und die neuesten 50 Bewertungen (nur Lesezugriff).',
  },
  'google_reports.reply': {
    title: 'Auf Bewertungen antworten',
    short: 'Öffnet den Standort in Google Maps, um dort zu antworten.',
  },
  'ads.google_conversions': {
    title: 'Conversion-Upload zu Google Ads',
    short:
      'Sendet bezahlte Bestellungen aus Anzeigen (gclid, Bestellnr., Wert) an Google Ads. Standardmäßig deaktiviert.',
  },
  'ads.google_conversions_enabled': {
    title: 'Conversions hochladen',
    short: 'Erfordert die Genehmigungsberechtigung und Zwei-Faktor-Authentifizierung.',
  },
  'ads.google_conversion_action': {
    title: 'Conversion-Aktion',
    short: 'Ressourcenname customers/<id>/conversionActions/<id>.',
  },
  'ads.google_conversions_save': {
    title: 'Speichern',
    short: 'Speichert die Einstellung; wird im Prüfprotokoll erfasst.',
  },
  'blog.title': { title: 'Beitragstitel', short: 'Wird im Storefront angezeigt (max. 160).' },
  'blog.slug': {
    title: 'Kurzname (Adresse)',
    short:
      '/blog/<short-name>. Bei einem veröffentlichten Beitrag ändert sie sich beim Speichern und die alte Adresse wird weitergeleitet.',
  },
  'blog.excerpt': { title: 'Auszug', short: 'Kurzer Text, der in der Liste angezeigt wird (max. 300).' },
  'blog.category': { title: 'Kategorie', short: 'Optionale Blogkategorie.' },
  'blog.tags': { title: 'Schlagwörter', short: 'Durch Kommas getrennt (max. 10).' },
  'blog.author': { title: 'Autor', short: 'Name und Titel, die im Beitrag angezeigt werden.' },
  'blog.cover': { title: 'Titelbild', short: 'Optional; ein Alternativtext ist erforderlich.' },
  'blog.health_content': {
    title: 'Gesundheitsbezogene Inhalte',
    short: 'Erfordert die Freigabe durch einen Fachprüfer; im Storefront wird ein Hinweis angezeigt.',
  },
  'blog.references': { title: 'Quellenangaben', short: 'Quellen mit optionalen Links (max. 30).' },
  'blog.related': { title: 'Verwandte Produkte', short: 'Werden am Ende des Beitrags angezeigt (max. 8).' },
  'blog.preview': {
    title: 'Vorschau',
    short: 'Speichert den Entwurf und öffnet ihn in einem neuen Tab (signierter Link, 2 Stunden).',
  },
  'blog.save': {
    title: 'Entwurf speichern',
    short: 'Der Storefront ändert sich erst, wenn Sie erneut veröffentlichen.',
  },
  'blog.feature': {
    title: 'Hervorheben',
    short: 'Macht den Beitrag zum empfohlenen Beitrag auf der Artikelseite.',
  },
  'blog.publish': { title: 'Veröffentlichen', short: 'Veröffentlicht den Entwurf als neue Version.' },
  'blog.schedule': { title: 'Veröffentlichungsdatum', short: 'Leer bedeutet: sofort veröffentlichen.' },
  'blog.unpublish': {
    title: 'Veröffentlichung zurückziehen',
    short: 'Entfernt den Beitrag aus dem Storefront.',
  },
  'blog.delete': { title: 'Beitrag löschen', short: 'Löscht einen nie veröffentlichten Beitrag endgültig.' },
  'blog.category_add': { title: 'Kategorie hinzufügen', short: 'Fügt eine Blogkategorie hinzu.' },
  'blog.category_edit': {
    title: 'Kategorie bearbeiten oder löschen',
    short: 'Beim Umbenennen wird die alte Adresse weitergeleitet; Beiträge bleiben erhalten.',
  },
  'menus.post': {
    title: 'Blogbeitrag',
    short: 'Wird nur im Menü angezeigt, solange der Beitrag veröffentlicht ist.',
  },
  'page.product_bulk': {
    title: 'Verlauf der Massenaktionen',
    short:
      'Massenänderungen an Produkten, ihre Ergebnisse und übersprungene Zeilen; angewendete Aktionen können rückgängig gemacht werden.',
  },
  'page.product_grid': {
    title: 'In Tabelle bearbeiten',
    short:
      'Sehen Sie Produkte und Varianten in einer Tabelle und bearbeiten Sie SKU, Barcode, Status, Preis und Bestand wie in einer Tabellenkalkulation.',
  },
  'page.reviews': {
    title: 'Produktbewertungen',
    short:
      'Genehmigen, beantworten oder lehnen Sie Bewertungen von Kunden ab, die das Produkt gekauft und erhalten haben.',
  },
  'page.review_detail': { title: 'Bewertung', short: 'Bewertung genehmigen, beantworten oder ablehnen.' },
  'reviews.enabled': {
    title: 'Bewertungen im Storefront anzeigen',
    short: 'Wenn deaktiviert, werden Bewertungen ausgeblendet und können nicht verfasst werden.',
  },
  'reviews.filter': { title: 'Status', short: 'Ausstehende, veröffentlichte oder abgelehnte Bewertungen.' },
  'reviews.reply': {
    title: 'Antwort des Shops',
    short: 'Wird unter der Bewertung mit dem Shopnamen angezeigt (max. 2000).',
  },
  'reviews.save_reply': {
    title: 'Antwort speichern',
    short: 'Speichert die Antwort; eine leere Antwort entfernt sie.',
  },
  'reviews.publish': {
    title: 'Genehmigen und veröffentlichen',
    short: 'Veröffentlicht die Bewertung auf der Produktseite.',
  },
  'reviews.reject': { title: 'Ablehnungsgrund', short: 'Nur in der Verwaltung sichtbar.' },
  'page.questions': {
    title: 'Produktfragen',
    short: 'Beantworten Sie Kundenfragen und veröffentlichen Sie sie auf Produktseiten.',
  },
  'page.question_detail': { title: 'Frage', short: 'Frage beantworten, veröffentlichen oder ablehnen.' },
  'questions.filter': { title: 'Status', short: 'Unbeantwortete, veröffentlichte oder abgelehnte Fragen.' },
  'questions.answer': {
    title: 'Antwort',
    short: 'Wird mit der Frage unter dem Shopnamen angezeigt (max. 2000).',
  },
  'questions.save': {
    title: 'Antwort speichern',
    short: 'Ist die Frage veröffentlicht, ändert sich der Storefront sofort.',
  },
  'questions.publish': {
    title: 'Veröffentlichen',
    short: 'Zeigt Frage und Antwort auf der Produktseite; der Kunde erhält einmalig eine E-Mail.',
  },
  'questions.reject': {
    title: 'Ablehnen / Veröffentlichung zurückziehen',
    short: 'Blendet die Frage aus; der Grund wird nur in der Verwaltung angezeigt.',
  },
  'content.locale': {
    title: 'Inhaltssprache',
    short: 'Zeigt Inhalte in der ausgewählten Sprache; neue Elemente werden in dieser Sprache erstellt.',
  },
  'content.translate': {
    title: 'Übersetzung hinzufügen',
    short: 'Kopiert den Inhalt als Entwurf in die gewählte Sprache und öffnet den Editor.',
  },
  'content.translate_target': {
    title: 'Zielsprache',
    short: 'Es werden nur Shopsprachen ohne Übersetzung aufgeführt.',
  },
  'blog.category_translation_name': {
    title: 'Kategoriename (Zielsprache)',
    short: 'Der Kategoriename in der Zielsprache; die Adresse wird daraus erzeugt.',
  },
  'menus.translation_draft': {
    title: 'Von einer anderen Sprache ausgehen',
    short:
      'Lädt das Menü aus einer anderen Sprache als ungespeicherten Entwurf und verknüpft Seiten und Beiträge mit ihren Übersetzungen.',
  },
  'pages.hero_text': {
    title: 'Text unter der Überschrift',
    short: 'Kurzer Text unter der großen Überschrift; wird auf gesundheitsbezogene Aussagen geprüft.',
  },
  'pages.grid_category': {
    title: 'Kategorie',
    short: 'Das Raster zeigt sichtbare Produkte aus dieser Kategorie.',
  },
  'pages.post_limit': {
    title: 'Anzahl der Beiträge',
    short: 'Wie viele der neuesten veröffentlichten Beiträge angezeigt werden (1–12).',
  },
  'pages.add_section': {
    title: 'Abschnitt hinzufügen',
    short: 'Fügt am Ende der Seite einen Abschnitt des ausgewählten Typs hinzu.',
  },
  'pages.faq_items': {
    title: 'Frage hinzufügen / entfernen',
    short: 'Fügt im FAQ-Abschnitt ein Frage-Antwort-Paar hinzu oder entfernt es.',
  },
  'pages.review_decision': {
    title: 'Genehmigen / ablehnen',
    short:
      'Der tierärztliche Fachprüfer genehmigt den gespeicherten Inhalt oder lehnt ihn mit Begründung ab.',
  },
  'blog.category_name': {
    title: 'Kategoriename',
    short: 'Wird im Storefront als Titel der Blogkategorie angezeigt.',
  },
  'blog.category_slug': {
    title: 'Kurzname',
    short: 'Adresse der Kategorie; bei einer Änderung wird die alte Adresse weitergeleitet.',
  },
  'blog.category_description': {
    title: 'Kategoriebeschreibung',
    short: 'Kurzer Text oben auf der Kategorieseite.',
  },
  'editor.link_url': {
    title: 'Linkadresse',
    short: 'Ein Pfad der Website, eine https://-Adresse, mailto: oder tel:.',
  },
  'editor.image_alt': { title: 'Alternativtext', short: 'Erforderliche kurze Beschreibung des Bildes.' },
  'editor.image_caption': { title: 'Bildunterschrift', short: 'Optionale Bildunterschrift unter dem Bild.' },
  'editor.table': {
    title: 'Tabelle',
    short:
      'Wenn sich der Cursor in einer Tabelle befindet, können Sie Zeilen und Spalten hinzufügen und löschen. Die erste Zeile ist die Kopfzeile.',
  },
  'editor.video': {
    title: 'Videoadresse',
    short: 'Eine YouTube- oder Vimeo-Adresse. Videos von anderen Websites werden nicht eingebettet.',
  },
  'editor.import': {
    title: 'Markdown oder HTML importieren',
    short:
      'Konvertiert anderswo verfasste Texte; Skripte, Stile, Formulare und externe Bilder werden entfernt.',
  },
  'menus.translation_source': {
    title: 'Ausgangssprache',
    short: 'Die Sprache des Menüs, von dem ausgegangen wird.',
  },
  'categories.name': {
    title: 'Kategoriename',
    short: 'Wird in Menüs, auf der Kategorieseite und in der Breadcrumb-Navigation der Produkte angezeigt.',
  },
  'categories.slug': {
    title: 'Kurzname (Adresse)',
    short: 'Die Adresse der Kategorie; bei einer Änderung leitet die alte Adresse weiter (301).',
  },
  'categories.description': {
    title: 'Kategoriebeschreibung',
    short: 'Wird oben auf der Kategorieseite angezeigt; dient als Ersatz für die Suchbeschreibung.',
  },
  'categories.seo_title': {
    title: 'Titel in Suchergebnissen',
    short: 'Bis zu 70 Zeichen; bei leerem Feld wird der Kategoriename verwendet.',
  },
  'categories.seo_description': {
    title: 'Beschreibung in Suchergebnissen',
    short: 'Bis zu 170 Zeichen; bei leerem Feld wird die Kategoriebeschreibung verwendet.',
  },
  'categories.edit': {
    title: 'Kategorie bearbeiten',
    short: 'Ändert Name, Adresse, Beschreibung und die Felder für Suchergebnisse.',
  },
  'redirects.not_found': {
    title: 'Nicht gefundene Adressen',
    short: 'Listet Storefront-Adressen auf, die 404 zurückgeben, mit der Häufigkeit ihres Aufrufs.',
  },
  'redirects.not_found_filter': { title: 'Anzeigen', short: 'Zeigt ausstehende oder ignorierte Adressen.' },
  'redirects.not_found_actions': {
    title: 'Weiterleiten / 410 / Ignorieren',
    short:
      '„Weiterleiten“ öffnet den Weiterleitungsdialog; „410“ markiert die Adresse als dauerhaft entfernt; „Ignorieren“ blendet sie aus.',
  },
  'redirects.filter_source': {
    title: 'Quelle',
    short:
      'Filtert Regeln nach Herkunft: manuell, Import, automatisch (Adresse geändert) oder Zuordnung (vom System bestätigt).',
  },
  'redirects.filter_host': {
    title: 'Domain',
    short:
      '„Alle Domains“ gilt für die Shop-Domain und alle alten Domains; eine alte Domain zeigt nur ihre eigenen Regeln.',
  },
  'redirects.host': {
    title: 'Quelldomain',
    short:
      'Leer lassen, damit die Regel auf allen Domains gilt (empfohlen); alte Domain (ohne „www.“) für eine Regel nur dort.',
  },
  'redirects.shadowed': {
    title: 'Regeln, die Shopseiten verdecken',
    short:
      'Die alte Adresse dieser Regeln ist eine echte Shopseite; Besucher werden umgeleitet. Regel löschen, um die Seite anzuzeigen.',
  },
  'page.broken_links': {
    title: 'Defekte Links',
    short:
      'Shop-Adressen mit 404 und alte Adressen, die mangels Gegenstück auf eine sichere Seite umgeleitet wurden.',
  },
  'broken_links.collect': {
    title: 'Alte Adressen aus dem Archiv sammeln',
    short:
      'Holt die Seitenadressen der alten Domains aus dem Internet Archive (Wayback Machine) und ordnet sie zu.',
  },
  'broken_links.host': {
    title: 'Domain',
    short: '„Shop-Domain“ ist die eigene Domain; die anderen sind alte Domains.',
  },
  'broken_links.found_via': {
    title: 'Herkunft',
    short:
      'Besuch: Adresse wurde aufgerufen. Archiv: aus der Wayback Machine. Search Console: in Google gesehen.',
  },
  'sc_setup.connect': {
    title: 'Mit Google verbinden und einrichten',
    short:
      'Das System richtet die Search Console ein: Properties der Shop-Domain und der alten Domains werden bestätigt und hinzugefügt, die Sitemap eingereicht und alte Seiten zugeordnet.',
  },
  'sc_setup.run': {
    title: 'Mit Google verbinden / Einrichtung erneut ausführen',
    short:
      'Führt die Einrichtung sofort aus, wenn Verbindung und Berechtigungen vollständig sind; sonst öffnet sich die Google-Zustimmung.',
  },
  'sc_setup.enable_api': {
    title: 'API in Google Cloud aktivieren',
    short:
      'Die Einrichtung nutzt zwei kostenlose Google-APIs: die Bestätigungsschritte die Site Verification API, Hinzufügen der Properties, Sitemap und alte Seiten die Search Console API. Den Link im Google-Cloud-Projekt des verbundenen OAuth-Clients öffnen, „Aktivieren“ drücken und die Einrichtung nach 1–2 Minuten erneut ausführen.',
  },
  'sc_setup.inspections': {
    title: 'Google-Indexstatus',
    short:
      'Jeden Morgen werden aktive Produktseiten und die meistgenutzten alten Adressen mit der Google-URL-Prüfung kontrolliert.',
  },
  'indexnow.section': {
    title: 'IndexNow (Bing, Yandex)',
    short:
      'Meldet geänderte Adressen alle zehn Minuten an Bing und Yandex; beim ersten Lauf werden alle Adressen einmal gesendet.',
  },
  'indexnow.submit': {
    title: 'Alle Adressen jetzt senden',
    short: 'Sendet alle indexierbaren Adressen des Shops an IndexNow.',
  },
  'system.staging_banner': {
    title: 'Staging-Umgebung',
    short:
      'Diese Verwaltung läuft auf dem Staging-Server; E-Mails werden nur an Adressen auf der Zulassungsliste gesendet.',
  },
  'system.mail_allowlist': {
    title: 'E-Mail-Zulassungsliste',
    short:
      'Adressen oder @Domains, die im Staging E-Mails empfangen dürfen. Eine leere Liste blockiert alle E-Mails.',
  },
  'system.mail_allowlist_save': {
    title: 'Zulassungsliste speichern',
    short: 'Speichert die Liste; gilt ab der nächsten E-Mail. Wird im Prüfprotokoll erfasst.',
  },
  'products.media_retry': {
    title: 'Bild erneut versuchen',
    short:
      'Stellt das fehlgeschlagene Importbild erneut in die Warteschlange; der Hintergrundjob versucht es in etwa 30 s erneut.',
  },
  'imports.sku_generate': {
    title: 'Erneute Vorschau mit vorgeschlagenen SKUs',
    short:
      'Schlägt SKUs für Varianten ohne SKU vor (Barcode oder Produktcode + Option). Vor dem Anwenden prüfen.',
  },
  'imports.media_retry_all': {
    title: 'Fehlgeschlagene Downloads wiederholen',
    short: 'Stellt alle fehlgeschlagenen Importbilder erneut in die Warteschlange.',
  },
  'imports.categories_file': {
    title: 'Kategoriedatei auswählen und Vorschau anzeigen',
    short:
      'Spalten: Name, übergeordnete Kategorie, Beschreibung, Reihenfolge, Slug, Status, SEO-Titel/-Beschreibung. Zuerst Vorschau.',
  },
  'imports.categories_apply': {
    title: 'Kategorien übernehmen',
    short:
      'Schreibt alle Neuanlagen und Aktualisierungen in einer Transaktion, übergeordnete Kategorien zuerst.',
  },
  'imports.products_apply': {
    title: 'Produkte importieren',
    short:
      'Legt neue Produkte als Entwurf an und aktualisiert bestehende; mehr als 50 Produkte laufen im Hintergrund.',
  },
  'imports.dealers_apply': {
    title: 'Händler importieren',
    short:
      'Legt die Händler aus der Vorschau in einer Transaktion an oder aktualisiert sie und verknüpft die Benutzer.',
  },

  'imports.categories_export': {
    title: 'Vorlage / Kategorien exportieren',
    short: 'Lädt die Vorlage oder den aktuellen Kategoriebaum mit denselben Spalten herunter.',
  },
  'imports.create_missing_categories': {
    title: 'Fehlende Kategorien anlegen',
    short:
      'Legt Kategorien an, die in der Spalte „Kategorie“ nicht gefunden wurden, einschließlich übergeordneter Kategorien in Pfaden wie „A > B“.',
  },
  'imports.category_mode': {
    title: 'Kategorien vorhandener Produkte',
    short: '„Hinzufügen“ behält die aktuellen Kategorien bei; „Ersetzen“ verwendet nur die in der Datei.',
  },
  'stock.transfer_receipt': {
    title: 'Wareneingang erfassen',
    short:
      'Öffnet statt einer Umlagerung die erste Bestandsbuchung (Wareneingang mit Charge und Mindesthaltbarkeitsdatum).',
  },
  'stores.archive': {
    title: 'Archivieren',
    short:
      'Schließt den Storefront eines nicht genutzten Shops und listet ihn zuletzt auf; es wird nichts gelöscht.',
  },
  'stores.delete': {
    title: 'Shop löschen',
    short:
      'Nur ein Vorschau- oder archivierter Shop ohne Bestellungen; seine Domains, Märkte, Preislisten und Inhalte werden gelöscht.',
  },
  'stores.delete_code': { title: 'Shopcode', short: 'Geben Sie zur Bestätigung den Shopcode exakt ein.' },
  'products.price_list_missing': {
    title: 'Zur Preisgestaltung',
    short: 'Die Storefront-Preise stammen aus der aktiven Basispreisliste des Shops (ohne Gruppe).',
  },
  'payments.card_status': {
    title: 'Kartenzahlung im Storefront',
    short:
      'Aus, Test (Sandbox, ohne echtes Geld) oder Live. Das Abzeichen bei „Integrationen“ zeigt nur den Verbindungstest der Schlüssel.',
  },
  'migration.export': {
    title: 'Migrationspaket herunterladen',
    short: 'Lädt den ausgewählten Shop als ZIP herunter; ohne personenbezogene Daten oder Schlüssel.',
  },
  'migration.target_store': {
    title: 'Zielshop',
    short: 'Shop, auf den angewendet wird; leer: Zuordnung über den Shopcode des Pakets oder Neuanlage.',
  },
  'migration.include_stock': {
    title: 'Anfangsbestand',
    short: 'Schreibt die aktuellen Bestände pro Lager/Charge als Anfangsbestand.',
  },
  'migration.overwrite': {
    title: 'Überschreiben',
    short: 'Aus: Nur fehlende Datensätze werden hinzugefügt. An: Abweichende Datensätze werden aktualisiert.',
  },
  'migration.legal_reviewed': {
    title: 'Anwaltliche Prüfung',
    short: 'Veröffentlicht Rechtstexte mit dem Vermerk „geprüft“ in Ihrem Namen.',
  },
  'migration.carry_approvals': {
    title: 'Tierärztliche Freigaben übernehmen',
    short:
      'Erfasst die Freigaben aus dem Staging in Ihrem Namen; andernfalls warten die Elemente auf Freigabe.',
  },
  'migration.file': {
    title: 'Paket auswählen und Vorschau anzeigen',
    short: 'Lädt die ZIP-Datei hoch und zeigt eine Vorschau an; es wird nichts geschrieben.',
  },
  'migration.refresh': {
    title: 'Vorschau aktualisieren',
    short: 'Berechnet die Vorschau mit den geänderten Optionen neu.',
  },
  'migration.apply': {
    title: 'Importieren',
    short: 'Wendet das in der Vorschau angezeigte Paket in einer Transaktion an.',
  },
  'privacy.create': {
    title: 'Antrag erfassen',
    short: 'Erfasst einen per E-Mail, KEP, Post oder persönlich eingegangenen Antrag.',
  },
  'privacy.requester_email': {
    title: 'E-Mail-Adresse des Antragstellers',
    short:
      'Das Ergebnis wird an diese Adresse gesendet; Konten und Gastbestellungen werden über diese Adresse zugeordnet.',
  },
  'privacy.received_at': { title: 'Antragsdatum', short: 'Die 30-Tage-Frist beginnt mit diesem Datum.' },
  'privacy.export': {
    title: 'Kopie der Daten herunterladen',
    short: 'Lädt die Datensätze der Person als JSON herunter.',
  },
  'privacy.note': {
    title: 'Antwort an die anfragende Person',
    short: 'Wird der anfragenden Person beim Erledigen oder Ablehnen per E-Mail gesendet.',
  },
  'privacy.start_review': { title: 'Prüfung starten', short: 'Markiert die Anfrage als in Bearbeitung.' },
  'privacy.complete': { title: 'Erledigen', short: 'Schließt die Anfrage mit einer Antwort ab.' },
  'privacy.reject': { title: 'Ablehnen', short: 'Schließt die Anfrage mit einer Begründung ab.' },
  'privacy.anonymize': {
    title: 'Anonymisieren',
    short: 'Anonymisiert personenbezogene Daten bei einem Löschantrag dauerhaft.',
  },
  'privacy.confirm_email': {
    title: 'Bestätigung',
    short:
      'Geben Sie die E-Mail-Adresse der anfragenden Person exakt ein, um die Schaltfläche zu aktivieren.',
  },
  'system.error_tracking': {
    title: 'Externe Fehlerüberwachung',
    short:
      'Unerwartete Serverfehler werden an einen Sentry-kompatiblen Dienst gesendet; keine personenbezogenen Daten.',
  },
  'system.alert_recipients': {
    title: 'Empfänger von Warnmeldungen',
    short: 'Adressen für kritische Warnmeldungen; wenn leer, die Inhaber.',
  },
  'system.mail_dns': {
    title: 'Prüfung der E-Mail-Domain',
    short: 'SPF, DKIM und DMARC werden aus dem DNS gelesen.',
  },
  'system.dkim_selector': { title: 'DKIM-Selektor', short: 'Der erste Teil des Namens des DKIM-Eintrags.' },
  'page.exports': {
    title: 'Exporte',
    short:
      'Laden Sie Bestellungen, Bestellpositionen, Kunden, Bestand, Rechnungen und Zahlungen als XLSX oder CSV herunter.',
  },
  'exports.period': {
    title: 'Zeitraum',
    short: 'Tage in türkischer Zeit; der Endtag ist eingeschlossen. Der Bestand ist immer aktuell.',
  },
  'exports.from': { title: 'Startdatum', short: 'Datensätze ab 00:00 Uhr dieses Tages (türkische Zeit).' },
  'exports.to': { title: 'Enddatum', short: 'Datensätze bis zum Ende dieses Tages.' },
  'exports.store': {
    title: 'Shop',
    short: 'Beschränkt Bestellungen, Rechnungen und Zahlungen auf einen Shop.',
  },
  'exports.format': {
    title: 'Dateiformat',
    short: 'XLSX behält Beträge als Zahlen bei; CSV ist durch Semikolons getrennt.',
  },
  'exports.personal_data': {
    title: 'Personenbezogene Daten',
    short:
      'Standardmäßig maskiert. Für unmaskierte Daten sind die Berechtigung für personenbezogene Daten und die Zwei-Faktor-Authentifizierung erforderlich.',
  },
  'exports.orders': {
    title: 'Bestellungen herunterladen',
    short: 'Eine Zeile pro Bestellung mit Summen und MwSt.',
  },
  'exports.order_lines': {
    title: 'Bestellpositionen herunterladen',
    short: 'Eine Zeile pro Artikel mit MwSt.-Satz und Chargen.',
  },
  'exports.customers': {
    title: 'Kunden herunterladen',
    short: 'Im Zeitraum registrierte Kunden mit ihren Einwilligungen.',
  },
  'exports.stock': {
    title: 'Bestand herunterladen',
    short: 'Aktueller Bestand nach Lager und Charge, mit Mindesthaltbarkeitsdaten.',
  },
  'exports.invoices': {
    title: 'Rechnungen herunterladen',
    short: 'Im Zeitraum datierte Rechnungen mit Netto, MwSt. und Gesamtbetrag.',
  },
  'exports.payments': {
    title: 'Zahlungen herunterladen',
    short: 'Zahlungen und Erstattungen (negativ) in Datumsreihenfolge.',
  },
  'stock.critical': {
    title: 'Niedriger Bestand',
    short: 'Produkte, deren verkaufbare Menge ihren Schwellenwert erreicht oder unterschreitet.',
  },
  'stock.default_threshold': {
    title: 'Standard-Schwellenwert',
    short: 'Gilt für Produkte ohne eigenen Schwellenwert, die mindestens einmal auf Lager waren.',
  },
  'stock.daily_digest': {
    title: 'Tägliche E-Mail-Zusammenfassung',
    short: 'Jeden Morgen um 08:00 Uhr (türkische Zeit) eine E-Mail, wenn der Bestand niedrig ist.',
  },
  'stock.thresholds': {
    title: 'Schwellenwerte für niedrigen Bestand',
    short: 'Alle Lager (gesamt) oder pro Lager; leer bedeutet kein Schwellenwert.',
  },
  'stock.thresholds_save': {
    title: 'Schwellenwerte speichern',
    short: 'Speichert die Schwellenwerte dieses Produkts; die Liste wird sofort aktualisiert.',
  },
  'page.seo': {
    title: 'SEO-Center',
    short: 'SEO-Probleme veröffentlichter Produkte, Kategorien, Seiten und Beiträge.',
  },
  'seo.indexing': {
    title: 'Indexierung',
    short: 'Für Suchmaschinen geöffnet, wenn der Shop live und die Indexierung aktiviert ist.',
  },
  'seo.issues': {
    title: 'SEO-Probleme',
    short:
      'Nur veröffentlichte Inhalte werden geprüft. Hauptbilder unter 500 px (kurze Seite) gelten als klein, unter 1500 px als niedrig aufgelöst.',
  },
  'seo.issue_filter': { title: 'Problemtyp', short: 'Filtert die Liste nach einem Problemtyp.' },
  'seo.history': {
    title: 'SEO-Änderungsverlauf',
    short: 'Änderungen an SEO-Titeln, -Beschreibungen und Slugs.',
  },
  'seo.crawl': {
    title: 'Website-Crawler',
    short: 'Durchsucht Ihre eigene Storefront und findet technische SEO-Probleme.',
  },
  'seo.crawl_start': {
    title: 'Crawl starten',
    short:
      'Stellt einen Crawl in die Warteschlange; pro Sekunde wird eine Seite abgerufen, 150 Seiten dauern also einige Minuten.',
  },
  'seo.crawl_filter': {
    title: 'Problemtyp (Crawl)',
    short: 'Filtert die Probleme des letzten Crawls nach Typ.',
  },
  'page.google': {
    title: 'Google-Verbindungen',
    short:
      'Verbindet Ihr Google-Konto und wählt pro Shop die Ressourcen für Search Console, GA4 und Merchant Center aus.',
  },
  'google.client': {
    title: 'OAuth-Client',
    short: 'Wird in der Google Cloud Console erstellt und unter Integrationen eingetragen.',
  },
  'google.redirect_uri': {
    title: 'Autorisierter Weiterleitungs-URI',
    short: 'Fügen Sie genau diese Adresse dem OAuth-Client in Google Cloud hinzu.',
  },
  'google.connect': {
    title: 'Mit Google verbinden',
    short: 'Öffnet den Einwilligungsbildschirm von Google für die ausgewählten Dienste.',
  },
  'google.services': {
    title: 'Dienste und Berechtigungen',
    short: 'Search Console, Analytics (nur Lesezugriff), Merchant Center.',
  },
  'google.test': {
    title: 'Verbindung testen',
    short: 'Aktualisiert das Token und prüft die verknüpften Ressourcen.',
  },
  'google.disconnect': {
    title: 'Verbindung entfernen',
    short: 'Widerruft den Zugriff bei Google und löscht das Token und die Verknüpfungen.',
  },
  'google.resources': {
    title: 'Shop-Ressourcen',
    short: 'Ausgewählt aus den Ressourcen, auf die das verbundene Konto zugreifen kann.',
  },
  'google.logs': { title: 'Protokoll', short: 'Verbindungs-, Token-, Listen-, Test- und Widerrufsvorgänge.' },
  'page.search_console': {
    title: 'Suchleistung',
    short:
      'Klicks, Impressionen, CTR und durchschnittliche Position in der Google-Suche nach Suchanfrage, Seite, Land und Gerät.',
  },
  'sc.sync': {
    title: 'Jetzt synchronisieren',
    short: 'Ruft beim ersten Mal die letzten 28 Tage ab, danach die letzten 5 Tage.',
  },
  'sc.metrics': {
    title: 'Kennzahlen',
    short: 'Klicks, Impressionen, Klickrate und durchschnittliche Position.',
  },
  'sc.dimension': {
    title: 'Detailtabelle',
    short: 'Die 100 Zeilen mit den meisten Klicks; filtern Sie mit „Suchen“.',
  },
  'sc.movers': {
    title: 'Steigende und fallende Seiten',
    short: 'Klicks im Vergleich zum vorherigen, gleich langen Zeitraum.',
  },
  'sc.sitemaps': { title: 'Sitemaps', short: 'Sitemaps in der Search Console und ihr Status.' },
  'sc.sitemap_submit': {
    title: 'Sitemap einreichen',
    short: 'Reicht den Sitemap-Index des Shops bei der Search Console ein.',
  },
  'sc.inspect': {
    title: 'URL-Prüfung',
    short: 'Google-Indexstatus einer Seite; 2.000 pro Property und Tag.',
  },
  'sc.digest': {
    title: 'Wöchentliche E-Mail-Zusammenfassung',
    short: 'Wird jeden Montag an die Empfänger von Warnmeldungen gesendet.',
  },
  'page.merchant': {
    title: 'Google Shopping',
    short: 'Sendet Produkte an Google Merchant Center: Vorabprüfung, Feed-Datei und Merchant API.',
  },
  'merchant.preview': {
    title: 'Vorabprüfung des Feeds',
    short:
      'Produkte mit Problemen bei gesundheitsbezogenen Inhalten, ohne Bild oder ohne Preis werden nicht gesendet. Bilder unter der Mindestgröße von Google werden übersprungen; empfohlen sind mindestens 1500×1500 px.',
  },
  'merchant.feed': {
    title: 'Feed-Datei',
    short: 'Eine geheime Adresse für den geplanten Abruf durch Merchant Center.',
  },
  'merchant.feed_rotate': {
    title: 'Adresse erneuern',
    short: 'Erstellt eine neue Adresse; die alte funktioniert nicht mehr.',
  },
  'merchant.api': {
    title: 'Merchant API',
    short: 'Sendet Produkte jeden Morgen über die Google-Verbindung.',
  },
  'merchant.sync': {
    title: 'Jetzt senden',
    short: 'Sendet geänderte Produkte, löscht entfernte und aktualisiert Status.',
  },
  'merchant.performance': {
    title: 'Produktleistung',
    short:
      'Merchant-Center-Bericht: Impressionen und Klicks aus kostenlosen Einträgen und Shopping-Anzeigen pro Produkt.',
  },
  'merchant.performance_period': { title: 'Zeitraum', short: 'Letzte 7, 28 oder 90 Tage (bis gestern).' },
  'merchant.issues': {
    title: 'Probleme bei Google',
    short: 'Abgelehnte oder mit Warnungen versehene Produkte mit den Gründen von Google.',
  },
  'products.merchant': {
    title: 'Google Shopping',
    short: 'Ob das Produkt an Merchant Center gesendet wird, sowie die Google-Felder.',
  },
  'products.google_category': {
    title: 'Google-Produktkategorie',
    short: 'Nummer oder vollständiger Pfad in der Google-Taxonomie.',
  },
  'products.identifier_exists': {
    title: 'Hersteller-Barcode',
    short: 'Wählen Sie „kein Barcode“ nur, wenn das Produkt keinen hat.',
  },
  'products.sku_is_mpn': {
    title: 'SKU = MPN',
    short: 'Nur für Ihre eigenen Produkte aktivieren: Die SKU wird als MPN an Google gesendet.',
  },
  'products.custom_labels': {
    title: 'Benutzerdefinierte Labels',
    short: 'Freie Labels zum Gruppieren von Produkten in Google Ads.',
  },
  'page.attribution': {
    title: 'Kampagnen und Quellen',
    short: 'Erstellt UTM-Kampagnenlinks und wertet Bestellungen nach Quelle aus.',
  },
  'attribution.builder': {
    title: 'Kampagnenlink',
    short: 'Quelle, Medium und Kampagne; der Link wird nur auf dem Bildschirm erstellt.',
  },
  'attribution.url': {
    title: 'Seitenadresse',
    short: 'Die Seite der Storefront, auf der der Besucher landet.',
  },
  'attribution.source': { title: 'Quelle', short: 'Woher der Traffic kommt: google, instagram, newsletter…' },
  'attribution.medium': { title: 'Medium', short: 'Art des Traffics: cpc, social, email, sms, qr…' },
  'attribution.campaign': { title: 'Kampagne', short: 'Kampagnenname; die Zeilen werden danach gruppiert.' },
  'attribution.term': { title: 'Keyword', short: 'Optional: Keyword der bezahlten Suche.' },
  'attribution.content': {
    title: 'Inhalt',
    short: 'Optional: unterscheidet Links innerhalb derselben Kampagne.',
  },
  'attribution.report': {
    title: 'Bestellungen nach Quelle',
    short: 'Bestellungen, bezahlter Umsatz, Erstattungen und Netto nach Quelle.',
  },
  'attribution.model': {
    title: 'Kontaktpunkt',
    short: 'Letzter Kontakt: letzte nicht direkte Quelle vor der Bestellung. Erster Kontakt: erster Besuch.',
  },
  'page.web_vitals': {
    title: 'Seitengeschwindigkeit',
    short:
      'Misst die Geschwindigkeit der Storefront mit PageSpeed Insights (Core Web Vitals und Lighthouse-Score).',
  },
  'web_vitals.run': {
    title: 'Jetzt messen',
    short: 'Acht Messungen (vier Seiten × Mobil/Desktop); dauert einige Minuten.',
  },
  'web_vitals.results': {
    title: 'Ergebnisse',
    short: 'LCP: Ladezeit des größten Inhalts. INP: Reaktion auf Klicks. CLS: Layoutverschiebung.',
  },
  'page.attributes': {
    title: 'Produktattribute',
    short: 'Technische Attributdefinitionen (Zahl, Option, Ja/Nein, Text) und verwendete Tags.',
  },
  'attributes.list': {
    title: 'Attributliste',
    short: 'Typ, Optionen, Anzeigeort und Produkte mit einem Wert.',
  },
  'attributes.code': {
    title: 'Kurzcode',
    short: 'Wird als Importspalte und als Filteradresse in der Storefront verwendet.',
  },
  'attributes.type': {
    title: 'Attributtyp',
    short: 'Kann nicht mehr geändert werden, sobald Produkte Werte haben.',
  },
  'attributes.options': {
    title: 'Optionen',
    short: 'Eine pro Zeile; von Produkten verwendete Optionen können nicht entfernt werden.',
  },
  'attributes.visible': {
    title: 'Auf der Produktseite anzeigen',
    short: 'Wird in der Tabelle „Technische Daten“ und in den Produktdaten für Suchmaschinen angezeigt.',
  },
  'attributes.filterable': {
    title: 'In den Filtern der Storefront anzeigen',
    short: 'Ermöglicht Kunden, danach zu filtern.',
  },
  'attributes.position': { title: 'Reihenfolge', short: 'Kleinere Zahlen erscheinen zuerst.' },
  'products.tags': {
    title: 'Tags',
    short:
      'Durch Kommas getrennt, in Kleinbuchstaben gespeichert; in der Storefront nicht sichtbar. Werden von Kollektionsregeln und Filtern verwendet.',
  },
  'products.copy': {
    title: 'Kopieren',
    short:
      'Erstellt eine Entwurfskopie mit neuen SKUs; Shop-Veröffentlichung und Bestand werden nicht kopiert.',
  },
  'products.save_template': {
    title: 'Als Vorlage speichern',
    short: 'Speichert wiederverwendbare Felder dieses Produkts als Vorlage.',
  },
  'products.template': {
    title: 'Vorlage',
    short: 'Füllt das Formular für ein neues Produkt mit den Werten der Vorlage aus.',
  },
  'products.sku_suggest': {
    title: 'SKUs vorschlagen',
    short: 'Füllt leere SKUs anhand des SKU-Musters aus; immer eindeutig.',
  },
  'products.sku_pattern': {
    title: 'SKU-Muster',
    short: 'Platzhalter: {marka} {kategori} {urun} {secenek} {sira}.',
  },
  'products.schedule': {
    title: 'Veröffentlichung planen',
    short: 'Veröffentlicht das Produkt in diesem Shop zu einem späteren Zeitpunkt.',
  },
  'products.bulk': {
    title: 'Massenaktion',
    short:
      'Ändert nach einer Vorschau Status, Kategorien, Tags, Kollektion, Bestand, SEO, Google Shopping oder Shop-Veröffentlichung für viele Produkte gleichzeitig.',
  },
  'products.bulk_select_all': {
    title: 'Alle Treffer auswählen',
    short:
      'Wählt alle Produkte aus, die der Suche und den Filtern entsprechen (bis zu 5.000), nicht nur diese Seite.',
  },
  'products.bulk_kind': {
    title: 'Aktion',
    short:
      'Die vorzunehmende Änderung. Für das Anwenden sind die Berechtigung für Massenaktionen und die Zwei-Faktor-Authentifizierung erforderlich.',
  },
  'products.bulk_store': {
    title: 'Shop',
    short:
      'Der Shop, in dem veröffentlicht oder aus dem die Veröffentlichung zurückgezogen wird. Nur aktive, (wo erforderlich) tierärztlich freigegebene Produkte mit Grundpreis werden veröffentlicht; andere werden mit Begründung übersprungen.',
  },
  'products.bulk_stock': {
    title: 'Bestandsmenge',
    short:
      'Setzen Sie jede aktive Variante auf eine Anzahl oder fügen Sie Einheiten hinzu bzw. entfernen Sie sie. Wird als Bestandsbewegungen erfasst.',
  },
  'products.bulk_seo': {
    title: 'SEO-Muster',
    short: 'Platzhalter: {urun} Produkt, {marka} Marke, {kategori} erste Kategorie.',
  },
  'products.bulk_preview': {
    title: 'Vorschau',
    short:
      'Zeigt Werte vorher und nachher sowie übersprungene Produkte mit Begründung. Es wird nichts geändert.',
  },
  'products.bulk_apply': {
    title: 'Anwenden',
    short:
      'Führt die in der Vorschau gezeigten Änderungen aus; seit der Vorschau geänderte Zeilen bleiben unberührt.',
  },
  'products.bulk_history': {
    title: 'Verlauf der Massenaktionen',
    short:
      'Die letzten 50 Massenaktionen mit ihren Ergebnissen; angewendete Aktionen können rückgängig gemacht werden.',
  },
  'products.bulk_revert': {
    title: 'Rückgängig machen',
    short: 'Stellt die vorherigen Werte wieder her; seitdem manuell geänderte Zeilen werden übersprungen.',
  },
  'products.grid': {
    title: 'In Tabelle bearbeiten',
    short:
      'Öffnet Produkte als Variantenzeilen, um SKU, Barcode, Status, Preis und Bestand wie in einer Tabellenkalkulation zu bearbeiten.',
  },
  'products.grid_price_list': {
    title: 'Preisliste',
    short: 'Die Preisspalte zeigt den Einzelstückpreis in dieser Liste und ermöglicht seine Bearbeitung.',
  },
  'products.grid_warehouse': {
    title: 'Lager',
    short:
      'Die Bestandsspalte zeigt die vorhandenen Einheiten in diesem Lager und ermöglicht ihre Bearbeitung.',
  },
  'products.grid_save': {
    title: 'Änderungen speichern',
    short:
      'Zeigt zuerst eine Vorschau mit Konflikten und ungültigen Zellen; gespeicherte Änderungen können rückgängig gemacht werden.',
  },
  'products.grid_discard': { title: 'Verwerfen', short: 'Entfernt alle nicht gespeicherten Zelländerungen.' },
  'stock.lot_pick': {
    title: 'Charge',
    short:
      'Wählen Sie die Charge, für die die Zählung, Korrektur oder Beschädigung gilt; „Bestand ohne Charge“ für den chargenlosen Bestand.',
  },
  'imports.column_map': {
    title: 'Spaltenzuordnung',
    short:
      'Welchem Feld eine Spalte zugeordnet wird; wird beim Anwenden gespeichert und für dieselben Spaltenüberschriften wiederverwendet.',
  },
  'imports.retry': {
    title: 'Fehlgeschlagene Produkte erneut versuchen',
    short: 'Wendet nur die fehlgeschlagenen Produkte erneut an.',
  },
  'pricing.cost_based': {
    title: 'Kostenbasierter Preis',
    short: 'Bildet Preise aus den Kosten mit einem Aufschlag oder einer Ziel-Bruttomarge.',
  },
  'pricing.min_margin': {
    title: 'Mindestmarge',
    short: 'Markiert (oder überspringt) Produkte, deren Marge darunter fallen würde.',
  },
  'pricing.fx_rate': {
    title: 'Kurs',
    short: 'Rechnet den Preis der Quellliste mit diesem manuell eingegebenen Kurs um.',
  },
  'pricing.campaign_dates': {
    title: 'Gültigkeitszeitraum',
    short: 'Die Liste liefert nur innerhalb dieses Zeitraums Preise.',
  },
  'products.price_tiers': { title: 'Mengenpreise', short: 'Stückpreis ab einer Mindestmenge.' },
  'products.extras': {
    title: 'Videos, Dokumente und ergänzende Produkte',
    short: 'Videos, PDF-Dokumente und „Passt gut dazu“-Produkte, die auf der Produktseite angezeigt werden.',
  },
  'products.videos': {
    title: 'Videos',
    short:
      'Fügen Sie eine YouTube- oder Vimeo-Adresse und einen Titel ein (bis zu 5). Es wird nichts geladen, bis der Besucher das Video abspielt.',
  },
  'products.video_add': {
    title: 'Video hinzufügen',
    short: 'Fügt eine Videozeile hinzu. Geben Sie Adresse und Titel ein und speichern Sie dann.',
  },
  'products.documents': {
    title: 'Dokumente',
    short: 'PDF-Dokumente wie ein Analysezertifikat (bis zu 10), öffentlich auf der Produktseite verlinkt.',
  },
  'products.document_upload': {
    title: 'PDF-Dokument hochladen',
    short: 'Bis zu 10 MB. PDFs mit JavaScript, eingebetteten Dateien oder Startaktionen werden abgelehnt.',
  },
  'products.complementary': {
    title: 'Ergänzende Produkte',
    short: 'Zusammen mit diesem Produkt empfohlene Produkte (bis zu 12), angezeigt als „Passt gut dazu“.',
  },
  'products.attributes': {
    title: 'Produktattribute',
    short: 'Werte der definierten Attribute für dieses Produkt; leere Werte werden nicht gespeichert.',
  },
  'page.collections': { title: 'Kollektionen', short: 'Handverlesene oder regelbasierte Produktgruppen.' },
  'page.collection_editor': {
    title: 'Kollektion',
    short: 'Name, Adresse, Status und Produkte einer Kollektion.',
  },
  'collections.slug': {
    title: 'Slug',
    short: 'Storefront-Adresse /koleksiyon/<slug>; die alte Adresse wird weitergeleitet.',
  },
  'collections.status': {
    title: 'Status',
    short: 'Ausgeblendete Kollektionen werden in der Storefront nicht angezeigt.',
  },
  'collections.kind': {
    title: 'Handverlesen oder Regel',
    short: 'Regelbasierte Kollektionen aktualisieren sich selbst.',
  },
  'collections.manual': {
    title: 'Produkt hinzufügen',
    short: 'Suche nach Name, SKU oder Barcode; bis zu 500 Produkte.',
  },
  'collections.match': {
    title: 'Verknüpfung der Bedingungen',
    short: 'Alle Bedingungen oder eine beliebige davon.',
  },
  'collections.preview': {
    title: 'Treffer anzeigen',
    short: 'Listet passende Produkte auf, ohne zu speichern.',
  },
  'page.site_search': {
    title: 'Website-Suche',
    short: 'Häufigste Suchen und Suchen ohne Ergebnis; Synonymgruppen.',
  },
  'site_search.report': {
    title: 'Suchbericht',
    short:
      'Gezählt wird der erste Aufruf einer Suchseite, Vorschläge nicht. Keine personenbezogenen Daten; Aufbewahrung 90 Tage.',
  },
  'site_search.zero': {
    title: 'Suchen ohne Ergebnisse',
    short: 'Fügen Sie ein Synonym hinzu oder nehmen Sie das Wort in die Produktnamen auf.',
  },
  'site_search.synonyms': {
    title: 'Synonyme',
    short: 'Bei jedem Wort einer Gruppe wird auch nach den anderen gesucht.',
  },
  'site_search.terms': { title: 'Wörter', short: 'Zwei bis 20 einzelne Wörter, durch Kommas getrennt.' },
  'page.keywords': {
    title: 'Keywords',
    short: 'Pool von Zielsuchanfragen mit Search-Console-Kennzahlen und Warnungen.',
  },
  'keywords.pool': {
    title: 'Keyword-Pool',
    short: 'In Kleinbuchstaben gespeichert; ein Eintrag pro Keyword. Letzte 28 Tage aus der Search Console.',
  },
  'keywords.alerts': { title: 'Warnungen', short: 'Kannibalisierung und Inhaltslücken.' },
  'keywords.intent': {
    title: 'Suchintention',
    short: 'Informativ, kommerziell, transaktional oder navigational.',
  },
  'keywords.cluster': { title: 'Cluster', short: 'Gruppiert Keywords zum selben Thema.' },
  'keywords.target': {
    title: 'Zielseite',
    short: 'Der Storefront-Pfad, der für dieses Keyword ranken soll.',
  },
  'keywords.candidates': {
    title: 'Search-Console-Vorschläge',
    short: 'Die 100 häufigsten Suchanfragen, die noch nicht im Pool sind.',
  },
  'keywords.rank_tracking': {
    title: 'Ranking-Tracking',
    short: 'Erfordert einen lizenzierten (kostenpflichtigen) Datenanbieter.',
  },
  'page.tracking': {
    title: 'Messung',
    short:
      'Fügt der Storefront GTM-, GA4-, Google-Ads-, Meta- und TikTok-Tags hinzu und verwaltet die Cookie-Einwilligung.',
  },
  'tracking.status': {
    title: 'Storefront-Status',
    short: 'Aktiv, wenn ein Tag festgelegt und die Cookie-Richtlinie veröffentlicht ist.',
  },
  'tracking.gtm': {
    title: 'Google Tag Manager-ID',
    short: 'GTM-XXXXXXX. Wird erst nach Einwilligung geladen.',
  },
  'tracking.ga4': {
    title: 'GA4-Mess-ID',
    short: 'G-XXXXXXXXXX. Wird nur mit Einwilligung in Analyse geladen.',
  },
  'tracking.google_ads': {
    title: 'Google-Ads-ID',
    short: 'AW-123456789. Wird nur mit Einwilligung in Marketing geladen.',
  },
  'tracking.google_ads_label': {
    title: 'Conversion-Label für Käufe',
    short: 'Der Teil nach „AW-…/“ in den Einstellungen des Conversion-Tags.',
  },
  'tracking.meta': {
    title: 'Meta-Pixel-ID',
    short: 'Nur Ziffern. Wird nur mit Einwilligung in Marketing geladen.',
  },
  'tracking.tiktok': { title: 'TikTok-Pixel-ID', short: 'Wird nur mit Einwilligung in Marketing geladen.' },
  'tracking.server_events': {
    title: 'Serverseitige Ereignisse',
    short:
      'Bezahlte Bestellungen und Erstattungen werden unter Beachtung der Einwilligung vom Server an GA4, Meta und TikTok gesendet.',
  },
  'tracking.server_ga4': {
    title: 'GA4 Measurement Protocol',
    short: 'GA4 → Verwaltung → Datenstreams → Measurement Protocol API-Secrets → Erstellen.',
  },
  'tracking.server_meta': {
    title: 'Meta Conversions API',
    short: 'Events Manager → Pixel → Einstellungen → Conversions API → Zugriffsschlüssel generieren.',
  },
  'tracking.server_tiktok': {
    title: 'TikTok Events API',
    short:
      'TikTok Ads Manager → Ereignisse → Web-Ereignisse → Pixel → Einstellungen → Zugriffstoken generieren.',
  },
  'tracking.server_test_code': {
    title: 'Testereignis-Code',
    short: 'Wird nur von der Schaltfläche „Testereignis senden“ verwendet.',
  },
  'tracking.server_test': {
    title: 'Testereignis senden',
    short: 'GA4: Validierungsendpunkt; Meta/TikTok: ein Seitenaufruf mit dem Testcode.',
  },
  'tracking.server_user_data': {
    title: 'Abgleich personenbezogener Daten',
    short: 'E-Mail und Telefonnummer werden als SHA-256-Hashes gesendet. Standardmäßig deaktiviert.',
  },
  'tracking.server_log': {
    title: 'Server-Ereignisprotokoll',
    short:
      'Gesendete Daten, Status und Anbieterfehler; fehlgeschlagene Ereignisse können erneut gesendet werden.',
  },
  'tracking.events': {
    title: 'Ereignismanager',
    short:
      'Wählen Sie, welche Standardereignisse an welchen Dienst gehen, und definieren Sie benutzerdefinierte Ereignisse. Die Einwilligungsregeln bleiben unverändert.',
  },
  'tracking.events_standard': {
    title: 'Standardereignisse',
    short:
      'Entfernen Sie das Häkchen, um dieses Ereignis nicht mehr an diesen Dienst zu senden. Ereignisse ohne Meta/TikTok-Entsprechung werden nie gesendet (—).',
  },
  'tracking.custom_events': {
    title: 'Benutzerdefinierte Ereignisse',
    short:
      'Bis zu 20 benutzerdefinierte Ereignisse: ein Seitenaufruf oder Klicks auf Telefon-, E-Mail-, WhatsApp- oder ausgehende Links. Keine personenbezogenen Daten.',
  },
  'tracking.custom_event_name': {
    title: 'Ereignisname',
    short:
      'Beginnt mit einem Kleinbuchstaben; a–z, Ziffern und Unterstrich, 3–40 Zeichen. Standard- und reservierte Namen sind nicht erlaubt.',
  },
  'tracking.custom_event_trigger': { title: 'Wann', short: 'Seitenaufruf oder Klick auf einen Linktyp.' },
  'tracking.custom_event_path': {
    title: 'Seitenpfad',
    short: 'Exakter Pfad oder mit * am Ende, um den Anfang abzugleichen.',
  },
  'tracking.custom_event_destinations': {
    title: 'Senden an',
    short: 'Dienste, die das Ereignis erhalten (nur bei festgelegtem Tag und Einwilligung des Besuchers).',
  },
  'tracking.custom_event_add': {
    title: 'Zur Liste hinzufügen',
    short: 'Fügt das Ereignis hinzu; speichern Sie, um es in der Storefront anzuwenden.',
  },
  'tracking.custom_event_remove': {
    title: 'Ereignis entfernen',
    short: 'Entfernt das Ereignis; speichern Sie, um die Änderung anzuwenden.',
  },
  'tracking.events_save': {
    title: 'Ereigniseinstellungen speichern',
    short: 'Übernimmt die Ereignisauswahl und die benutzerdefinierten Ereignisse.',
  },
  'tracking.save': { title: 'Tags speichern', short: 'Ein leeres Feld entfernt das jeweilige Tag.' },
  'tracking.legal': {
    title: 'Auswirkung auf Rechtstexte',
    short: 'Cookies und Empfänger der konfigurierten Tags, abgeglichen mit den veröffentlichten Texten.',
  },
  'tracking.stats': {
    title: 'Statistik der Cookie-Einwilligungen',
    short: 'Entscheidungen der letzten 30 Tage (jeweils letzte Entscheidung pro Browser).',
  },
  'stores.market_tax_mode': {
    title: 'Steuermodus',
    short:
      'Welche MwSt. für Bestellungen in diesem Markt gilt: inländische MwSt., Ausfuhrbefreiung (keine MwSt.) oder MwSt. des Bestimmungslandes.',
  },
  'pricing.tax_country_rate': {
    title: 'MwSt.-Sätze nach Land',
    short:
      'Der Satz einer Steuerklasse in einem Land; angewendet in Märkten mit dem Steuermodus „MwSt. des Bestimmungslandes“.',
  },
  'shipping.countries': {
    title: 'Zielländer',
    short:
      'Länder des Marktes, in denen die Methode angeboten wird; ist keines ausgewählt, wird sie überall angeboten.',
  },
  'products.customs': {
    title: 'Zoll und Export',
    short: 'HS-Code, Ursprungsland und Zollbeschreibung für die Proforma-Rechnung und die Zollanmeldung.',
  },
  'products.hs_code': { title: 'HS-Code (GTİP)', short: 'Zolltarifposition der Ware (6–12 Ziffern).' },
  'products.origin_country': { title: 'Ursprungsland', short: 'Land, in dem die Ware hergestellt wurde.' },
  'products.customs_description': {
    title: 'Zollbeschreibung',
    short: 'Kurze, klare Warenbeschreibung für die Zollanmeldung.',
  },
  'orders.tax_exemption': {
    title: 'Export – MwSt.-befreit',
    short:
      'Die Bestellung stammt aus einem Markt mit dem Steuermodus Export; es wurde keine MwSt. berechnet.',
  },
  'orders.proforma': {
    title: 'Proforma-Rechnung',
    short: 'Druckbares Bestelldokument für den Zoll und den Käufer; keine Handelsrechnung.',
  },
  'payments.bank_account_currency': {
    title: 'Kontowährung',
    short: 'Banküberweisung wird mit diesem Konto nur für Bestellungen in dieser Währung angeboten.',
  },
  'page.order_proforma': {
    title: 'Proforma-Rechnung',
    short: 'Proforma-Rechnung der Bestellung; drucken oder als PDF speichern.',
  },
  'products.translations': {
    title: 'Übersetzungen',
    short:
      'Produktname, Beschreibungen und SEO-Felder in anderen Sprachen, veröffentlicht unter Adressen im Stil /en/….',
  },
  'translations.seo': {
    title: 'SEO-Felder in einer Übersetzung',
    short:
      'Titel (70 Zeichen) und Beschreibung (170 Zeichen), die in den Suchergebnissen in dieser Sprache angezeigt werden.',
  },
  'keywords.language': {
    title: 'Keyword-Sprache',
    short: 'Die Sprache, in der nach dem Keyword gesucht wird; der Pool kann nach Sprache gefiltert werden.',
  },
  'merchant.markets': {
    title: 'An Google gesendete Märkte',
    short:
      'Jeder Markt (Land + Sprache + Währung) ist ein eigener Feed und eine eigene Merchant-API-Datenquelle.',
  },
  'payments.intl': {
    title: 'Auslandszahlungen (Stripe, PayPal)',
    short:
      'Kartenzahlungen in Fremdwährungen über Stripe sowie PayPal-Zahlungen auf der sicheren Seite des Anbieters.',
  },
  'payments.intl_entity': {
    title: 'Ausländisches Unternehmen erforderlich',
    short:
      'Stripe und PayPal eröffnen keine Konten für Unternehmen in der Türkei; verwenden Sie Ihr Unternehmen in einem unterstützten Land.',
  },
  'payments.intl_card_provider': {
    title: 'Kartenzahlungen in Fremdwährung',
    short:
      'Wenn Stripe ausgewählt ist, laufen Kartenzahlungen außerhalb von TRY über Stripe; TRY-Zahlungen nutzen den inländischen Anbieter.',
  },
  'payments.paypal': {
    title: 'Mit PayPal bezahlen',
    short: 'Wird an der Kasse nur für EUR-, USD- und GBP-Märkte angezeigt.',
  },
  'payments.intl_notify_urls': {
    title: 'Webhook-URLs für Stripe und PayPal',
    short: 'Tragen Sie diese im Stripe Dashboard → Webhooks und bei PayPal Developer → App → Webhooks ein.',
  },
  'page.ui_translations': {
    title: 'Übersetzungen',
    short: 'Übersetzungsstand der Oberflächen- und Hilfetexte je Sprache.',
  },
  'ui_translations.coverage': {
    title: 'Übersetzungsabdeckung',
    short:
      'Wie viele Oberflächen- und Hilfetexte in jeder Sprache übersetzt sind; 0 fehlend bedeutet vollständig.',
  },
  'page.subscriptions': {
    title: 'Wiederkehrende Bestellungen',
    short:
      'Regelmäßige Lieferungen, die Kunden „alle N Wochen“ starten: Einstellungen, Bereitschaft und Abo-Liste.',
  },
  'subscriptions.settings': {
    title: 'Einstellungen für wiederkehrende Bestellungen',
    short:
      'Pro Shop; standardmäßig aus. Zum Einschalten sind ein Kartenzahlungsanbieter und die Vertragstexte nötig.',
  },
  'subscriptions.enabled': {
    title: 'Regelmäßige Lieferung einschalten',
    short:
      'Wenn aktiv, zeigen geeignete Produktseiten das Feld „Regelmäßig liefern“. Wenn aus, werden keine neuen Bestellungen angelegt; Abos warten.',
  },
  'subscriptions.intervals': {
    title: 'Intervalle',
    short: 'Lieferabstände (Wochen), die Kunden wählen können. Wählen Sie mindestens einen.',
  },
  'subscriptions.discount': {
    title: 'Abo-Rabatt',
    short:
      'Prozentrabatt auf wiederkehrende Bestellungen (0–30). Der Satz beim Start eines Abos bleibt unverändert.',
  },
  'subscriptions.link_hours': {
    title: 'Gültigkeit des Zahlungslinks',
    short:
      'Zeit zum Bezahlen einer Bestellung (24–168 Stunden). Danach wird sie storniert und der Bestand freigegeben.',
  },
  'subscriptions.save': {
    title: 'Einstellungen speichern',
    short: 'Speichert die Einstellungen; beim Einschalten mit Bestätigung. Die Änderung wird protokolliert.',
  },
  'subscriptions.list': {
    title: 'Abos',
    short:
      'Kunde, Produkt, Menge, Intervall, nächster Bestelltag, Status und die Bestellung, die auf Zahlung wartet.',
  },
  'subscriptions.runs': {
    title: 'Zeiträume',
    short:
      'Jeder Zeitraum des Abos: Bestellung angelegt, bezahlt, übersprungen, nicht auf Lager, nicht bezahlt.',
  },
  'subscriptions.cancel': {
    title: 'Abo kündigen',
    short:
      'Grund mit mindestens 3 Zeichen, wird protokolliert. Der Kunde erhält keine E-Mail; unter „Mein Konto“ steht „Gekündigt“.',
  },
  'products.subscribable': {
    title: 'Für regelmäßige Lieferung verfügbar',
    short:
      'Wenn aktiv und die regelmäßige Lieferung im Shop eingeschaltet ist, zeigt die Produktseite das Feld „Regelmäßig liefern“.',
  },
  'tracking.cross_domain': {
    title: 'Domainübergreifendes Tracking',
    short:
      'Bei mehreren Storefront-Domains werden GA4-Linker und Quelldaten zwischen ihnen übertragen; schaltet sich mit einer zweiten Domain selbst ein.',
  },
  'shipping.geliver': {
    title: 'Versand mit Geliver',
    short:
      'Mit Ihrem Geliver-Konto Sendungen aus Bestellungen anlegen, Preise der Versanddienstleister vergleichen und das Etikett im Panel kaufen. Verbindung unter Integrationen → Versand → Geliver.',
  },
  'shipping.geliver_sender': {
    title: 'Absenderadresse',
    short:
      'Abholadresse des Pakets. Wird aus den in Geliver angelegten Absenderadressen gewählt; die Liste wird aus Geliver gelesen.',
  },
  'shipping.geliver_parcel': {
    title: 'Standard-Paketmaße',
    short: 'Im Sendungsfenster vorgeschlagene Länge, Breite und Höhe (cm, 1–300). Für jede Sendung änderbar.',
  },
  'shipping.geliver_weight': {
    title: 'Standardgewicht',
    short:
      'Gewicht (Gramm) für Positionen, deren Produkt kein Gewicht hat. Produktgewichte machen die Angebote genauer.',
  },
  'shipping.geliver_save': {
    title: 'Geliver-Einstellungen speichern',
    short:
      'Speichert Absenderadresse und Standardpaket; eine in Geliver fehlende Absenderadresse wird nicht gespeichert. Die Änderung wird protokolliert.',
  },
  'orders.ship_geliver': {
    title: 'Mit Geliver versenden',
    short:
      'Verpackte Produkte, Paketmaße und Gewicht wählen, Angebote der Versanddienstleister von Geliver holen und das Etikett mit dem gewählten Angebot kaufen.',
  },
  'orders.geliver_parcel': {
    title: 'Paketmaße und Gewicht',
    short:
      'Maße (cm) und Gewicht (Gramm), die den Versandpreis bestimmen. Das Gewicht wird aus Produktgewichten vorgeschlagen; Paket wiegen und korrigieren.',
  },
  'orders.geliver_offers': {
    title: 'Angebote holen',
    short:
      'Legt die Sendung in Geliver an und holt die Preise; es entstehen keine Kosten. Bei geänderten Maßen oder Produkten erneut holen.',
  },
  'orders.geliver_offer': {
    title: 'Versandangebot',
    short:
      'Dienstleister, Service, voraussichtliche Dauer und der von Geliver gemeldete Preis. Günstigstes und schnellstes sind markiert.',
  },
  'orders.geliver_buy': {
    title: 'Etikett kaufen',
    short:
      'Kauft das Etikett mit dem gewählten Angebot; nach Bestätigung wird die Gebühr vom Geliver-Guthaben abgebucht (in der Testumgebung eine Testsendung). Dasselbe Angebot wird nie doppelt gekauft.',
  },
  'orders.geliver_label': {
    title: 'Etikett öffnen',
    short:
      'Öffnet das von Geliver erzeugte Versandetikett (PDF) in einem neuen Tab; ausdrucken und auf das Paket kleben.',
  },
  'orders.geliver_sync': {
    title: 'Status aktualisieren',
    short:
      'Liest Status und Sendungsnummer jetzt aus Geliver (automatisch alle 30 Minuten). Beim ersten Eintreffen der Sendungsnummer erhält der Kunde eine E-Mail.',
  },
  'orders.geliver_cancel': {
    title: 'In Geliver stornieren',
    short:
      'Storniert die Sendung in Geliver vor der Abholung; verpackte Produkte gehen zurück in den Bestand und die Bestellung kann erneut versendet werden. Die Etikettgebühr wird nach Geliver-Regeln erstattet.',
  },
  'stores.customer_auth': {
    title: 'Kundenanmeldung',
    short:
      '„Weiter mit Google“ im Shop: Anmeldung, Registrierung und Kasse. Pro Shop aktivierbar; standardmäßig aus.',
  },
  'stores.customer_auth_toggle': {
    title: 'Google-Anmeldung an/aus',
    short:
      'Nur der Inhaber kann sie mit Zwei-Faktor-Bestätigung aktivieren; der Client unter Integrationen → Mit Google anmelden muss eingerichtet sein. Ausschalten stoppt neue Google-Anmeldungen.',
  },
  'stores.customer_auth_redirects': {
    title: 'Weiterleitungs-URIs',
    short:
      'Tragen Sie diese Adressen für jede Shop-Domain in Google Cloud → APIs & Dienste → Anmeldedaten → OAuth-Client → Autorisierte Weiterleitungs-URIs ein.',
  },
};

export const MOBILE_HELP_DE: Record<MobileHelpKey, Loc> = {
  'mobile.page.home': {
    title: 'Startseite (mobil)',
    short: 'Die heutigen Bestellungen und alles, was Ihre Aufmerksamkeit erfordert.',
  },
  'mobile.page.more': {
    title: 'Mehr',
    short: 'Kunden, Rücksendungen, Inhaltsprüfung, SEO, Gutscheincodes und Einstellungen.',
  },
  'mobile.page.count': {
    title: 'Bestandszählung per Barcode',
    short: 'Scannen Sie, was Sie im Regal zählen, und korrigieren Sie den Lagerbestand.',
  },
  'mobile.page.review': {
    title: 'Warten auf Fachprüfung',
    short: 'Produkte, Beiträge, Seiten, Banner und Fragen, die auf die tierärztliche Fachprüfung warten.',
  },
  'mobile.page.settings': {
    title: 'Einstellungen',
    short: 'Konto, Unternehmen und Shop, biometrische Sperre, Sprache und offene Sitzungen.',
  },
  'mobile.server_url': {
    title: 'Adresse der Verwaltung',
    short: 'Die Verwaltung, mit der eine Verbindung hergestellt wird (z. B. panel.zootopia.com.tr).',
  },
  'mobile.biometric': {
    title: 'Biometrische Sperre',
    short: 'Fragt beim Öffnen der App nach Fingerabdruck, Gesichtserkennung oder Bildschirmsperre.',
  },
  'mobile.scan': { title: 'Barcode scannen', short: 'Liest einen Barcode mit der Kamera.' },
  'mobile.take_photo': {
    title: 'Foto hinzufügen',
    short: 'Nehmen Sie ein Produktfoto auf oder wählen Sie eines aus der Galerie.',
  },
  'mobile.barcode_assign': {
    title: 'Barcode zuweisen',
    short: 'Speichert den gescannten Barcode für diese Variante.',
  },
  'mobile.count_lot': { title: 'Charge', short: 'Die Chargennummer der gezählten Packungen.' },
  'mobile.count_submit': {
    title: 'Zählung übermitteln',
    short: 'Erfasst fertige Zeilen als Bestandszählungen.',
  },
  'mobile.count_discard': {
    title: 'Zählung verwerfen',
    short: 'Löscht die Zählung auf diesem Gerät; bereits übermittelte Zeilen bleiben erfasst.',
  },
  'mobile.store_switch': { title: 'Shop', short: 'Shop-spezifische Bildschirme verwenden diesen Shop.' },
  'mobile.organization_switch': {
    title: 'Unternehmen wechseln',
    short: 'Wechseln Sie zu einem anderen Unternehmen, dem Sie angehören.',
  },
  'mobile.sign_out': {
    title: 'Abmelden',
    short: 'Beendet die Sitzung und löscht die Daten auf diesem Gerät.',
  },
  'mobile.notifications': {
    title: 'Push-Benachrichtigungen',
    short:
      'Benachrichtigungen zu neuen Bestellungen, Zahlungsprüfungen, Rücksendeanfragen, Händlerbewerbungen und Kontaktnachrichten.',
  },
  'mobile.notifications.device': {
    title: 'Dieses Telefon',
    short: 'Benachrichtigungsberechtigung und Registrierung dieses Telefons.',
  },
  'mobile.notifications.allow': {
    title: 'Benachrichtigungen aktivieren',
    short: 'Fragt nach der Berechtigung und registriert dieses Telefon für Benachrichtigungen.',
  },
  'mobile.notifications.open_settings': {
    title: 'Telefoneinstellungen öffnen',
    short:
      'Kann die Berechtigung nicht erneut abgefragt werden, aktivieren Sie Benachrichtigungen in den Telefoneinstellungen.',
  },
  'mobile.notifications.devices': {
    title: 'Meine Geräte',
    short: 'Telefone, die für Ihr Konto Benachrichtigungen erhalten.',
  },
  'mobile.notifications.remove': {
    title: 'Gerät entfernen',
    short: 'An dieses Gerät werden keine Benachrichtigungen gesendet.',
  },
  'mobile.offline': {
    title: 'Offline',
    short: 'Ohne Internet kann nur die Zählung auf dem Gerät fortgesetzt werden.',
  },
};
